/**
 * dp-designer.js — Kalender-Designer (MVP), Custom Element <dp-designer>.
 *
 * SCOPE (siehe docs/knowledge/_research/editor-architektur.md, calendar-builder.md): Format
 * wählen, Fotos hinzufügen (Dialog + Drag&Drop, Prüfung von Typ/Größe/Auflösung), Seiten
 * wechseln, Fotos Plätzen zuweisen, Ausschnitt/Zoom anpassen (Touch + Regler + Tastatur),
 * Hinweise sofort statt am Ende, Fortschritt, Prüf-Sheet nach „Weiter“.
 *
 * Rendering ist DOM/CSS (keine Canvas-Bibliothek, siehe Architekturentscheidung): Fotos
 * werden als background-image auf einem absolut positionierten Layer dargestellt, das
 * "object-fit: cover"-Verhalten wird über eine eigene transform-Berechnung (translate+scale)
 * nachgebildet, siehe panPxToNormalizedOffset()/normalizedOffsetToPanPx() weiter unten.
 *
 * AUSDRÜCKLICH NICHT TEIL DIESER DATEI (siehe docs/knowledge/known-issues.md und
 * docs/knowledge/calendar-builder.md Abschnitt 9/11): PDF-/ZIP-Erzeugung der Druckdatei,
 * Übergabe in den Warenkorb, Upload zum FLYERALARM-Connector. Diese Datei bereitet nur ein
 * vollständiges, valides Design-Dokument samt Zugriff auf die Original-Fotos vor und
 * übergibt es über eine klar benannte Schnittstelle an das nächste Glied der Kette:
 *
 *   - Custom Event `dp:design-ready` (Klasse `DesignReadyForHandoffEvent`, hier exportiert),
 *     bubbelt vom <dp-designer>-Element, `detail` enthält:
 *       { designId: string,
 *         getDesignDocument(): DesignDocument,        // deep clone, sofort verfügbar
 *         getOriginalImageBlob(assetId): Promise<Blob|null> }
 *     ausgelöst, sobald der Nutzer auf "Weiter" klickt und keine Befunde mit
 *     `level: 'error'` mehr vorliegen (siehe validateDesign() aus dp-designer-model.js).
 *   - Zusätzlich als direkte öffentliche Methoden auf dem <dp-designer>-Element:
 *     getDesignDocument(), getOriginalImageBlob(assetId), getPreviewImageBlob(assetId),
 *     getValidationIssues(), isReadyForHandoff() — je nachdem, was für die
 *     Druckdatei-/Warenkorb-Integration einfacher zu konsumieren ist.
 *
 * SEIT P2-07 (2026-09-11): dp-designer-handoff.js (data-module-handoff, createDesignerHandoff) übernimmt
 *   diese Stufe (PDF erzeugen, prüfen, Handoff-Speicher, Produktseite ?design=&fmt=). Diese Datei bindet nur an:
 *   continueToNextStep → handoff.begin, Sheet-Knöpfe → Controller (startHandoffExport/cancelHandoffExport/
 *   retryHandoff/downloadHandoffPdf/proceedToOrder), Stufen zurück über openReviewSheet(); URLs aus #handoffDeps().
 *
 * ABHÄNGIGKEITEN VON PARALLELEN DATEIEN (von einem anderen Agenten geschrieben — diese
 * Datei erfindet weder das Datenmodell noch den Format-Katalog neu, sondern importiert
 * sie. Nachfolgend die TATSÄCHLICHE Schnittstelle, wie sie in theme/assets/
 * dp-designer-model.js und dp-designer-formats.js implementiert ist):
 *
 *   `@theme/dp-designer-model` — genutzte Exporte: createDesign, addPhoto, removePhoto,
 *     assignPhotoToSlot(doc, pageId, slotId, assetId, { frame }), setSlotTransform, setText,
 *     setPhotoLook(doc, pageId, elementId, look), applyLookToAll(doc, look) (Bildverbesserung/
 *     Look, dp-photo-look.js), validateDesign(doc, format?) → `{ level, code, seite, nachricht }` (`seite` ist ein
 *     deutscher Anzeigename, keine pageId — siehe modelPageLabel(), das die nicht exportierte
 *     Beschriftung dupliziert), migrate. Slot-Geometrie (`frame`, Slot-IDs photo-main/
 *     photo-2/photo-3, Textrahmen title/caption) kommt aus dp-designer-panels.js.
 *
 *   `@theme/dp-designer-formats` — listFormats(), getFormat(formatKey); jedes Format trägt
 *     `layout` ({ photo, calendarium }, relative 0..1-Flächen) — Monatsseiten bekommen ihren
 *     Fotobereich daraus, dadurch prüft effectiveDpi() im Modell gegen den Fotobereich.
 *     Optional preferredVariant(format, variant) (seit 2026-09-16, Tischkalender): Darstellung
 *     „zeile" bei Seiten, deren Kalendarium-Band kein lesbares Raster mehr fasst — siehe #preferredVariant().
 *
 *   `@theme/dp-kalendarium-render` — liefert das Datumsraster einer Monatsseite:
 *     `renderMonthCalendarium({ month, year, formatKey, variant, farben, showWeekNumbers,
 *     showHolidays, region, weekStart, locale }): string`. `formatKey` ist Pflicht (sonst
 *     kalibriert der Renderer fest auf A4 hoch); `variant`/`region` werden hier normalisiert
 *     ('liste'→'zeile', 'DE-BY'→'BY'). Nicht fatal: ohne Modul rendert
 *     shell.renderFallbackCalendarium(). Das Kalendarium ist bewusst KEIN Element im
 *     Design-Dokument — reines Rendering aus Seitenrolle, Format/Theme-Band und Jahr
 *     (`doc.meta.startYear`).
 *
 *   `@theme/dp-upload` — inspectFile(file): Promise<{ ok, message, … }> (Typ-/Größenprüfung,
 *     KI-32). checkPrintability wird bewusst nicht aufgerufen: die effektive Druckauflösung
 *     prüft validateDesign() im Modell, sobald ein Foto in einem Platz liegt.
 *
 *   Alle Module werden per DYNAMIC import() geladen (#loadDependencies()): ein fehlender
 *   benannter Export bei statischem Import wäre ein harter SyntaxError für die ganze Datei;
 *   so gibt es stattdessen eine verständliche Fehlermeldung. Kalendarium und Themes laufen
 *   über eigene, nicht-fatale Pfade.
 *
 * Persistenz: IndexedDB ("dp-designer"-Datenbank, Store "imageBlobs") für die
 * Original-/Vorschau-Bilddateien, ein kompaktes Design-Dokument (ohne Bild-Bytes) in
 * localStorage — siehe Klasse DesignerStorage weiter unten.
 *
 * Basisklasse `Component` aus @theme/component (ref-Mechanismus, deklarative
 * on:click/on:change/on:pointerdown-Bindungen). Pointermove/-up sowie Drag&Drop-Events
 * sind nicht Teil des deklarativen Event-Sets von Component und werden deshalb manuell
 * mit AbortController-Cleanup gebunden (siehe #bindGlobalListeners()).
 *
 * EDITOR-HÜLLE (seit 2026-09-11): Das Markup des Editors (Topbar, Tabs Designs/Fotos/Layout/
 * Text, Bühne, Kontextpanel, Seitenstreifen mit Miniaturen) rendert dp-designer-shell.js aus
 * einem `view`-Objekt (#buildView()); Seitenlayouts (1 Foto / 2 Fotos / Collage / mit
 * Bildunterschrift), Textrahmen und Panel-Inhalte liefert dp-designer-panels.js. Beide werden
 * wie Modell/Formatkatalog per dynamic import() geladen (data-module-shell / -panels) und sind
 * Pflicht. Designs (Themes) kommen optional aus dp-designer-themes.js (data-module-themes:
 * listThemes, getTheme, applyTheme(doc, id), layoutForPage(theme, page, format),
 * themeStyles(theme), sampleImagesFor(theme), resolveTheme(theme, overrides) für eigene Farben
 * in doc.theme.overrides); fehlt das Modul, gibt es keinen Tab „Designs“
 * und keine Beispielfotos — alles andere funktioniert unverändert. Diese Datei hält nur noch
 * Zustand, Aktionen, Gesten, Bild-Pipeline und Persistenz.
 *
 * EINFACH-MODUS ALS QUIZ (seit 2026-09-18, dp-designer-simple.js): `#mode` 'einfach' | 'voll' (Schalter in der
 * Kopfzeile, ?mode=, localStorage `dp-designer:mode`, Erstbesuch aus Social-Anzeigen per utm) und `#quiz`
 * { screen, answers, done } (localStorage `dp-designer:quiz`): Für wen? → Titelvorschlag · Wo? → Produkt/Format
 * (selectFormat/switchFormat wie beim Deep-Link) · Stil → selectTheme · Fotos → Upload + automatisch Magic
 * Layout; ab genug Fotos für alle Seiten endet das Quiz von selbst (quizFinish: Titel aus Frage 1 per
 * setPageText, leere Seiten reihum gefüllt). Vier Bildschirme seit „unter 90 Sekunden“ (Inhaber 2026-09-18) –
 * der frühere Titel-Bildschirm entfällt, der Titel wird im Fertig-Panel geändert. Danach „Dein Kalender ist
 * fertig“ mit Bühne, Anpassen-Panel und dem bestehenden „Weiter“-Weg. Ereignisse `dp:designer-mode` und
 * `dp:designer-quiz` für spätere Pixel (keine Requests).
 *
 * FOTOS EINLESEN (#ingestFiles, Tempo-Ziel 13 Handyfotos < 20 s): Dateien werden zu dritt parallel dekodiert
 * (createImageBitmap läuft im Browser ohnehin neben dem Hauptthread) und genau EINMAL – vorher dekodierte
 * dp-upload.js inspectFile() jedes Foto zusätzlich nur für die Pixelmaße; die billigen Prüfungen (Typ, Größe)
 * laufen vorab, inspectFile() liefert nur noch bei Lesefehlern die passende Meldung (HEIC, defekt).
 * Ergebnisse werden in Auswahlreihenfolge übernommen; der Fortschritt „7 von 13 verarbeitet“ wird im Quiz
 * live gepatcht ([data-upload-ring|-num|-text]) statt voll gerendert.
 */

import { Component } from '@theme/component';
import { clamp, debounce } from '@theme/utilities';
import { freezeBlobs, thawBlobs } from './dp-idb-blobs.js?v=1195b1929c';
// Deep-Link-Vertrag (?product=&fmt=&paper=&binding=): eine Quelle für Produktseite und Designer.
import { OPTION_VALUES, DEFAULT_FORMAT_BY_PRODUCT, readDesignerParams, sanitizeFormatKeyParam, sanitizeProductIdParam, productIdForFormat, resolveDesignerMode, sanitizeModeParam, DESIGNER_DEFAULT_MODE, DESIGNER_MODE_STORAGE_KEY, resolveOptionOffer, expressOfferFrom, deliveryLabel } from './dp-designer-link.js?v=79b02de54d';

/* ------------------------------------------------------------------------------------ *
 * Typedefs — Feldnamen 1:1 aus dp-designer-model.js / dp-designer-formats.js.
 * ------------------------------------------------------------------------------------ */

/**
 * @typedef {{ xMm:number, yMm:number, widthMm:number, heightMm:number }} Frame
 * @typedef {{ zoom:number, offsetX:number, offsetY:number }} Crop  zoom ≥ 1 (1 = Cover-Basis), Offsets -0.5…0.5
 * @typedef {{ rotate:0|90|180|270, flipH:boolean }} Orientation  Drehen/Spiegeln (dp-designer-model.js rotatePhoto/flipPhoto), additiv
 * @typedef {{ id:string, type:'image', slotId:string, assetId:string|null, frame:Frame, fit:'cover', crop:Crop, rotationDeg:number, orientation?:Orientation }} ImageElement
 * @typedef {{ id:string, type:'text', slotId:string, frame:Frame, text:string, font:{family:string,weight:number}, fontSizePt:number, color:string, align:string, lineHeightPt:number }} TextElement
 * @typedef {{ pageId:string, index:number, role:'cover'|'month'|'back', calendariumMonth:number|null, calendariumYear?:number, layoutTemplateId:string|null, elements:Array<ImageElement|TextElement> }} PageDoc
 * @typedef {{ assetId:string, sourceFileName:string, originalWidthPx:number, originalHeightPx:number, mimeType:string, fileSizeBytes:number, storage:'indexeddb', storageRef:string, previewStorageRef:string, exifOrientationApplied:boolean }} ImageAssetMeta
 * @typedef {{ paper:'matt-170'|'glanz-170'|'glanz-250', binding:'schwarz'|'silber'|'weiss' }} ProductOptions
 * @typedef {{ shopifyProductId:string|null, shopifyVariantId:string|null, dpProductId?:string|null, options?:ProductOptions, formatKey:string, widthMm:number, heightMm:number, bleedMm:number, safeMarginMm:number, dpiTarget:number }} DesignProduct
 *   dpProductId (aus ?product=) setzt DIESE Datei additiv, siehe #withProductContext(); options (Papier/Bindung,
 *   nur Schlüssel — die Brücke übersetzt in App-Optionstexte) ebenfalls, siehe selectOption().
 * @typedef {{ schemaVersion:string, designId:string, createdAt:string, updatedAt:string, productType:'calendar', product:DesignProduct, pages:PageDoc[], assets:{images:Record<string,ImageAssetMeta>}, meta:Object, theme?:Object }} DesignDocument
 *   meta.startYear (Kalenderjahr) und meta.themeId verwaltet diese Datei; doc.theme setzt applyTheme().
 * @typedef {{ edge:'top'|'bottom'|'left'|'right', marginMm:number }} BindingMargin
 * @typedef {{ top:number, left:number, width:number, height:number }} LayoutArea  Anteile 0..1 des Endformats
 * @typedef {{ key:string, displayName:string, endFormatMm:{widthMm:number,heightMm:number}, bleedMm:number, safeMarginMm:number, binding:BindingMargin, pageCount:number, dpiTarget:number, priceFromEur?:number, layout:{photo:LayoutArea, calendarium:LayoutArea} }} CalendarFormat
 * @typedef {{ level:'error'|'warning', code:string, seite:string, nachricht:string }} ValidationFinding  `seite` = deutscher Seitenname, siehe modelPageLabel()
 */

/* ------------------------------------------------------------------------------------ *
 * Konstanten
 * ------------------------------------------------------------------------------------ */

const DB_NAME = 'dp-designer';
const DB_VERSION = 1;
const STORE_IMAGES = 'imageBlobs';
const LS_PREFIX = 'dp-designer:design:';
const LS_LAST_ID = 'dp-designer:last-design-id';

const PREVIEW_MAX_EDGE = 1600;
const PREVIEW_QUALITY = 0.82;
/** Fotos gleichzeitig dekodieren (Tempo 2026-09-18): drei halten auch ein mittleres Handy ohne Speichernot beschäftigt. */
const INGEST_CONCURRENCY = 3;

/** items nacheinander an höchstens `limit` Arbeiter verteilen (Reihenfolge der Ergebnisse regelt der Aufrufer). */
async function runLimited(items, limit, fn) {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop -- bewusst: ein Arbeiter verarbeitet seine Dateien nacheinander
      await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
}

// Spiegeln MIN_ZOOM/MAX_ZOOM/ZOOM_WARNING_THRESHOLD aus dp-designer-model.js (dieselben
// Werte) — lokal gehalten, weil sie hier auch VOR dem Laden des Moduls gebraucht würden
// (Modul-Konstanten wie Klassenfeld-Defaults) und die Gesten-/Render-Mathematik unten rein
// UI-seitig ist, unabhängig vom Datenmodell.
const ZOOM_MIN = 1;
const ZOOM_MAX = 5;
const ZOOM_WARN_THRESHOLD = 3;
const DOUBLE_TAP_ZOOM = 1.5;
const DOUBLE_TAP_DELAY_MS = 300;

const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const MONTH_ABBR_DE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
// Spiegelt BUNDESLAND_CODES aus dp-kalendarium.js (dieselben 16 Kürzel) — lokal gehalten, weil
// diese Datei jenes Modul nicht selbst importiert (nur der Renderer tut das) und die Prüfung
// hier VOR dem Aufruf des Renderers passieren muss: feiertage() dort wirft bei unbekanntem
// Kürzel, und dp-designer.liquid liefert historisch 'DE-BY'-Werte (siehe normalizeCalendarRegion()).
const BUNDESLAND_CODES = ['BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV', 'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH'];

// Zulässige Form der URL-Parameter `?fmt=` und `?product=` sowie `?paper=`/`?binding=`:
// sanitizeFormatKeyParam / sanitizeProductIdParam / readDesignerParams aus dp-designer-link.js
// (Systemgrenze: alles andere wird verworfen, nie unverändert ins Dokument oder Markup übernommen).

/* ------------------------------------------------------------------------------------ *
 * Kleine, reine Hilfsfunktionen
 * ------------------------------------------------------------------------------------ */

/**
 * Darstellungsvariante des Kalendariums aus der Section-Einstellung `calendar_variant`.
 * dp-designer.liquid bietet historisch den Wert 'liste' an, dp-kalendarium-render.js akzeptiert
 * aber nur 'raster' | 'zeile' (DARSTELLUNGEN dort) und wirft sonst → hier abgebildet, damit
 * bestehende Template-Werte weiter funktionieren, ohne gespeicherte Einstellungen anzufassen.
 * @param {string|undefined} raw @returns {'raster'|'zeile'}
 */
function normalizeCalendarVariant(raw) {
  const value = String(raw ?? '').trim().toLowerCase();
  return value === 'liste' || value === 'zeile' ? 'zeile' : 'raster';
}

/**
 * Bundesland-Kürzel für die Feiertagsberechnung aus `calendar_region`. Die Section liefert
 * ISO-3166-2-Werte ('DE-BY'), der Renderer erwartet das nackte Kürzel ('BY') — Präfix abstreifen,
 * gegen BUNDESLAND_CODES prüfen, alles Unbekannte (auch '' = "nur bundesweit") → undefined.
 * @param {string|undefined} raw @returns {string|undefined}
 */
function normalizeCalendarRegion(raw) {
  const code = String(raw ?? '').trim().toUpperCase().replace(/^DE-/, '');
  return BUNDESLAND_CODES.includes(code) ? code : undefined;
}

/** data-*-Attribute tragen Booleans als 'true'/'false'-Strings. @param {string|undefined} raw @param {boolean} fallback */
function parseBooleanAttr(raw, fallback) {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return fallback;
}

// Erlaubte Schlüssel für Papier und Bindung: OPTION_VALUES aus dp-designer-link.js (Spiegel von
// PAPER_CHOICES/BINDING_CHOICES in dp-designer-calendar-panel.js). Erster Wert = Standard.

const OPTION_CHIP = Object.freeze({ paper: { 'glanz-170': 'glänzend', 'glanz-250': '250 g glänzend' }, binding: { silber: 'Silber', weiss: 'Weiß' } });

/** Chip-Zusätze für Nicht-Standard-Optionen, z. B. ['glänzend', 'Silber']. */
function optionChipParts(raw) {
  const o = normalizeProductOptions(raw);
  return [OPTION_CHIP.paper[o.paper], OPTION_CHIP.binding[o.binding]].filter(Boolean);
}

/** @param {unknown} raw @returns {ProductOptions} Unbekanntes/Fehlendes → Standard; immer ein neues Objekt */
function normalizeProductOptions(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  for (const [group, values] of Object.entries(OPTION_VALUES)) {
    const value = String(src[group] ?? '');
    out[group] = values.includes(value) ? value : values[0];
  }
  return out;
}

/**
 * Kurzform eines Katalog-Formatnamens für den Kontext-Chip und die Hinweise: aus
 * "Wandkalender DIN A4 Hochformat" wird "DIN A4 hoch", aus "Tischkalender DIN A5 Querformat"
 * "DIN A5 quer" (die Produktfamilie steht schon im Produktnamen daneben – "Tischkalender Hygge",
 * "Fotokalender Lykke"; "Hochformat"/"Querformat" werden auf "hoch"/"quer" gekürzt).
 * @param {CalendarFormat|null|undefined} format @returns {string}
 */
function shortFormatLabel(format) {
  if (!format) return '';
  return String(format.displayName ?? format.key)
    .replace(/^(?:Wand|Tisch|Foto)kalender\s+/i, '')
    .replace(/\bHochformat\b/i, 'hoch')
    .replace(/\bQuerformat\b/i, 'quer')
    .trim();
}

/**
 * Kurzform eines Shop-Produktnamens aus dp-format-map.json ("Fotokalender mit Wire-O-Bindung" →
 * "Fotokalender"); der volle Name bleibt als title/Screenreader-Text erhalten.
 * @param {string} productName @returns {string}
 */
function shortProductName(productName) {
  return String(productName ?? '').split(/\s+mit\s+/i)[0].trim();
}

/** „ab 18,44 €“ aus einer Zahl (leer, wenn keine positive Zahl). @param {unknown} price @returns {string} */
function priceFromLabel(price) {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return '';
  try {
    return `ab ${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n)}`;
  } catch {
    return `ab ${n.toFixed(2).replace('.', ',')} €`;
  }
}

/** Hinweis in der Fotoliste, wenn der Entwurf nicht mehr lokal gespeichert werden kann (localStorage voll). */
const STORAGE_WARNING = Object.freeze({ fileName: 'Speicher', message: 'Dein Entwurf konnte nicht mehr gespeichert werden – der Browser-Speicher ist voll. Entferne nicht benötigte Fotos, sonst geht dein Entwurf beim Neuladen verloren.' });

/** Enthält der Drag-Vorgang Dateien vom Rechner (nicht ein Foto aus der Liste)? @param {DragEvent} event */
function hasFileDrag(event) {
  const types = event?.dataTransfer?.types;
  return Boolean(types && Array.from(types).includes('Files'));
}

/** @param {unknown} value */
/** Entwurfsdatei am Namen erkennen (Drag-and-drop) – Endung wie dp-design-file.js FILE_EXTENSION. */
function isDesignFileName(name) {
  return /\.dpkalender$/i.test(String(name ?? ''));
}

function formatMegabytes(bytes) {
  const mb = bytes / 1_000_000;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`.replace('.', ',');
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c
  ));
}

/** Prozent-Anteil einer mm-Größe an der Gesamtseite, für absolute CSS-Positionierung. */
function pct(valueMm, totalMm) {
  if (!totalMm) return 0;
  return Number(((valueMm / totalMm) * 100).toFixed(4));
}

function generateId(prefix) {
  const rand = typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${rand}`;
}

function distanceBetween(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Dupliziert bewusst die private (nicht exportierte) `describePage()`-Beschriftung aus
 * dp-designer-model.js, um validateDesign()-Befunde (die nur `seite` als Textlabel tragen,
 * keine pageId) einer konkreten Seite zuordnen zu können. Muss mit jener Funktion
 * wortgleich bleiben — kein struktureller Schlüssel verfügbar, siehe Datei-Kopf.
 * @param {PageDoc} page
 */
function modelPageLabel(page) {
  if (page.role === 'cover') return 'Titelseite';
  if (page.role === 'back') return 'Rückseite';
  if (page.calendariumMonth) return MONTH_NAMES_DE[page.calendariumMonth - 1] ?? `Seite ${page.index + 1}`;
  return `Seite ${page.index + 1}`;
}

/** Eigenes, kompaktes Label für den Seiten-Streifen (unabhängig vom Modell). @param {PageDoc} page */
function shortPageLabel(page) {
  if (page.role === 'cover') return 'Cover';
  if (page.role === 'back') return 'Rückseite';
  if (page.calendariumMonth) return MONTH_ABBR_DE[page.calendariumMonth - 1];
  return `S. ${page.index + 1}`;
}

/** Stabiler Schlüssel für einen ValidationFinding (das Modell liefert keine id). */
function issueKey(issue) {
  return `${issue.code}|${issue.seite}`;
}

const HINT_SEEN_KEY = 'dp-designer:hint-seen';
/** Zieh-Tipp unter der Bühne (renderStageNote): nur beim ersten Mal, danach gemerkt (Inhaber 2026-09-22). */
const TRAY_TIP_SEEN_KEY = 'dp-designer:tray-tip-seen';
/** Quiz-Antworten des Einfach-Modus (Spiegel von QUIZ_STORAGE_KEY in dp-designer-simple.js, das nur dynamisch geladen wird). */
const QUIZ_STORAGE_KEY = 'dp-designer:quiz';
/** Sperre nach einem Quiz-Bildschirmwechsel: ein Doppeltipp darf die nächste Frage nicht mitbeantworten. */
const QUIZ_ANSWER_LOCK_MS = 350;
/** Wunschmenge aus der Staffel-Karte („Gleich zwei bestellen?“), sessionStorage – überlebt einen Reload, nicht die Sitzung. */
const WANTED_QUANTITY_KEY = 'dp-designer:wanted-quantity';
const MODE_HELP_STORAGE_KEY = 'dp-designer:mode-help-seen'; // Spiegel von dp-designer-simple.js
const NUDGE_STEP_PX = 10;
/** Sprachausgabe der Look-Namen (Anzeige: LOOK_LABELS in dp-photo-look.js). */
const LOOK_NAMES = Object.freeze({ none: 'Original', bw: 'Schwarzweiß', sepia: 'Sepia', warm: 'Warm', cool: 'Kühl' });
const TEXT_COLOR_TITLE = '#FFFFFF';
const TEXT_COLOR_CAPTION = '#1E241C';
/** Bühnentexte schrumpfen höchstens auf 60 % (dann „…“) – dieselbe Grenze wie TEXT_SHRINK_MIN in dp-print-page.js (fitSingleLine). */
const TEXT_SHRINK_MIN = 0.6;
/** Untergrenze der PDF (dp-print-page.js TEXT_SHRINK_MIN): 2 % unter der Bühne, damit die PDF nie kürzt, wo die Bühne alles zeigt (QA D-14). */
const PDF_TEXT_SHRINK_MIN = 0.58;
/**
 * Schriftwahl-Familien der PDF (Schlüssel wie FONT_FAMILY_KEYS in dp-print-export.js) → Namensteil der
 * data-Attribute der Section: data-module-font-<teil>, data-font-<teil>-regular/-bold (dp-designer.liquid).
 */
const FONT_FAMILY_DATASET = Object.freeze({ serif: 'serif', hand: 'hand', display: 'display', rounded: 'rounded' });

/** @param {number} [now] Testbarkeit — Default: aktuelles Jahr. @returns {number} */
function defaultStartYear(now = new Date()) {
  return now.getFullYear() + 1;
}

/**
 * Optionsliste fürs Kalenderjahr-Auswahlfeld: aktuelles Jahr bis +5 Jahre, damit auch früh im
 * Voraus bestellt werden kann. Ein zuvor gespeichertes Jahr außerhalb dieses Fensters (z. B.
 * ein altes, inzwischen vergangenes Design) wird trotzdem als Option angezeigt statt
 * stillschweigend verworfen.
 * @param {number} selectedYear
 * @returns {string}
 */
function yearSelectOptions(selectedYear) {
  const base = new Date().getFullYear();
  const years = [];
  for (let y = base; y <= base + 5; y += 1) years.push(y);
  if (!years.includes(selectedYear)) years.push(selectedYear);
  years.sort((a, b) => a - b);
  return years.map((y) => `<option value="${y}"${y === selectedYear ? ' selected' : ''}>${y}</option>`).join('');
}

/* ------------------------------------------------------------------------------------ *
 * Foto-in-Slot: Cover-Pan/Zoom-Mathematik.
 *
 * `.dp-slot__photo` (inset:0, background cover/center = Basis zoom=1/pan=0; bei 90°/270° eine
 * Box in Bildproportion, mittig im Rahmen — siehe shell.photoLayerBox) bekommt `transform:
 * translate(panPx) scale(zoom) rotate(deg) scale(flipH ? -1 : 1, 1)` — translate wirkt so in
 * Bildschirm-Pixeln, unabhängig von Zoom und Drehung (Ziehen verschiebt sichtbar in
 * Zieh-Richtung, keine Umrechnung nötig); panPx ist relativ zur Rahmenmitte (transform-origin:
 * center). Drehen/Spiegeln (element.orientation) sitzt innen in der Kette: bei 90°/270° ist die
 * Cover-Basis mit getauschten Bildmaßen zu rechnen (orientedAssetPx), offsetX/Y sind Bruchteile
 * der GEDREHT dargestellten Bildgröße — dieselbe Konvention wie dp-print-images.js coverPlacement
 * (orientedImageSize) und dp-design-snapshot.js cropTransformPercent.
 * ------------------------------------------------------------------------------------ */

/** Bildmaße, wie das Foto nach der Drehung im Rahmen liegt (90°/270°: Breite/Höhe getauscht). @param {ImageAssetMeta} asset @param {ImageElement} el */
function orientedAssetPx(asset, el) {
  const swapped = el?.orientation?.rotate === 90 || el?.orientation?.rotate === 270;
  return swapped
    ? { widthPx: asset.originalHeightPx, heightPx: asset.originalWidthPx }
    : { widthPx: asset.originalWidthPx, heightPx: asset.originalHeightPx };
}

/** Innerer Teil der Transform-Kette (Drehen/Spiegeln) — '' ohne Ausrichtung. @param {ImageElement|null|undefined} el */
function orientationTransform(el) {
  const rotate = Number(el?.orientation?.rotate) || 0;
  const flipH = el?.orientation?.flipH === true;
  return `${rotate ? ` rotate(${rotate}deg)` : ''}${flipH ? ' scale(-1, 1)' : ''}`;
}

/** Vollständige Transform-Kette des Foto-Layers. @param {{x:number,y:number}} panPx @param {number} zoom @param {string} orientationCss */
function photoTransform(panPx, zoom, orientationCss = '') {
  return `translate(${panPx.x}px, ${panPx.y}px) scale(${zoom})${orientationCss}`;
}

/** @param {{x:number,y:number}} panPx @param {number} zoom @param {number} frameWidthPx @param {number} frameHeightPx */
function clampPanPx(panPx, zoom, frameWidthPx, frameHeightPx) {
  const maxX = (frameWidthPx * (zoom - 1)) / 2;
  const maxY = (frameHeightPx * (zoom - 1)) / 2;
  return { x: clamp(panPx.x, -maxX, maxX), y: clamp(panPx.y, -maxY, maxY) };
}

/**
 * Wandelt den gespeicherten, auflösungsunabhängigen Crop (offsetX/offsetY als Bruchteil
 * der ORIGINAL-Bildbreite/-höhe) in eine aktuell darstellbare Pixel-Verschiebung um.
 */
function normalizedOffsetToPanPx(offsetX, offsetY, zoom, frameWidthPx, frameHeightPx, imgWidthPx, imgHeightPx) {
  const coverScale = Math.max(frameWidthPx / imgWidthPx, frameHeightPx / imgHeightPx);
  const displayedWidthPx = imgWidthPx * coverScale * zoom;
  const displayedHeightPx = imgHeightPx * coverScale * zoom;
  return clampPanPx({ x: offsetX * displayedWidthPx, y: offsetY * displayedHeightPx }, zoom, frameWidthPx, frameHeightPx);
}

/** Kehrfunktion zu normalizedOffsetToPanPx() — wird beim Beenden einer Geste aufgerufen. */
function panPxToNormalizedOffset(panPx, zoom, frameWidthPx, frameHeightPx, imgWidthPx, imgHeightPx) {
  const coverScale = Math.max(frameWidthPx / imgWidthPx, frameHeightPx / imgHeightPx);
  const displayedWidthPx = imgWidthPx * coverScale * zoom;
  const displayedHeightPx = imgHeightPx * coverScale * zoom;
  return {
    offsetX: displayedWidthPx > 0 ? panPx.x / displayedWidthPx : 0,
    offsetY: displayedHeightPx > 0 ? panPx.y / displayedHeightPx : 0,
  };
}

/* ------------------------------------------------------------------------------------ *
 * Bild-Pipeline (calendar-builder.md Abschnitt 6.1) — Validierung, EXIF-bewusste
 * Dekodierung, Downscale auf Vorschau-Auflösung.
 * ------------------------------------------------------------------------------------ */

function guessMimeFromName(name) {
  const lower = name.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.heif')) return 'image/heif';
  return 'image/jpeg';
}

function decodeImageFileViaImgTag(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (error) => { URL.revokeObjectURL(url); reject(error); };
    img.src = url;
  });
}

/** @param {File} file @returns {Promise<ImageBitmap|HTMLImageElement>} */
async function decodeImageFile(file) {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (error) {
      // > Annahme (siehe editor-architektur.md 6.2): granulare HEIC/iOS-Unterstützung von
      // createImageBitmap ist nicht für jeden Browser verifiziert — Fallback statt Absturz.
      console.warn('[dp-designer] createImageBitmap fehlgeschlagen, nutze <img>-Fallback', error);
    }
  }
  return decodeImageFileViaImgTag(file);
}

function getMediaSize(media) {
  if ('naturalWidth' in media) return { width: media.naturalWidth, height: media.naturalHeight };
  return { width: media.width, height: media.height };
}

/** @param {ImageBitmap|HTMLImageElement} media */
async function renderPreviewBlob(media, maxEdge = PREVIEW_MAX_EDGE, quality = PREVIEW_QUALITY) {
  const { width, height } = getMediaSize(media);
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  // Durchsichtige Stellen (PNG) zuerst weiss fuellen: JPEG kennt keine Transparenz und liess sie sonst
  // SCHWARZ erscheinen, waehrend der Druck sie weiss fuellt (dp-print-images.js) – Buehne und Druckdatei
  // zeigten dasselbe Foto verschieden (Druckpruefung 2026-09-22 D-1).
  const fillWhite = (ctx) => { ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, targetW, targetH); };

  if ('OffscreenCanvas' in window) {
    const canvas = new OffscreenCanvas(targetW, targetH);
    const ctx = canvas.getContext('2d');
    fillWhite(ctx);
    ctx.drawImage(media, 0, 0, targetW, targetH);
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  fillWhite(ctx);
  ctx.drawImage(media, 0, 0, targetW, targetH);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Vorschau konnte nicht erzeugt werden'))), 'image/jpeg', quality);
  });
}

/** @param {File} file */
async function buildImageAsset(file) {
  const media = await decodeImageFile(file);
  const { width, height } = getMediaSize(media);
  const previewBlob = await renderPreviewBlob(media);
  if ('close' in media && typeof media.close === 'function') media.close();

  const assetId = generateId('img');
  /** @type {ImageAssetMeta} */
  const meta = {
    assetId,
    sourceFileName: file.name,
    originalWidthPx: width,
    originalHeightPx: height,
    mimeType: file.type || guessMimeFromName(file.name),
    fileSizeBytes: file.size,
    storage: 'indexeddb',
    storageRef: `originals/${assetId}`,
    previewStorageRef: `previews/${assetId}`,
    exifOrientationApplied: true,
  };
  return { meta, originalBlob: file, previewBlob };
}

/* ------------------------------------------------------------------------------------ *
 * Persistenzschicht: IndexedDB für Bild-Blobs, localStorage für das kompakte
 * Design-Dokument (ohne Bild-Bytes) — siehe Datei-Kopf.
 * ------------------------------------------------------------------------------------ */

class DesignerStorage {
  /** @type {IDBDatabase | null} */
  #db = null;
  /** @type {Promise<IDBDatabase> | null} */
  #opening = null;

  #open() {
    if (this.#db) return Promise.resolve(this.#db);
    if (this.#opening) return this.#opening;
    this.#opening = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_IMAGES)) {
          db.createObjectStore(STORE_IMAGES, { keyPath: 'assetId' });
        }
      };
      request.onsuccess = () => { this.#db = request.result; resolve(request.result); };
      // Nach einem Fehler darf der nächste Aufruf neu öffnen (sonst bleibt der Speicher für die
      // ganze Sitzung „kaputt" – Technik-Prüfung 2026-09-16, P2-7).
      request.onerror = () => { this.#opening = null; reject(request.error); };
      request.onblocked = () => { this.#opening = null; reject(new Error('IndexedDB blockiert')); };
    });
    this.#opening.catch(() => { this.#opening = null; });
    return this.#opening;
  }

  async requestPersistence() {
    try {
      if (navigator.storage?.persist) await navigator.storage.persist();
    } catch {
      // Best effort — nicht kritisch für die Editor-Funktion.
    }
  }

  async estimate() {
    try {
      if (navigator.storage?.estimate) return await navigator.storage.estimate();
    } catch {
      // Quota-API evtl. nicht verfügbar (z. B. privates Fenster) — kein harter Fehler.
    }
    return null;
  }

  /** @param {string} assetId @param {{original:Blob, preview:Blob}} blobs */
  async putImageBlobs(assetId, blobs) {
    const db = await this.#open();
    // Safari/iOS speichert keine Blobs in IndexedDB (Fehler null) – als ArrayBuffer ablegen, VOR der Transaktion (dp-idb-blobs.js).
    const record = await freezeBlobs({ assetId, original: blobs.original, preview: blobs.preview });
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_IMAGES, 'readwrite');
      tx.objectStore(STORE_IMAGES).put(record);
      tx.oncomplete = () => resolve(undefined);
      tx.onerror = () => reject(tx.error);
    });
  }

  /** @param {string} assetId @returns {Promise<{original:Blob, preview:Blob}|null>} */
  async getImageBlobs(assetId) {
    const db = await this.#open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_IMAGES, 'readonly');
      const req = tx.objectStore(STORE_IMAGES).get(assetId);
      req.onsuccess = () => resolve(req.result ? thawBlobs(req.result) : null);
      req.onerror = () => reject(req.error);
    });
  }

  async deleteImageBlobs(assetId) {
    const db = await this.#open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_IMAGES, 'readwrite');
      tx.objectStore(STORE_IMAGES).delete(assetId);
      tx.oncomplete = () => resolve(undefined);
      tx.onerror = () => reject(tx.error);
    });
  }

  /** @param {DesignDocument} doc */
  saveDesignDocument(doc) {
    try {
      localStorage.setItem(LS_PREFIX + doc.designId, JSON.stringify(doc));
      localStorage.setItem(LS_LAST_ID, doc.designId);
      return true;
    } catch (error) {
      console.warn('[dp-designer] Design konnte nicht lokal gespeichert werden', error);
      return false;
    }
  }

  /** @param {string} designId @returns {DesignDocument|null} */
  loadDesignDocument(designId) {
    try {
      const raw = localStorage.getItem(LS_PREFIX + designId);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  getLastDesignId() {
    try {
      return localStorage.getItem(LS_LAST_ID);
    } catch {
      return null;
    }
  }
}

/* ------------------------------------------------------------------------------------ *
 * Handoff-Event für die nächste, hier bewusst nicht implementierte Stufe
 * (PDF-Erzeugung/Warenkorb-Übergabe/Connector-Upload) — siehe Datei-Kopf.
 * ------------------------------------------------------------------------------------ */

export class DesignReadyForHandoffEvent extends Event {
  static eventName = 'dp:design-ready';
  /**
   * @param {{designId:string, getDesignDocument:()=>DesignDocument, getOriginalImageBlob:(assetId:string)=>Promise<Blob|null>,
   *   openReviewSheet:(state:{stage?:'saved'|'exporting'|'done'|'error', message?:string, progress?:number})=>void}} detail
   *   openReviewSheet (additiv seit 2026-09-11): Listener/Export-Module aktualisieren damit das Prüf-Sheet.
   */
  constructor(detail) {
    super(DesignReadyForHandoffEvent.eventName, { bubbles: true, composed: true });
    this.detail = detail;
  }
}

/* ------------------------------------------------------------------------------------ *
 * Custom Element <dp-designer>
 * ------------------------------------------------------------------------------------ */

/**
 * @typedef {Object} Refs
 * @property {HTMLInputElement} [fileInput]
 * @extends {Component<Refs>}
 */
export class DpDesigner extends Component {
  #storage = new DesignerStorage();
  #startOverPending = false;
  #modeHelpOpen = false;
  /** Hat der Kunde die Modus-Erklärung schon gesehen/weggeklickt? (localStorage MODE_HELP_STORAGE_KEY) */
  #modeHelpSeen = false;
  #modeToast = null;
  #modeToastTimer = null;
  /** Destrukturierte, geprüfte Funktionen aus @theme/dp-designer-model, siehe #loadDependencies(). */
  #model = null;
  /** Destrukturierte, geprüfte Funktionen aus @theme/dp-designer-formats. */
  #formatsApi = null;
  /** Destrukturierte, geprüfte Funktionen aus @theme/dp-upload. */
  #uploadApi = null;
  /** Modul-Namespaces dp-designer-shell.js / dp-designer-panels.js (Pflicht, siehe Datei-Kopf). */
  #shell = null;
  #panels = null;
  /** Optionale Themes-API aus dp-designer-themes.js (null = ohne Designs, siehe Datei-Kopf). */
  #themesApi = null;
  /** @type {any[]} Katalog aus listThemes(), leer ohne Modul. */
  #themes = [];

  // UI-Zustand der Editor-Hülle
  /** @type {'calendar'|'designs'|'photos'|'layout'|'text'|'events'} */
  #activeTab = 'photos';
  /** Mobil (< 1024 px): Panel als aufgeschobenes Sheet sichtbar. */
  #sheetOpen = false;
  /**
   * Einfach-Modus (Inhaber-Auftrag 2026-09-18, dp-designer-simple.js): 'einfach' = drei geführte Schritte
   * (Fotos · Anpassen · Fertig) – seit 2026-09-22 die Standardansicht, 'voll' = alle Tabs. Ermittelt beim Start
   * aus ?mode=, localStorage `dp-designer:mode` und utm (dp-designer-link.js resolveDesignerMode), danach
   * über den Schalter in der Kopfzeile (setMode). Beide Ansichten teilen Dokument, Fotos und Bühne.
   * @type {'einfach'|'voll'}
   */
  #mode = DESIGNER_DEFAULT_MODE;
  /**
   * Kam der Kunde mit einem selbst gewählten Format (?fmt=…&pick=1 von Produktseite, Format- oder Anlasskarte)?
   * Dann überspringt das Quiz Frage 2 und die Unterfrage, statt genau die Wahl zu überschreiben, für die er
   * geklickt hat. Nur beim Start aus der URL gesetzt – `#context.requestedFormatExplicit` taugt nicht, weil
   * #applyQuizFormat es selbst setzt. (Inhaber-Regel „Variante öffnet genau diese Variante“, 2026-09-22.)
   * @type {boolean}
   */
  #formatPicked = false;
  /** Formatschlüssel aus `?fmt=` (nur wenn er in der URL stand) – für #formatPickedNow(). @type {string|null} */
  #linkFormatKey = null;
  /** Versandart des Kunden: 'standard' | 'express' (aus ?speed=, siehe #readContextFromDataset). */
  #speed = 'standard';

  /**
   * Reihenfolge-Optionen des Quiz. Frage 2 entfällt nur, wenn das gewählte Format wirklich steht (Dokument
   * angelegt) – bei einem unbekannten Formatschlüssel („kommt bald“) führt das Quiz wieder selbst zum Format.
   */
  #quizOpts() {
    return { formatPicked: this.#formatPickedNow() && Boolean(this.#doc) };
  }

  /**
   * Gilt das Format als vom Kunden gewählt? `?pick=1` sagt es ausdrücklich; sonst zählt ein Format aus
   * dem Link, das Frage 2 gar nicht anbietet (die Panels liefern quizOffersFormat, erst nach dem Laden).
   */
  #formatPickedNow() {
    if (this.#formatPicked) return true;
    if (!this.#linkFormatKey || typeof this.#panels?.quizOffersFormat !== 'function') return false;
    return !this.#panels.quizOffersFormat(this.#linkFormatKey);
  }
  /**
   * Quiz des Einfach-Modus (dp-designer-simple.js normalizeQuizState): Bildschirm fuer · ort · ausrichtung · stil ·
   * fotos, Antworten, done = Fertig-Bildschirm. Gespeichert unter `dp-designer:quiz`, damit ein Reload nicht
   * bei Frage 1 beginnt; ein Dokument mit Fotos überspringt das Quiz (#syncQuizWithDoc()).
   * @type {{ screen: string, answers: Record<string, string>, done: boolean, more: boolean }}
   */
  #quiz = { screen: 'fuer', answers: {}, done: false, more: false };
  /** Nur nach einem Bildschirmwechsel true (Einblend-Bewegung der Frage, nicht bei Re-Renders derselben Frage). */
  #quizEntering = false;
  /** Zeitpunkt des letzten Quiz-Bildschirmwechsels (Doppeltipp-Sperre, QUIZ_ANSWER_LOCK_MS). */
  #quizScreenAt = 0;
  /** Fortschritt beim Einlesen von Fotos ({ done, total }) – der Ring im Quiz zeigt ihn; null außerhalb des Einlesens. */
  #uploadProgress = null;
  /** @type {'layout'|'design'|'title'} Werkzeug im Fertig-Panel (mobil je eines sichtbar). */
  #simpleTool = 'layout';
  /**
   * Staffel-Karte „Gleich zwei bestellen?“ (Fertig-Bildschirm/Prüf-Sheet, dp-designer-simple.js renderQuantityCard):
   * Wunschmenge 1 | 2 | 3 (Standard 1 – „Menge 1“ bleibt die Regel, solange der Kunde nichts wählt). Wandert als
   * `wantedQuantity` in den Handoff-Eintrag (detail.getWantedQuantity → dp-designer-handoff.js) und wird auf der
   * Produktseite nur angewendet, wenn die App die Menge anbietet (dp-connector-bridge.js). Nie eine Bestellung.
   */
  #wantedQuantity = 1;
  /** Auf-/Zuklapp-Zustand der Karte über Re-Renders (null = Automatik: Frage 1 „Oma & Opa“/„Geschenk“ oder Wunsch > 1). */
  #quantityCardOpen = null;
  /** Hinweisliste im Prüf-Sheet auf-/zugeklappt (null = Automatik: offen bei leeren Fotoplätzen, siehe shell.renderReviewSheet). */
  #reviewHintsOpen = null;
  #showGuides = false;
  #hintDismissed = false;
  /** Zieh-Tipp unter der Bühne schon gesehen (localStorage TRAY_TIP_SEEN_KEY). */
  #trayTipDismissed = false;
  /** Foto zuerst angetippt, Stelle folgt (Gegenstück zu #assigningElementId). */
  #pendingAssetId = null;
  /** @type {{key?:string, priceFrom?:number}|null} sessionStorage dp:selectedFormat von der Produktseite. */
  #storedSelection = null;
  /** @type {{stage:'saved'|'exporting'|'done'|'error', message?:string, progress?:number}|null} Prüf-Sheet nach „Weiter“, siehe openReviewSheet(). */
  #review = null;
  /** Handoff-Controller aus dp-designer-handoff.js (data-module-handoff, P2-07): Druckdatei erzeugen, prüfen,
   *  übergeben — treibt das Prüf-Sheet über openReviewSheet(). null = Modul fehlt → Sheet endet in 'saved'. */
  #handoff = null;
  /** Foto-Dekodierung hinter EINER Aufrufstelle (#ingestSingleFile); ein Modul dp-designer-image-pipeline.js
   *  (data-module-image-pipeline, Export buildImageAsset(file)) ersetzt sie, ohne diese Datei zu ändern. */
  #imagePipeline = { buildImageAsset };
  /** dp-design-file.js (Entwurf als Datei sichern/öffnen), erst bei Bedarf geladen – siehe #designFile(). */
  #designFileApi = null;
  /** @type {null|'saving'|'opening'} */
  #designFileBusy = null;
  /** @type {null|{tone:'ok'|'error', text:string}} Rückmeldung unter „Entwurf mitnehmen“ */
  #designFileNotice = null;
  /** @type {null|{tone:'ok'|'error', text:string}} Rückmeldung im Panel „Termine“ (bis zur nächsten Aktion). */
  #eventNotice = null;
  /** Formularstand „Termine“ über den Voll-Render hinweg (Tag/Monat bleiben stehen, Text nur bei Fehler). */
  #eventDraft = null;

  /** @type {CalendarFormat[]} */
  #formats = [];
  /** @type {CalendarFormat|null} Format des aktuell offenen Designs. */
  #activeFormat = null;

  /** @type {((args:{month:number,year:number,formatKey?:string,variant?:string,showWeekNumbers?:boolean,showHolidays?:boolean,region?:string,weekStart:string,locale:string})=>string)|null}
   *  Aus @theme/dp-kalendarium-render, siehe #loadKalendariumModule(). null = noch nicht
   *  geladen/nicht verfügbar → #buildCalendariumInnerHtml() nutzt den eingebauten Fallback. */
  #kalendariumRenderFn = null;
  /** Kalendarium-Vorgaben aus den Section-Einstellungen, bereits normalisiert (siehe
   *  normalizeCalendarVariant()/normalizeCalendarRegion()) — werden 1:1 an den Renderer gereicht. */
  #calendarSettings = { variant: 'raster', showWeekNumbers: false, showHolidays: true, region: undefined };
  /** @type {{formats?:Record<string,{label?:string,products?:Record<string,{productName?:string}>}>}|null}
   *  Inhalt von dp-format-map.json (Format-Schlüssel → Shop-Produkte), optional nachgeladen über
   *  data-format-map, siehe #loadFormatMap(). null = nicht (mehr) verfügbar → Chip zeigt nur das Format. */
  #formatMap = null;
  /** @type {{formatKey:string}|null} Solange gesetzt, zeigt der Editor die Wahl "Weitermachen / Neu
   *  starten" (dp-designer-shell.js formatSwitch, keepCurrentDesign/startRequestedFormat). Seit 2026-09-17
   *  setzt der Start sie nicht mehr: ein per URL verlinktes Format wird direkt übernommen (Fotos bleiben),
   *  siehe #restoreOrShowFormatPicker(). Bleibt als Mechanik für eine spätere Rückfrage erhalten. */
  #pendingFormatSwitch = null;
  /** Fokus einmalig auf "Weitermachen" setzen, sobald die Wahl gerendert wurde. */
  #focusPendingChoice = false;
  /** @type {string|null} Per URL angefordertes Format, das der Katalog (noch) nicht kennt —
   *  bleibt im Kontext erhalten und steuert den Hinweis "kommt bald", siehe #renderUnsupportedFormatNotice(). */
  #unsupportedFormatKey = null;
  /** @type {{fromLabel:string,toLabel:string,minQuantity:number}|null} Per URL wurde ein Format angefordert, das es
   *  erst ab 100 Stück gibt (DIN A2 quer, 70 × 50): der Designer öffnet stattdessen das Standardformat des
   *  Produkts und sagt es dem Kunden (QA 2026-09-17 D-08), siehe #redirectNonOrderableRequest()/dismissFormatRedirectNotice(). */
  #formatRedirect = null;
  /** Kalenderjahr, siehe Auftrag "Das Startjahr wird wählbar. Standard ist das kommende Jahr." */
  #startYear = defaultStartYear();

  /** @type {DesignDocument|null} */
  #doc = null;
  #activePageId = null;
  #selectedElementId = null;
  #assigningElementId = null;

  /** @type {ValidationFinding[]} */
  #issues = [];
  #dismissedIssueIds = new Set();
  #uploadErrors = [];
  /** Stabile Live-Region (außerhalb des innerHTML-Renders, sonst überhören Screenreader die Ansage). */
  #liveRegion = null;
  #lastAnnounced = '';
  #nudge = null; // Rückmeldung nach blockiertem „Weiter"
  #nudgeTimer = 0;
  /** @type {Record<string, { factor: number, truncated: boolean }>} Einpass-Ergebnis der Bühnentexte je Slot (title/caption), siehe #fitStageTexts() */
  #textFit = {};
  /** @type {string|null} zuletzt zufällig gewähltes Design (Knopf „Zufälliges Design“) – nie zweimal dasselbe hintereinander */
  #lastRandomThemeId = null;
  #shuffleTimer = 0;
  /** @type {Map<string,string>} */
  #previewUrls = new Map();
  /** @type {{ regular: string, semibold?: string, bold?: string } | null} blob:-URLs der Druckschriften (dp-font-instrument-sans.js) */
  #fontUrls = null;
  /** @type {Map<string, Promise<{ regular: string, bold?: string } | null>>} Schriftwahl-Familien (serif = Lora, hand = Caveat, display = Playfair Display, rounded = Nunito), lazy — siehe #fontFamilyUrls() */
  #fontFamilyPromises = new Map();

  #labels = { heading: '', subline: '', uploadHint: '', continueLabel: 'Weiter', backLabel: 'Zurück' };
  /** `defaultFormatKey` = Vorauswahl aus der Section (gilt nur ohne gespeichertes Design),
   *  `requestedFormatKey`/`requestedProductId` = Anforderung per URL (?fmt=&product=, von Produktseite,
   *  Startseite oder Kopfzeile verlinkt) — sie GEWINNT immer: ein gespeichertes Design in einem anderen
   *  Format wird auf das verlinkte Format umgestellt, die Fotos bleiben (Inhaber-Vorgabe 2026-09-17,
   *  siehe #restoreOrShowFormatPicker()). `requestedFormatExplicit` = ?fmt= stand selbst in der URL
   *  (sonst nur aus ?product= abgeleitet: dann bleibt ein gespeichertes Design desselben Produkts).
   *  `requestedOptions` = ?paper=/?binding= (einmalig beim Start übernommen, #applyRequestedOptions()).
   *  `requestedThemeId` = ?theme= (Beispielkalender „Dieses Design im Designer öffnen“; einmalig in #ensureTheme()). */
  #context = { shopifyProductId: null, shopifyVariantId: null, defaultFormatKey: null, requestedFormatKey: null, requestedProductId: null, requestedFormatExplicit: false, requestedOptions: null, requestedThemeId: null };

  #initialized = false;
  #persistDebounced = () => {};
  #rootController = new AbortController();
  #resizeObserver = null;

  // Pointer-Gesten-Zustand für das aktuell selektierte Foto (siehe Cover-Pan/Zoom-Block).
  #activePointers = new Map();
  #gesture = null;
  #gestureController = null;
  #lastTapAt = 0;

  async connectedCallback() {
    super.connectedCallback();
    if (this.#initialized) return;
    this.#initialized = true;

    this.#persistDebounced = debounce(() => this.#persist(), 400);
    this.#readContextFromDataset();

    // Bewusst NICHT awaited (siehe #loadKalendariumModule()): das Kalendarium-Rendering-Modul
    // ist anders als Modell/Formatkatalog kein Hart-Abbruch-Kandidat, der Designer darf auf
    // seine Antwort nicht warten müssen.
    const kalendariumSrc = this.dataset.moduleKalendarium || '@theme/dp-kalendarium-render';
    this.#loadKalendariumModule(kalendariumSrc);
    // Ebenfalls nicht awaited und nicht fatal: liefert nur den Produktnamen für den Kontext-Chip.
    this.#loadFormatMap(this.dataset.formatMap || '');
    // Designs (Themes): optional, nicht fatal — ohne Modul kein Tab „Designs“.
    this.#loadThemesModule(this.dataset.moduleThemes || '');
    // Optional: Handoff-Modul (Druckdatei erzeugen, prüfen, übergeben — P2-07) und austauschbare Bild-Pipeline.
    this.#loadOptional(this.dataset.moduleHandoff, (m) => {
      if (typeof m.createDesignerHandoff === 'function') this.#handoff = m.createDesignerHandoff(this.#handoffDeps());
    });
    // Druckschriften als Base64-Modul (Shopifys CDN liefert .ttf als WOFF2 aus, siehe dp-font-instrument-sans.js);
    // die blob:-URLs ersetzen die Asset-URLs, sobald das Modul da ist (Getter in #handoffDeps()).
    this.#loadOptional(this.dataset.moduleFonts, (m) => {
      if (typeof m.fontBlobUrls === 'function') this.#fontUrls = m.fontBlobUrls();
    });
    this.#loadOptional(this.dataset.moduleImagePipeline, (m) => { if (typeof m.buildImageAsset === 'function') this.#imagePipeline = m; });

    const deps = await this.#loadDependencies();
    if (!deps) return;
    this.#model = deps.model;
    this.#formatsApi = deps.formatsApi;
    this.#uploadApi = deps.uploadApi;

    try {
      this.#formats = this.#formatsApi.listFormats() ?? [];
    } catch (error) {
      console.error('[dp-designer] Format-Katalog konnte nicht geladen werden.', error);
      this.#renderFatalError('Die verfügbaren Kalenderformate konnten nicht geladen werden. Bitte lade die Seite neu.');
      return;
    }

    await this.#storage.requestPersistence();
    this.#bindGlobalListeners();
    this.#restoreQuiz();
    this.#restoreOrShowFormatPicker();
    this.setAttribute('data-dp-ready', 'true');
  }

  /** Ansage für Screenreader: eine Region je Editor, Text wird ersetzt (kurz geleert, damit Wiederholungen hörbar sind). */
  #announce(text) {
    const message = String(text ?? '').trim();
    if (!message) return;
    if (!this.#liveRegion) {
      const region = document.createElement('div');
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      region.id = 'dp-designer-live';
      document.body.appendChild(region);
      this.#liveRegion = region;
    }
    const region = this.#liveRegion;
    const say = () => { region.textContent = message; this.#lastAnnounced = message; };
    if (this.#lastAnnounced === message) { region.textContent = ''; window.setTimeout(say, 60); } else say();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#liveRegion?.remove();
    this.#liveRegion = null;
    this.#handoff?.dispose();
    this.#rootController.abort();
    this.#gestureController?.abort();
    this.#resizeObserver?.disconnect();
    for (const url of this.#previewUrls.values()) URL.revokeObjectURL(url);
    this.#previewUrls.clear();
  }

  /* ---- Öffentliche Schnittstelle für die (hier nicht implementierte) nächste Stufe ---- */

  /** @returns {DesignDocument|null} */
  getDesignDocument() {
    return this.#doc ? structuredClone(this.#doc) : null;
  }

  /** @param {string} assetId */
  async getOriginalImageBlob(assetId) {
    const record = await this.#storage.getImageBlobs(assetId);
    return record?.original ?? null;
  }

  /** @param {string} assetId */
  async getPreviewImageBlob(assetId) {
    const record = await this.#storage.getImageBlobs(assetId);
    return record?.preview ?? null;
  }

  getValidationIssues() {
    return [...this.#issues];
  }

  isReadyForHandoff() {
    return Boolean(this.#doc) && this.#issues.every((issue) => issue.level !== 'error');
  }

  /* ---------------------------- Initialisierung / Laden ---------------------------- */

  #readContextFromDataset() {
    const ds = this.dataset;
    this.#labels = {
      heading: ds.heading || 'Gestalte deinen Kalender',
      subline: ds.subline || '',
      uploadHint: ds.uploadHint || 'Tippe, um Fotos aus deiner Fotos-App zu wählen, oder ziehe sie hierher.',
      continueLabel: ds.continueLabel || 'Weiter',
      backLabel: ds.backLabel || 'Zurück',
    };
    this.#context = {
      shopifyProductId: ds.shopifyProductId || null,
      shopifyVariantId: ds.shopifyVariantId || null,
      defaultFormatKey: sanitizeFormatKeyParam(ds.defaultFormatKey),
      requestedFormatKey: null,
      requestedProductId: null,
      requestedFormatExplicit: false,
      requestedOptions: null,
      requestedThemeId: null,
    };
    this.#calendarSettings = {
      variant: normalizeCalendarVariant(ds.calendarVariant),
      showWeekNumbers: parseBooleanAttr(ds.calendarShowWeekNumbers, false),
      showHolidays: parseBooleanAttr(ds.calendarShowHolidays, true),
      region: normalizeCalendarRegion(ds.calendarRegion),
    };
    try {
      // Verlinkt von Produktseite, Startseite oder Kopfzeile: /pages/designer?product=<dpId>&fmt=<key>
      // [&paper=&binding=] (dp-designer-link.js readDesignerParams: prüft alle Werte; ?product= ohne
      // ?fmt= liefert das Standardformat des Produkts). Ein unbekannter, aber wohlgeformter
      // Formatschlüssel bleibt erhalten und führt zum "kommt bald"-Hinweis.
      // `format` ist bei Shopify ein reservierter Antwort-Parameter (liefert 404), daher `fmt`.
      const link = readDesignerParams(location.search);
      this.#context.requestedFormatKey = link.fmt;
      this.#context.requestedProductId = link.product;
      this.#context.requestedFormatExplicit = link.fmtExplicit;
      this.#context.requestedOptions = link.paper || link.binding ? { paper: link.paper, binding: link.binding } : null;
      this.#context.requestedThemeId = link.theme;
      // „Der Kunde kam mit seinem Format": ausdrücklich (?pick=1 von Produktseite, Format- und
      // Anlasskarten) – oder, in #quizOpts() nachgeholt, weil das verlinkte Format im Quiz gar nicht
      // vorkommt (Quadrat, Streifen, A3, Familienplaner, Großformat): dann würde Frage 2 die Wahl
      // ersatzlos überschreiben, ohne dass der Kunde sie zurückholen kann (Prüfung 2026-09-22 G-01).
      this.#formatPicked = link.pick === true;
      this.#linkFormatKey = link.fmtExplicit === true ? link.fmt : null;
      // Versandart aus der Produktseite (?speed=express). Sie wandert in den Handoff-Eintrag und
      // entscheidet dort, welche Variante die Brücke im Konfigurator einstellt.
      this.#speed = link.speed === 'express' ? 'express' : 'standard';
    } catch {
      // location.search nicht verfügbar (z. B. in Tests) — Defaults bleiben bestehen.
    }
    try {
      // Auswahl der Produktseite (dp-format-picker.js): liefert den ab-Preis, falls
      // dp-format-map.json (noch) nicht geladen ist. Nur lesen, nie schreiben.
      const raw = sessionStorage.getItem('dp:selectedFormat');
      const parsed = raw ? JSON.parse(raw) : null;
      this.#storedSelection = parsed && typeof parsed === 'object' ? parsed : null;
      this.#hintDismissed = localStorage.getItem(HINT_SEEN_KEY) === '1';
      this.#trayTipDismissed = localStorage.getItem(TRAY_TIP_SEEN_KEY) === '1';
      // Wunschmenge der Staffel-Karte (nur 1/2/3; beim Rendern noch einmal gegen das Angebot des Formats geprüft).
      const wanted = Number(sessionStorage.getItem(WANTED_QUANTITY_KEY));
      this.#wantedQuantity = [1, 2, 3].includes(wanted) ? wanted : 1;
    } catch {
      // Privater Modus / gesperrter Speicher — ohne Vorauswahl weiterarbeiten.
    }
    this.#resolveModeAtBoot();
    this.#maybeOpenModeHelp();
  }

  /* --------------------------------- Einfach-Modus --------------------------------- */

  /**
   * Ansicht beim Start: ?mode= gewinnt, sonst die gespeicherte Wahl, sonst ein Erstbesuch aus einer
   * Social-Anzeige (utm_source instagram/tiktok/…, utm_medium paid/social/cpc) → „einfach“, sonst „voll“.
   * URL und utm werden einmalig gespeichert – danach gilt die gespeicherte bzw. per Schalter gewählte Ansicht.
   */
  #resolveModeAtBoot() {
    let search = '';
    let stored = null;
    try { search = location.search; } catch { /* Tests ohne location */ }
    try { stored = localStorage.getItem(DESIGNER_MODE_STORAGE_KEY); } catch { /* Speicher gesperrt */ }
    const { mode, source } = resolveDesignerMode({ search, stored });
    this.#mode = mode;
    if (source === 'url' || source === 'utm') this.#storeMode(mode);
    this.#dispatchModeEvent(source);
  }

  #storeMode(mode) {
    try { localStorage.setItem(DESIGNER_MODE_STORAGE_KEY, mode); } catch { /* Speicher gesperrt — nur für diese Sitzung */ }
  }

  /** Analytics-Hook für spätere Pixel (keine externen Requests): `dp:designer-mode` mit { mode, source }. */
  #dispatchModeEvent(source) {
    try {
      document.dispatchEvent(new CustomEvent('dp:designer-mode', { detail: { mode: this.#mode, source } }));
    } catch { /* kein document (Tests) */ }
  }

  /**
   * Schalter „Einfach | Alle Optionen“ (Kopfzeile) und Link „Alle Optionen anzeigen“ (Schritt 2). Nichts geht
   * verloren: gleiches Dokument, gleiche Fotos. Beim Wechsel zu „einfach“ startet Schritt 1 ohne Fotos, sonst 2.
   * @param {{mode:string}} data @param {Event} [event] @param {string} [source] Herkunft für das Analytics-Ereignis
   */
  setMode({ mode }, event, source = 'toggle') {
    const next = sanitizeModeParam(mode);
    if (!next) return;
    const changed = next !== this.#mode;
    this.#mode = next;
    this.#storeMode(next);
    if (next === 'einfach') {
      this.#sheetOpen = false;
      this.#syncQuizWithDoc();
    }
    if (this.#doc || this.#panels) this.#render();
    if (changed) {
      this.#dispatchModeEvent(source);
      this.#announce(next === 'einfach' ? 'Geführter Modus: wir führen dich Schritt für Schritt zum Kalender.' : 'Alle Optionen: alle Werkzeuge des Designers sind sichtbar.');
      this.#showModeToast(next);
      this.querySelector(`[data-focus-key="mode:${next}"]`)?.focus({ preventScroll: true });
    }
  }

  /* --------------------------- Erklärung des Schalters --------------------------- */

  /** Sichtbarer Hinweis nach dem Wechsel (3 s), zusätzlich zur Screenreader-Ansage. */
  #showModeToast(mode) {
    this.#modeToast = mode;
    clearTimeout(this.#modeToastTimer);
    this.#modeToastTimer = setTimeout(() => { this.#modeToast = null; this.#patchModeUi(); }, 3200);
    this.#patchModeUi();
  }

  /**
   * Hilfe-Popover beim ersten Besuch — aber NICHT im geführten Modus (Conversion-Audit 2026-09-22 #7):
   * dort lag es auf 390 px über Frage 3, Frage 4 und dem Upload-Bildschirm und kostete einen Pflichtklick
   * an der teuersten Stelle des Trichters. Ein Erstbesucher im Quiz sucht keinen Moduswechsel. Statt des
   * Overlays steht der Hinweis „Mehr Einfluss? Alle Optionen“ einmalig auf dem Fertig-Bildschirm
   * (view.modeHintVisible, dp-designer-simple.js renderDonePanel) — ein Angebot statt einer Hürde.
   * Im Voll-Modus bleibt es wie bisher; über „?" ist es überall erreichbar.
   */
  #maybeOpenModeHelp() {
    let seen = false;
    try { seen = localStorage.getItem(MODE_HELP_STORAGE_KEY) === '1'; } catch { seen = true; }
    this.#modeHelpSeen = seen;
    // Auch im Voll-Modus nicht mehr von selbst öffnen: das Popover lag über Bühne und Hinweiskarte und
    // blieb beim Tabwechsel stehen (Inhaber-Befund 2026-09-22). Stattdessen trägt das „?" einen Punkt,
    // bis es einmal geöffnet wurde (view.modeHelpNew → .dp-mode__help--new).
  }

  toggleModeHelp() {
    this.#modeHelpOpen = !this.#modeHelpOpen;
    if (this.#modeHelpOpen) this.#markModeHelpSeen();
    this.#patchModeUi();
    if (this.#modeHelpOpen) this.querySelector('[data-focus-key="mode:help-panel"]')?.focus({ preventScroll: true });
    else this.querySelector('[data-focus-key="mode:help"]')?.focus({ preventScroll: true });
  }

  /** Merken, dass die Modus-Erklärung gesehen wurde — Popover und Fertig-Hinweis erscheinen dann nicht mehr von selbst. */
  #markModeHelpSeen() {
    this.#modeHelpSeen = true;
    try { localStorage.setItem(MODE_HELP_STORAGE_KEY, '1'); } catch { /* Speicher gesperrt */ }
  }

  /**
   * Escape schließt das Modus-Popover (es hat role="dialog" und den Fokus – ohne Taste blieb nur der Klick,
   * Prüfung 2026-09-22 P2). Der Fokus geht zurück auf das „?“, wie beim Prüf-Sheet.
   * @param {KeyboardEvent} event
   */
  handleModeHelpKeydown(event) {
    if (event?.key !== 'Escape') return;
    event.preventDefault();
    this.dismissModeHelp();
  }

  dismissModeHelp() {
    this.#modeHelpOpen = false;
    this.#markModeHelpSeen();
    this.#patchModeUi();
    this.querySelector('[data-focus-key="mode:help"]')?.focus({ preventScroll: true });
  }

  /** „Mehr Einfluss?"-Hinweis auf dem Fertig-Bildschirm wegklicken — kein Pflichtklick, er verschwindet auch von selbst nie wieder. */
  dismissModeHint() {
    this.#markModeHelpSeen();
    this.#render();
  }

  /** „Alle Optionen" aus dem Fertig-Hinweis: gilt zugleich als gesehen, damit der Hinweis nicht zurückkommt. */
  openAllOptions() {
    this.#markModeHelpSeen();
    this.setMode({ mode: 'voll' }, undefined, 'fertig-hinweis');
  }

  /** Nur den Schalter-Bereich neu zeichnen (kein Voll-Render, Bühne/Fokus bleiben). */
  #patchModeUi() {
    const wrap = this.querySelector('.dp-mode-wrap');
    if (!wrap || typeof this.#shell?.renderModeSwitch !== 'function') { this.#render(); return; }
    const toast = wrap.nextElementSibling?.classList?.contains('dp-mode-toast') ? wrap.nextElementSibling : null;
    const view = { mode: this.#mode, panels: this.#panels, modeHelpOpen: this.#modeHelpOpen, modeHelpNew: !this.#modeHelpSeen, modeToast: this.#modeToast };
    const html = this.#shell.renderModeSwitch(view) + (typeof this.#shell.renderModeToast === 'function' ? this.#shell.renderModeToast(view) : '');
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    toast?.remove();
    wrap.replaceWith(...tpl.content.childNodes);
  }

  /** Pfeiltasten im Schalter (role=radiogroup): links/rechts wechseln die Ansicht. @param {{mode:string}} data @param {KeyboardEvent} event */
  handleModeKeydown({ mode }, event) {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    this.setMode({ mode: mode === 'einfach' ? 'voll' : 'einfach' });
  }

  /* ------------------------------------ Quiz ------------------------------------ */

  /** Gespeicherte Antworten lesen (nach dem Laden der Panels, die den Prüfer liefern). */
  #restoreQuiz() {
    let raw = null;
    try { raw = localStorage.getItem(QUIZ_STORAGE_KEY); } catch { /* Speicher gesperrt */ }
    this.#quiz = typeof this.#panels?.normalizeQuizState === 'function' ? this.#panels.normalizeQuizState(raw) : { screen: 'fuer', answers: {}, done: false, more: false };
  }

  #persistQuiz() {
    try { localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(this.#quiz)); } catch { /* Speicher gesperrt — nur für diese Sitzung */ }
  }

  /**
   * Quiz und Dokument in Einklang bringen: ein Dokument mit Fotos überspringt das Quiz (Fertig-Bildschirm);
   * Bildschirme ab „Stil“ brauchen ein Dokument – ohne eines zurück zu „Wo?“, das es anlegt.
   */
  #syncQuizWithDoc() {
    const photos = this.#doc ? Object.keys(this.#doc.assets.images).length : 0;
    // Läuft das Quiz gerade bei „Fotos" (Reload, Umschalten), bleibt es dort – sonst würde ein Reload nach
    // dem ersten Foto das Quiz still beenden und Titel/Auffüllen entfallen (QA Q-01).
    // „Läuft gerade" heißt: jeder Bildschirm außer der ersten Frage. Sonst beendete ein Reload auf Frage 3
    // das Quiz still, wenn der Kunde nach dem Hochladen zurückgegangen war (Prüfung 2026-09-22 P3).
    const inProgress = !this.#quiz.done && this.#quiz.screen !== 'fuer';
    if (photos > 0 && !this.#quiz.done && !inProgress) { this.#quiz = { ...this.#quiz, done: true }; this.#persistQuiz(); return; }
    if (!this.#doc && !this.#quiz.done && ['stil', 'fotos'].includes(this.#quiz.screen)) { this.#quiz = { ...this.#quiz, screen: 'ort' }; this.#persistQuiz(); }
    // Format schon gewählt (?pick=1): ein gespeicherter Stand auf Frage 2 oder der Unterfrage würde ins Leere
    // führen – die beiden Bildschirme gibt es in diesem Besuch nicht.
    if (this.#quizOpts().formatPicked && !this.#quiz.done && ['ort', 'ausrichtung'].includes(this.#quiz.screen)) {
      this.#quiz = { ...this.#quiz, screen: 'stil' };
      this.#persistQuiz();
    }
    if (!this.#doc && this.#quiz.done) { this.#quiz = { ...this.#quiz, done: false, screen: 'fuer' }; this.#persistQuiz(); }
  }

  /** Analytics-Hook für spätere Pixel: `dp:designer-quiz` mit { question, value, answers } (keine externen Requests). */
  #dispatchQuizEvent(question, value) {
    try {
      document.dispatchEvent(new CustomEvent('dp:designer-quiz', { detail: { question, value, answers: { ...this.#quiz.answers }, screen: this.#quiz.screen, done: this.#quiz.done } }));
    } catch { /* kein document (Tests) */ }
  }

  /**
   * Antwortkarte angetippt: Antwort merken, Wirkung anwenden (Format/Produkt, Stil), automatisch weiter.
   * @param {{q:string, value:string}} data
   */
  quizAnswer({ q, value }) {
    const p = this.#panels;
    if (!p || typeof p.normalizeQuizState !== 'function') return;
    // Doppeltipp auf eine Karte hätte sonst die nächste Frage gleich mitbeantwortet, ohne dass der Kunde
    // sie gesehen hat (Prüfung geführter Modus 2026-09-22 G-06). 350 ms Sperre nach jedem Wechsel.
    if (Date.now() - this.#quizScreenAt < QUIZ_ANSWER_LOCK_MS) return;
    const candidate = p.normalizeQuizState({ ...this.#quiz, answers: { ...this.#quiz.answers, [q]: String(value ?? '') } });
    if (candidate.answers[q] !== String(value ?? '')) return; // unbekannte Frage oder Antwort
    this.#quiz = { ...candidate, screen: this.#quiz.screen };
    if (q === 'ort' || q === 'ausrichtung') {
      if (q === 'ort' && value !== 'wand') delete this.#quiz.answers.ausrichtung;
      const target = p.quizFormatFor(this.#quiz.answers);
      if (target) this.#applyQuizFormat(target);
    } else if (q === 'stil' && this.#doc) {
      this.selectTheme({ themeId: String(value) });
    }
    const next = p.nextQuizScreen(q, this.#quiz.answers, this.#quizOpts());
    this.#quiz = { ...this.#quiz, screen: next ?? this.#quiz.screen };
    this.#persistQuiz();
    this.#dispatchQuizEvent(q, String(value));
    this.#renderQuizStep();
  }

  /** Render nach einem Bildschirmwechsel im Quiz: einmalige Einblend-Bewegung (view.quiz.entering), Fokus auf die Frage. */
  #renderQuizStep() {
    this.#quizScreenAt = Date.now();
    this.#quizEntering = true;
    try { this.#render(); } finally { this.#quizEntering = false; }
    this.#focusQuizQuestion();
  }

  /** Produkt + Format aus dem Quiz wie beim Deep-Link: Dokument anlegen (selectFormat) oder umstellen (switchFormat, Fotos bleiben). */
  #applyQuizFormat({ product, fmt }) {
    if (!this.#isKnownFormat(fmt)) return;
    this.#context.requestedProductId = product ?? null;
    this.#context.requestedFormatKey = fmt;
    this.#context.requestedFormatExplicit = true;
    if (!this.#doc) this.selectFormat({ formatKey: fmt });
    else if (fmt !== this.#activeFormat?.key) this.switchFormat({ formatKey: fmt });
    this.#applyRequestedProductContext();
  }

  /** Zurück-Pfeil (vorherige Frage) und gezielte Sprünge (`/quizStep?screen=fotos`). @param {{screen?:string}} data */
  quizStep({ screen } = {}) {
    const p = this.#panels;
    const target = typeof p?.normalizeQuizScreen === 'function' ? p.normalizeQuizScreen(screen) : null;
    if (!target) return;
    if (!this.#doc && ['stil', 'fotos'].includes(target)) return;
    // „ändern“ unter Frage 1: ab jetzt führt das Quiz wieder selbst zum Format (sonst spränge „Zurück“
    // von Frage 3 über die gerade beantwortete Frage 2 hinweg).
    if (['ort', 'ausrichtung'].includes(target)) { this.#formatPicked = false; this.#linkFormatKey = null; }
    this.#quiz = { ...this.#quiz, screen: target, done: false };
    this.#assigningElementId = null;
    this.#pendingAssetId = null;
    this.#persistQuiz();
    this.#renderQuizStep();
  }

  quizBack() {
    const prev = typeof this.#panels?.prevQuizScreen === 'function'
      ? this.#panels.prevQuizScreen(this.#quiz.screen, this.#quiz.answers, this.#quizOpts())
      : null;
    if (prev) this.quizStep({ screen: prev });
  }

  /* --------------------------------- Von vorne anfangen --------------------------------- */

  /** Knopf „Von vorne anfangen“: nur die Rückfrage einblenden (kein Browser-Dialog). */
  startOver() {
    this.#startOverPending = true;
    this.#render();
    this.querySelector('[data-focus-key="startover:cancel"]')?.focus({ preventScroll: true });
  }

  cancelStartOver() {
    this.#startOverPending = false;
    this.#render();
    this.querySelector('[data-focus-key="startover:btn"]')?.focus({ preventScroll: true });
  }

  /**
   * Alles löschen: Dokument (localStorage), Foto-Blobs (IndexedDB), Vorschau-URLs, Quiz-Antworten.
   * Danach beginnt der Einfach-Modus bei Frage 1, der Voll-Modus bei der Formatauswahl; die Ansicht
   * (Einfach/Alle) bleibt, ebenso das Kalenderjahr.
   */
  async confirmStartOver() {
    this.#startOverPending = false;
    const doc = this.#doc;
    const assetIds = doc ? Object.keys(doc.assets?.images ?? {}) : [];
    for (const [, url] of this.#previewUrls) { try { URL.revokeObjectURL(url); } catch { /* egal */ } }
    this.#previewUrls.clear();
    await Promise.all(assetIds.map((id) => this.#storage.deleteImageBlobs(id).catch(() => null)));
    try {
      if (doc?.designId) localStorage.removeItem(LS_PREFIX + doc.designId);
      localStorage.removeItem(LS_LAST_ID);
      localStorage.removeItem(QUIZ_STORAGE_KEY);
    } catch { /* Speicher gesperrt */ }
    this.#doc = null;
    this.#activePageId = null;
    this.#selectedElementId = null;
    this.#assigningElementId = null;
    this.#pendingAssetId = null;
    this.#sheetOpen = false;
    this.#pendingFormatSwitch = null;
    this.#quiz = { screen: 'fuer', answers: {}, done: false, more: false };
    this.#wantedQuantity = 1;
    try { sessionStorage.removeItem(WANTED_QUANTITY_KEY); } catch { /* Speicher gesperrt */ }
    this.#recomputeValidation();
    this.#render();
    this.#announce('Alles gelöscht. Du kannst von vorne anfangen.');
    try { document.dispatchEvent(new CustomEvent('dp:designer-startover')); } catch { /* Tests */ }
    (this.querySelector('[data-focus-key^="quiz:"]') ?? this.querySelector('.dp-format-grid button, .dp-format-grid a'))?.focus({ preventScroll: true });
  }

  /** „Mehr Stile anzeigen“ (Frage 3, mobil zuerst vier Karten). */
  quizMore() {
    this.#quiz = { ...this.#quiz, more: true };
    this.#persistQuiz();
    this.#render();
    this.querySelector('[data-focus-key^="quiz:stil:"]:nth-of-type(1)')?.focus({ preventScroll: true });
  }

  /**
   * Einfach-Modus verspricht „weniger Fotos – wir füllen auf": Seiten ohne Foto bekommen die
   * hochgeladenen Fotos reihum noch einmal (Titelseite zuerst), damit kein Monat leer bleibt und der
   * Prüfschritt nicht an leeren Plätzen hängt. Der Kunde kann jedes Foto im Fertig-Bildschirm tauschen.
   */
  #fillEmptyPagesByRepeating() {
    if (!this.#doc) return;
    const photoIds = Object.keys(this.#doc.assets.images);
    if (!photoIds.length) return;
    const ordered = [...this.#doc.pages.filter((p) => p.role !== 'back'), ...this.#doc.pages.filter((p) => p.role === 'back')];
    let doc = this.#doc;
    let i = 0;
    let changed = false;
    for (const page of ordered) {
      const current = doc.pages.find((p) => p.pageId === page.pageId);
      const images = current.elements.filter((el) => el.type === 'image');
      if (!images.length || images.some((el) => el.assetId)) continue;
      const [el] = images;
      doc = this.#model.assignPhotoToSlot(doc, page.pageId, el.slotId, photoIds[i % photoIds.length], { frame: el.frame });
      i += 1;
      changed = true;
    }
    if (!changed) return;
    // Wie runMagicLayout: Collage-Seiten mit nur einem Foto auf die Ein-Foto-Variante verkleinern.
    for (const page of ordered) {
      const current = doc.pages.find((p) => p.pageId === page.pageId);
      const images = current.elements.filter((el) => el.type === 'image');
      const filled = images.filter((el) => el.assetId).length;
      if (filled !== 1 || images.length === 1) continue;
      if (this.#panels.layoutIdForPage({ layoutTemplateId: 'one' }) !== 'one') continue;
      doc = this.#panels.applyLayout(doc, page.pageId, 'one', { format: this.#activeFormat, themeLayout: this.#themeLayoutFor(current) });
    }
    this.#doc = { ...doc, meta: { ...doc.meta, magicLayoutUsed: true } };
    this.#recomputeValidation();
    this.#persist();
  }

  /**
   * „Weiter“ bei den Fotos bzw. automatisch ab genug Fotos: Titel aus Frage 1 (oder ein früher gespeicherter
   * eigener Titel) auf die Titelseite, leere Seiten reihum füllen, Quiz beenden → Fertig-Bildschirm.
   */
  quizFinish() {
    if (!this.#doc) return;
    const typed = this.#quiz.answers.titel ?? this.#quizTitleSuggestion();
    const clean = String(typed ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);
    const answers = { ...this.#quiz.answers };
    if (clean) answers.titel = clean; else delete answers.titel;
    this.#quiz = { ...this.#quiz, answers, done: true };
    this.#fillEmptyPagesByRepeating();
    this.#applyCoverTitle(clean);
    this.#persistQuiz();
    this.#dispatchQuizEvent('titel', clean);
    this.#selectedElementId = null;
    this.#assigningElementId = null;
    this.#render();
    this.#announce('Dein Kalender ist fertig. Tippe ein Foto an, um es anzupassen.');
    this.querySelector('[data-focus-key="simple:title"]')?.focus({ preventScroll: true });
  }

  /** Titel auf der Titelseite setzen (leer = entfernen) – wie das Feld im Tab „Text“, ohne Voll-Render. */
  #applyCoverTitle(text) {
    const cover = this.#doc?.pages.find((p) => p.role === 'cover');
    if (!cover) return;
    this.#doc = this.#panels.setPageText(this.#doc, cover, 'title', text, { format: this.#activeFormat, themeLayout: this.#themeLayoutFor(cover) }, this.#model, TEXT_COLOR_TITLE);
    this.#recomputeValidation();
    this.#persist();
  }

  #quizTitleSuggestion() {
    return typeof this.#panels?.quizTitleSuggestion === 'function' ? this.#panels.quizTitleSuggestion(this.#quiz.answers, this.#startYear) : `Unser Jahr ${this.#startYear}`;
  }

  #focusQuizQuestion() {
    const target = this.querySelector('[data-focus-key="quiz:question"]') ?? this.querySelector('[data-focus-key="simple:title"]');
    target?.focus({ preventScroll: true });
    try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch { /* kein window */ }
  }

  /** Werkzeug im Fertig-Panel (Layout · Stil · Titel; mobil je eines sichtbar). @param {{tool:string}} data */
  simpleTool({ tool, focus } = {}) {
    this.#simpleTool = typeof this.#panels?.normalizeSimpleTool === 'function' ? this.#panels.normalizeSimpleTool(tool) : 'layout';
    this.#render();
    // „Ändern“ neben dem Titel im Fertig-Kopf: direkt ins Feld (mobil liegt das Werkzeug unter der Bühne).
    if (focus === '1' && this.#simpleTool === 'title') {
      const input = this.querySelector('#dp-text-title');
      if (input instanceof HTMLElement) { input.scrollIntoView({ block: 'center', behavior: 'auto' }); input.focus({ preventScroll: true }); }
    }
  }

  /* ------------------------------ Staffel-Karte „Gleich zwei bestellen?“ ------------------------------ */

  /**
   * Preis/Staffel des aktiven Angebots für die gewählte Papier/Bindungs-Kombination (doc.product.options →
   * products[dpId].options der Format-Map, dp-designer-link.js resolveOptionOffer). Kopfzeile, Karte „Gleich zwei
   * bestellen?“ und Prüf-Sheet rechnen damit dieselben Zahlen wie der Übergabe-Dialog (QA 2026-09-21 F-01);
   * ohne Datensatz für die Kombination Standardzahlen mit resolved=false. null ohne Karte/Produkt.
   */
  #optionOffer() {
    return resolveOptionOffer(this.#formatMapProduct(), this.#doc?.product?.options);
  }

  /** Staffel-Angebot des aktiven Produkts aus der Format-Map (tiers der Kombination); null ohne 2er-Staffel → keine Karte. */
  #quantityOffer() {
    if (typeof this.#panels?.quantityOfferFrom !== 'function') return null;
    const source = this.#optionOffer() ?? this.#formatMapProduct();
    const surcharge = this.#speedSurcharge();
    if (!surcharge || !Array.isArray(source?.tiers)) return this.#panels.quantityOfferFrom(source);
    // Express: der Aufschlag liegt einmal auf der Sendung, also auf jedem Gesamtpreis der Staffel.
    const tiers = source.tiers.map((t) => ({ ...t, price: Math.round((Number(t.price) + surcharge) * 100) / 100 }));
    return this.#panels.quantityOfferFrom({ ...source, tiers });
  }

  /** Wunschmenge, gegen das aktuelle Angebot geprüft (1, wenn das Format die Menge nicht führt). */
  #effectiveWantedQuantity() {
    const offer = this.#quantityOffer();
    if (!offer || typeof this.#panels?.normalizeWantedQuantity !== 'function') return 1;
    return this.#panels.normalizeWantedQuantity(this.#wantedQuantity, offer);
  }

  /**
   * Segment „1 Stück | 2 Stück | 3 Stück“ angetippt (`on:click="/setWantedQuantity?quantity=2&source=fertig"`).
   * Nur angebotene Mengen; Analytics-Hook `dp:designer-quantity-wish` { quantity, source } (keine Requests).
   * Im offenen Prüf-Sheet wird nur das Sheet neu gerendert, sonst der Fertig-Bildschirm.
   * @param {{quantity:string|number, source?:string}} data
   */
  setWantedQuantity({ quantity, source = 'fertig' } = {}) {
    const offer = this.#quantityOffer();
    const n = Number(quantity);
    if (!offer || !offer.choices.includes(n)) return;
    if (n === this.#wantedQuantity) return;
    this.#wantedQuantity = n;
    if (n > 1) this.#quantityCardOpen = true;
    try { sessionStorage.setItem(WANTED_QUANTITY_KEY, String(n)); } catch { /* Speicher gesperrt — nur für diese Sitzung */ }
    try {
      document.dispatchEvent(new CustomEvent('dp:designer-quantity-wish', { detail: { quantity: n, source: String(source), formatKey: this.#activeFormat?.key ?? null, dpProductId: this.#effectiveProductId() ?? null } }));
    } catch { /* kein document (Tests) */ }
    this.#rerenderQuantityCard();
    const total = typeof this.#panels.euroLabel === 'function' ? this.#panels.euroLabel(offer.totals[n]) : String(offer.totals[n]);
    this.#announce(`Menge ${n} Stück, gesamt ${total}`);
  }

  /** `<details>` der Karte auf-/zugeklappt (on:toggle): Zustand merken, damit ein Re-Render ihn nicht zurücksetzt. */
  toggleQuantityCard(event) {
    const details = event?.target;
    if (details instanceof HTMLElement && 'open' in details) this.#quantityCardOpen = Boolean(details.open);
  }

  /** Pfeiltasten in der Radiogruppe des Segments (WAI-ARIA-Muster): nächste/vorige Menge wählen und fokussieren. */
  handleQuantityKeydown({ source = 'fertig' } = {}, event) {
    const key = event?.key;
    const dir = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 0;
    if (!dir) return;
    const offer = this.#quantityOffer();
    if (!offer?.choices?.length) return;
    event.preventDefault();
    const current = this.#effectiveWantedQuantity();
    const at = Math.max(0, offer.choices.indexOf(current));
    const next = offer.choices[(at + dir + offer.choices.length) % offer.choices.length];
    this.setWantedQuantity({ quantity: next, source });
    this.querySelector(`[data-focus-key="qty:${CSS.escape(String(source))}:${next}"]`)?.focus({ preventScroll: true });
  }

  /** Karte(n) nach einer Mengenwahl nachziehen: offenes Prüf-Sheet nur als Sheet, sonst Voll-Render (Fokus bleibt per data-focus-key). */
  #rerenderQuantityCard() {
    const sheet = this.querySelector('.dp-review');
    if (sheet && this.#review) {
      const active = document.activeElement;
      const focusKey = active instanceof HTMLElement && sheet.contains(active) ? active.getAttribute('data-focus-key') : null;
      sheet.outerHTML = this.#shell.renderReviewSheet(this.#buildView());
      if (focusKey) this.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`)?.focus({ preventScroll: true });
      return;
    }
    this.#render();
  }

  /** Ansicht des Quiz ohne bzw. mit Dokument: nur, was die Fragen brauchen (kein deriveFacts nötig). */
  #buildQuizView() {
    const assetBase = this.#assetBase();
    const doc = this.#doc;
    const themeTiles = this.#panels.themeTiles(this.#themesApi, this.#themes, this.#panels.activeThemeId(doc), this.#shell.normalizeStyles, assetBase);
    const ids = doc ? Object.keys(doc.assets.images) : [];
    const usedIds = new Set(doc ? doc.pages.flatMap((p) => p.elements.filter((el) => el.type === 'image' && el.assetId).map((el) => el.assetId)) : []);
    const progress = doc ? this.#shell.computeProgress(doc) : { completedPages: 0, totalPages: 0 };
    const view = {
      panels: this.#panels,
      labels: this.#labels,
      backHref: this.#backHref(),
      mode: this.#mode,
      modeHelpOpen: this.#modeHelpOpen,
      modeHelpNew: !this.#modeHelpSeen,
      // Einmaliger, dezenter Hinweis "Mehr Einfluss? Alle Optionen" auf dem Fertig-Bildschirm statt des
      // Popovers im Quiz (Conversion-Audit 2026-09-22 #7, Begruendung an #maybeOpenModeHelp).
      modeHintVisible: !this.#modeHelpSeen && this.#mode === 'einfach' && this.#quiz.done === true,
      modeToast: this.#modeToast,
      quiz: { ...this.#quiz, answers: { ...this.#quiz.answers }, entering: this.#quizEntering, formatPicked: this.#quizOpts().formatPicked },
      formatLabel: shortFormatLabel(this.#activeFormat),
      quizStyles: this.#panels.quizStyleTiles(themeTiles, assetBase),
      quizTitleSuggestion: this.#quizTitleSuggestion(),
      startOverPending: this.#startOverPending,
      assetBase,
      startYear: this.#startYear,
      progress,
      photos: ids.map((id) => ({ assetId: id, url: this.#previewUrls.get(id) ?? '', name: doc.assets.images[id].sourceFileName, used: usedIds.has(id), quality: null })),
      uploadErrors: this.#uploadErrors,
      uploadProgress: this.#uploadProgress,
      notices: '',
    };
    return view;
  }

  /** Abhängigkeiten für dp-designer-handoff.js: Asset-URLs aus den data-Attributen der Section, Format-Map live aus diesem Element. */
  #handoffDeps() {
    const ds = this.dataset;
    const self = this;
    return {
      urls: {
        export: ds.moduleExport || '', preflight: ds.modulePreflight || '', worker: ds.printWorker || '', page: ds.printPage || '',
        pdfLib: ds.vendorPdfLib || '', fontkit: ds.vendorFontkit || '',
        // Getter: blob:-URLs aus dp-font-instrument-sans.js (byte-genaue TTF), sonst die Asset-URLs
        // (data-font-regular/-semibold/-bold; Ziffern-Suffixe wuerden im dataset nicht camelCased).
        get fonts() {
          const base = self.#fontUrls ?? { regular: ds.fontRegular || '', semibold: ds.fontSemibold || undefined, bold: ds.fontBold || undefined };
          // Schriftwahl: Lora/Caveat/Playfair Display/Nunito als Lader — dp-print-export.js ruft sie nur, wenn das Dokument die Familie nutzt.
          const families = Object.fromEntries(Object.keys(FONT_FAMILY_DATASET).map((key) => [key, () => self.#fontFamilyUrls(key)]));
          return { ...base, ...families };
        },
        store: ds.moduleHandoffStore || '', formatMap: ds.formatMap || '',
      },
      getFormatMap: () => this.#formatMap,
    };
  }

  /**
   * blob:-URLs einer Schriftwahl-Familie für die PDF: Base64-Modul (data-module-font-<familie>, siehe
   * FONT_FAMILY_DATASET: dp-font-lora.js / dp-font-caveat.js / dp-font-playfair-display.js / dp-font-nunito.js)
   * erst beim ersten Export mit dieser Schrift laden; ohne Modul die TTF-Asset-URLs
   * (data-font-<familie>-regular/-bold). null = nichts konfiguriert.
   * @param {'serif'|'hand'|'display'|'rounded'} key
   */
  #fontFamilyUrls(key) {
    if (!this.#fontFamilyPromises.has(key)) {
      const ds = this.dataset;
      const attr = FONT_FAMILY_DATASET[key] ?? key;
      const cap = attr.charAt(0).toUpperCase() + attr.slice(1);
      const src = ds[`moduleFont${cap}`];
      const fallback = { regular: ds[`font${cap}Regular`] || '', bold: ds[`font${cap}Bold`] || undefined };
      this.#fontFamilyPromises.set(key, (async () => {
        if (src) {
          try {
            const m = await import(src);
            if (typeof m.fontBlobUrls === 'function') return m.fontBlobUrls();
          } catch (error) {
            console.warn('[dp-designer] Schriftmodul nicht geladen — nutze Asset-URLs.', { src, error });
          }
        }
        return fallback.regular ? fallback : null;
      })());
    }
    return this.#fontFamilyPromises.get(key);
  }

  /**
   * „Zurück“: Produktseite aus dem Referrer (gleicher Ursprung, /products/…), sonst die Produktseite des
   * aktiven Formats aus der Format-Map (mit ?fmt=), sonst Rückfall-URL der Section. Vorher landete ein
   * Deep-Link ohne Referrer in /collections/all, obwohl das Produkt aus dem Format bekannt ist (QA 2026-09-17 D-05).
   */
  #backHref() {
    try {
      const ref = document.referrer ? new URL(document.referrer) : null;
      if (ref && ref.origin === location.origin && /\/products\//.test(ref.pathname)) return ref.pathname + ref.search;
    } catch {
      // ungültiger Referrer — Rückfall unten
    }
    const handle = this.#formatMapProduct()?.productHandle;
    if (typeof handle === 'string' && /^[a-z0-9-]+$/i.test(handle)) {
      const key = this.#activeFormat?.key;
      return `/products/${handle}${key ? `?fmt=${encodeURIComponent(key)}` : ''}`;
    }
    const fallback = String(this.dataset.backUrl ?? '').trim();
    return fallback.startsWith('/') ? fallback : '/collections/all';
  }

  /**
   * dp-Produkt des aktiven Dokuments: aus dem Dokument, sonst aus ?product=, sonst aus der Katalog-Gruppe des
   * Formats (dp-designer-link.js PRODUCT_ID_BY_GROUP: genau ein Shop-Produkt je Gruppe). Damit haben Chip,
   * ab-Preis, „Zurück“, Kalenderart und Handoff auch ohne (oder mit unbekanntem) ?product= einen gültigen
   * Produktkontext (QA 2026-09-17 D-05). @param {object} [format] Format statt des aktiven
   * @returns {string|null}
   */
  #effectiveProductId(format = this.#activeFormat) {
    return this.#doc?.product?.dpProductId ?? this.#context.requestedProductId ?? productIdForFormat(format) ?? null;
  }

  /**
   * „ab 18,44 €“ — Grundpreis bei Menge 1 aus dp-format-map.json (Produkt aus #effectiveProductId()) für die
   * gewählte Papier/Bindungs-Kombination (#optionOffer; 250 g glänzend + silber bei A4 hoch: „ab 18,86 €“). Solange
   * die Karte noch lädt: Produktseiten-Auswahl, sonst Formatkatalog. Ist die Karte da, zählt nur sie — der
   * Katalogpreis ist älter (A4 hoch 15,83 € statt 18,44 €) und stand bei unbekanntem ?product= im Kopf (D-05).
   */
  /** Preis hinter #priceLabel() als Zahl (mit Aufschlag der gewählten Lieferzeit); null ohne Preis. */
  #priceValue() {
    const format = this.#activeFormat;
    if (!format) return null;
    const stored = this.#storedSelection?.key === format.key ? this.#storedSelection?.priceFrom : null;
    const candidates = this.#formatMap ? [this.#optionOffer()?.priceFrom ?? null] : [stored, format.priceFromEur];
    const price = candidates.find((v) => Number.isFinite(Number(v)) && Number(v) > 0);
    if (price === undefined) return null;
    return Math.round((Number(price) + this.#speedSurcharge()) * 100) / 100;
  }

  #priceLabel() {
    const format = this.#activeFormat;
    if (!format) return '';
    const stored = this.#storedSelection?.key === format.key ? this.#storedSelection?.priceFrom : null;
    const candidates = this.#formatMap
      ? [this.#optionOffer()?.priceFrom ?? null]
      : [stored, format.priceFromEur];
    const price = candidates.find((v) => Number.isFinite(Number(v)) && Number(v) > 0);
    if (price === undefined) return '';
    return priceFromLabel(Math.round((Number(price) + this.#speedSurcharge()) * 100) / 100);
  }

  /** dp-designer-themes.js optional laden (nur listThemes ist Pflicht, Rest defensiv); nach dem Eintreffen einmal neu rendern. @param {string} src */
  async #loadThemesModule(src) {
    if (!src) return;
    let module;
    try {
      module = await import(src);
    } catch (error) {
      console.warn('[dp-designer] dp-designer-themes.js konnte nicht geladen werden — Designer läuft ohne Tab „Designs“.', { src, error });
      return;
    }
    if (typeof module?.listThemes !== 'function') {
      console.warn('[dp-designer] dp-designer-themes.js liefert keinen Export "listThemes" — Designer läuft ohne Tab „Designs“.', { src, module });
      return;
    }
    const pick = (name) => (typeof module[name] === 'function' ? module[name] : null);
    this.#themesApi = {
      listThemes: module.listThemes,
      getTheme: pick('getTheme'),
      applyTheme: pick('applyTheme'),
      layoutForPage: pick('layoutForPage'),
      themeStyles: pick('themeStyles'),
      sampleImagesFor: pick('sampleImagesFor'),
      kalendariumOptions: pick('kalendariumOptions'),
      defaultCoverTitle: pick('defaultCoverTitle'),
      // Eigene Farben (2026-09-16): resolveTheme/normalizeThemeOverrides/isDarkColor + Farbauswahl.
      resolveTheme: pick('resolveTheme'),
      normalizeThemeOverrides: pick('normalizeThemeOverrides'),
      isDarkColor: pick('isDarkColor'),
      COLOR_CHOICES: module.COLOR_CHOICES ?? null,
      // Schriftwahl: Kacheln „Schrift“ (Modern/Klassisch/Handschrift) im Panel „Designs“.
      FONT_CHOICES: module.FONT_CHOICES ?? null,
    };
    try {
      // Kann vor #loadDependencies() fertig sein — deshalb hier ohne panels.themeIdOf().
      this.#themes = (this.#themesApi.listThemes() ?? []).filter((t) => t && (t.id ?? t.key ?? t.themeId) != null);
    } catch (error) {
      console.warn('[dp-designer] listThemes() ist fehlgeschlagen.', error);
      this.#themes = [];
    }
    if (this.#doc) {
      this.#ensureTheme();
      this.#doc = this.#refitOverlappingPages(this.#doc);
      if (this.#activeTab === 'photos' && Object.keys(this.#doc.assets.images).length === 0 && !this.#selectedElementId) this.#activeTab = 'designs';
      this.#render();
    }
  }

  /** Optionales Modul laden; Fehlen oder Fehler sind kein Problem. @param {string} src @param {(m:any)=>void} onLoaded */
  async #loadOptional(src, onLoaded) {
    if (!src) return;
    try {
      onLoaded(await import(src));
    } catch (error) {
      console.warn('[dp-designer] Optionales Modul nicht geladen.', { src, error });
    }
  }

  /**
   * Lädt dp-format-map.json (Format → Shop-Produkte: Name, Preis, Handle, Eignung) nach.
   * Nicht fatal: ohne Karte zeigt der Chip nur das Format, alles gilt als geeignet. @param {string} url
   */
  async #loadFormatMap(url) {
    if (!url || typeof fetch !== 'function') return;
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      if (!json || typeof json !== 'object' || !json.formats || typeof json.formats !== 'object') {
        throw new Error('Unerwartete Struktur (kein "formats"-Objekt).');
      }
      this.#formatMap = json;
      // Auch die Formatauswahl (ohne Dokument) neu zeichnen, sobald sie steht: die Karten tragen
      // Produktname und ab-Preis aus der Karte (QA 2026-09-16, Befund 25).
      if (this.#doc || this.querySelector('.dp-format-grid')) this.#render();
    } catch (error) {
      console.warn('[dp-designer] dp-format-map.json konnte nicht geladen werden — der Kontext-Chip zeigt nur das Format.', { url, error });
    }
  }

  /** Pflichtmodule per dynamic import() laden (siehe Datei-Kopf); bei Fehler ist die Fehlermeldung schon gerendert → null. */
  async #loadDependencies() {
    let modelModule;
    let formatsModule;
    let uploadModule;
    let shellModule;
    let panelsModule;

    // Tinkers Importmap (snippets/scripts.liquid) ist eine Kerndatei und kennt unsere
    // Module nicht. Die Section reicht deshalb die aufgelösten Asset-URLs als
    // data-module-*-Attribute durch; damit funktioniert import() ohne Importmap-Eintrag.
    // Der Kurzname bleibt als Rückfallebene, falls die Attribute einmal fehlen.
    const modelSrc = this.dataset.moduleModel || '@theme/dp-designer-model';
    const formatsSrc = this.dataset.moduleFormats || '@theme/dp-designer-formats';
    const uploadSrc = this.dataset.moduleUpload || '@theme/dp-upload';
    const shellSrc = this.dataset.moduleShell || '@theme/dp-designer-shell';
    const panelsSrc = this.dataset.modulePanels || '@theme/dp-designer-panels';

    try {
      [modelModule, formatsModule, uploadModule, shellModule, panelsModule] = await Promise.all([
        import(modelSrc),
        import(formatsSrc),
        import(uploadSrc),
        import(shellSrc),
        import(panelsSrc),
      ]);
    } catch (error) {
      console.error('[dp-designer] Datenmodell, Format-Katalog, Upload-Prüfmodul oder Editor-Hülle konnte nicht geladen werden.', { modelSrc, formatsSrc, uploadSrc, shellSrc, panelsSrc, error });
      this.#renderFatalError('Der Kalender-Designer konnte nicht geladen werden. Bitte lade die Seite neu oder versuche es später erneut.');
      return null;
    }

    const model = {
      createDesign: modelModule.createDesign,
      addPhoto: modelModule.addPhoto,
      removePhoto: modelModule.removePhoto,
      assignPhotoToSlot: modelModule.assignPhotoToSlot,
      setSlotTransform: modelModule.setSlotTransform,
      setPhotoLook: modelModule.setPhotoLook,
      applyLookToAll: modelModule.applyLookToAll,
      setText: modelModule.setText,
      validateDesign: modelModule.validateDesign,
      migrate: modelModule.migrate,
      // Eigene Termine (Panel „Termine“, Kalendarium jeder Monatsseite)
      addEvent: modelModule.addEvent,
      removeEvent: modelModule.removeEvent,
      listEvents: modelModule.listEvents,
      eventsForMonth: modelModule.eventsForMonth,
      // Drehen/Spiegeln (2026-09-16): fehlten im Adapter – die Handler prüfen typeof und blieben live stumm.
      rotatePhoto: modelModule.rotatePhoto,
      flipPhoto: modelModule.flipPhoto,
    };
    // Optional (2026-09-17): Bildqualität vorab – Badges in Fotoliste/Bühne, siehe #photoQualities(). Ohne Export keine Badges.
    if (typeof modelModule.assessDocumentPhotos === 'function') model.assessDocumentPhotos = modelModule.assessDocumentPhotos;
    const formatsApi = {
      listFormats: formatsModule.listFormats,
      getFormat: formatsModule.getFormat,
    };
    // Optional (Tischkalender 2026-09-16): Darstellung „zeile" bei kleinen Seiten, siehe #preferredVariant().
    if (typeof formatsModule.preferredVariant === 'function') formatsApi.preferredVariant = formatsModule.preferredVariant;
    // Optional (QA 2026-09-17 D-08): Formate erst ab 100 Stück per Deep-Link umleiten, siehe #redirectNonOrderableRequest().
    if (typeof formatsModule.isOrderableFromOne === 'function') formatsApi.isOrderableFromOne = formatsModule.isOrderableFromOne;
    const uploadApi = {
      inspectFile: uploadModule.inspectFile,
      checkPrintability: uploadModule.checkPrintability,
    };
    const shellFns = ['renderShell', 'renderSheet', 'renderContext', 'renderValidation', 'renderNotices', 'renderPageStrip', 'renderPanel', 'renderContextChip', 'normalizeStyles', 'renderModeSwitch'];
    const panelsFns = ['reflowDoc', 'applyLayout', 'setPageText', 'pageText', 'layoutIdForPage', 'normalizeSamples', 'sampleUrlFor', 'renderPanelBody', 'tabsFor', 'tabLabel', 'escapeHtml', 'icon', 'pct', 'textIsOverlay', 'effectiveCalendar', 'normalizeCalendarSettings', 'applyCalendarToPages', 'periodLabel'];

    const allFnsPresent = [...Object.values(model), ...Object.values(formatsApi), ...Object.values(uploadApi)]
      .every((fn) => typeof fn === 'function')
      && shellFns.every((n) => typeof shellModule[n] === 'function')
      && panelsFns.every((n) => typeof panelsModule[n] === 'function');
    if (!allFnsPresent) {
      console.error('[dp-designer] Erwartete Exporte fehlen in dp-designer-model / -formats / dp-upload / dp-designer-shell / dp-designer-panels.', { modelModule, formatsModule, uploadModule, shellModule, panelsModule });
      this.#renderFatalError('Der Kalender-Designer ist noch nicht vollständig verknüpft. Bitte versuche es später erneut.');
      return null;
    }
    this.#shell = shellModule;
    this.#panels = panelsModule;
    // Optional (Tempo 2026-09-18, #prepareFile): billige Vorprüfung ohne Dekodieren – Typ und Größe wie inspectFile,
    // damit der einzige Dekodier-Lauf der von buildImageAsset bleibt. Fehlt der Export, prüft inspectFile wie bisher.
    const minBytes = Number(uploadModule.MIN_FILE_SIZE_BYTES);
    const maxBytes = Number(uploadModule.MAX_FILE_SIZE_BYTES);
    if (typeof uploadModule.isAcceptedType === 'function' && minBytes > 0 && maxBytes > minBytes) {
      uploadApi.quickCheck = (file) => file instanceof Blob && file.size >= minBytes && file.size <= maxBytes && uploadModule.isAcceptedType(file);
    }
    return { model, formatsApi, uploadApi };
  }

  #renderFatalError(message) {
    this.innerHTML = `<div class="dp-designer__fatal" role="alert"><p>${escapeHtml(message)}</p></div>`;
  }

  /**
   * Lädt @theme/dp-kalendarium-render optional nach; ohne Modul/Export rendert
   * shell.renderFallbackCalendarium() (nicht fatal, siehe Datei-Kopf). @param {string} src
   */
  async #loadKalendariumModule(src) {
    let module;
    try {
      module = await import(src);
    } catch (error) {
      console.warn('[dp-designer] @theme/dp-kalendarium-render konnte nicht geladen werden — nutze eingebauten Kalendarium-Fallback.', { src, error });
      return;
    }
    const fn = module?.renderMonthCalendarium;
    if (typeof fn !== 'function') {
      console.warn('[dp-designer] @theme/dp-kalendarium-render liefert nicht den erwarteten Export "renderMonthCalendarium" — nutze eingebauten Kalendarium-Fallback.', { src, module });
      return;
    }
    this.#kalendariumRenderFn = fn;
    // Kann NACH dem ersten Render eintreffen (Race mit #loadDependencies/#restoreOrShowFormatPicker)
    // — dann einmal neu rendern, damit die echte Implementierung sofort sichtbar wird statt erst
    // nach der nächsten Nutzerinteraktion.
    if (this.#doc) this.#render();
  }

  /**
   * Startzustand: ?fmt=/?product= bekannt → genau dieses Format öffnen: neues Design, oder ein
   * gespeichertes weiterführen — gleiches Format: Produktkontext nachziehen; anderes Format:
   * Dokument auf das verlinkte Format umstellen (wie /switchFormat, Fotos bleiben). Der Link
   * gewinnt immer (Inhaber-Vorgabe 2026-09-17); nur ein reiner ?product=-Link ohne ?fmt= lässt
   * ein gespeichertes Design desselben Produkts in seinem Format. ?paper=/?binding= werden
   * übernommen. ?fmt= unbekannt → Hinweis „kommt bald“; ohne Parameter gespeichertes Design,
   * sonst Section-Vorauswahl, sonst Formatauswahl.
   */
  #restoreOrShowFormatPicker() {
    this.#redirectNonOrderableRequest();
    const { requestedFormatKey, requestedProductId, requestedFormatExplicit, defaultFormatKey } = this.#context;
    const requestedKnown = Boolean(requestedFormatKey) && this.#isKnownFormat(requestedFormatKey);
    if (requestedFormatKey && !requestedKnown) this.#unsupportedFormatKey = requestedFormatKey;

    if (this.#restoreStoredDesign()) {
      if (requestedKnown) {
        const sameProduct = !requestedProductId || this.#doc.product.dpProductId === requestedProductId;
        const keepStoredFormat = this.#doc.product.formatKey === requestedFormatKey || (!requestedFormatExplicit && sameProduct);
        if (!keepStoredFormat) this.switchFormat({ formatKey: requestedFormatKey });
        this.#applyRequestedProductContext();
      } else {
        // Gespeichertes Design in einem Format, das es erst ab 100 Stück gibt (älterer Stand): wie beim Deep-Link
        // auf das Standardformat des Produkts umstellen (Fotos bleiben) und es sagen, statt halb-offen zu starten.
        const redirect = this.#nonOrderableRedirect(this.#activeFormat);
        if (redirect) {
          this.#formatRedirect = redirect.notice;
          this.switchFormat({ formatKey: redirect.targetKey });
        }
      }
      this.#applyRequestedOptions();
      this.#syncQuizWithDoc();
      this.#render();
      // Bewusst nicht awaited: Vorschauen kommen aus IndexedDB nach, dann einmal neu rendern.
      this.#restorePreviewUrls();
      return;
    }

    const startKey = requestedKnown
      ? requestedFormatKey
      : (defaultFormatKey && this.#isKnownFormat(defaultFormatKey) ? defaultFormatKey : null);
    if (startKey) {
      this.selectFormat({ formatKey: startKey });
      this.#applyRequestedOptions();
      return;
    }
    this.#syncQuizWithDoc();
    this.#render();
  }

  /**
   * Per URL angefordertes Format, das es erst ab 100 Stück gibt (Katalog `orderableFromOne:false`, z. B. DIN A2
   * quer oder 70 × 50): nicht halb-offen mit Upload und „Weiter“ starten (QA 2026-09-17 D-08), sondern das
   * Standardformat desselben Produkts anfordern und den Hinweis „… gibt es erst ab 100 Stück – wir haben …
   * geöffnet“ setzen. Ist auch das Standardformat nicht bestellbar, bleibt die Anforderung unverändert.
   */
  #redirectNonOrderableRequest() {
    const redirect = this.#nonOrderableRedirect(this.#formatFor(this.#context.requestedFormatKey));
    if (!redirect) return;
    this.#context.requestedFormatKey = redirect.targetKey;
    this.#context.requestedFormatExplicit = true;
    this.#formatRedirect = redirect.notice;
  }

  /**
   * Umleitungsziel für ein nicht ab 1 Stück bestellbares Format: Standardformat seines Produkts, sofern das
   * selbst bestellbar und ein anderes ist. null = keine Umleitung nötig/möglich.
   * @param {object|null} format @returns {{targetKey:string, notice:{fromLabel:string,toLabel:string,minQuantity:number}}|null}
   */
  #nonOrderableRedirect(format) {
    if (!format || this.#isOrderableFromOne(format)) return null;
    const productId = productIdForFormat(format) ?? this.#context.requestedProductId;
    const targetKey = productId ? DEFAULT_FORMAT_BY_PRODUCT[productId] : null;
    const target = targetKey && targetKey !== format.key ? this.#formatFor(targetKey) : null;
    if (!target || !this.#isOrderableFromOne(target)) return null;
    return { targetKey, notice: { fromLabel: shortFormatLabel(format), toLabel: shortFormatLabel(target), minQuantity: Number(format.minQuantity) || 100 } };
  }

  /** Katalog-Flag `orderableFromOne` (dp-designer-formats.js isOrderableFromOne); ohne Modul-Funktion direkt aus dem Eintrag. @param {object} format */
  #isOrderableFromOne(format) {
    if (typeof this.#formatsApi?.isOrderableFromOne === 'function') return this.#formatsApi.isOrderableFromOne(format);
    return format?.orderableFromOne !== false && !(Number.isFinite(format?.minQuantity) && format.minQuantity > 1);
  }

  /** Hinweis „… gibt es erst ab 100 Stück – wir haben … geöffnet“ schließen. */
  dismissFormatRedirectNotice() {
    this.#formatRedirect = null;
    this.#render();
    this.querySelector('.dp-slot')?.focus();
  }

  /**
   * ?paper=/?binding= aus dem Link einmalig ins Dokument übernehmen (wie /selectOption); danach ist
   * der Kontext verbraucht, damit eine spätere Wahl im Panel „Kalender“ nicht überschrieben wird.
   */
  #applyRequestedOptions() {
    const requested = this.#context.requestedOptions;
    this.#context.requestedOptions = null;
    if (!requested || !this.#doc) return;
    const options = normalizeProductOptions(this.#doc.product.options);
    let changed = false;
    for (const group of Object.keys(OPTION_VALUES)) {
      const value = requested[group];
      if (value && OPTION_VALUES[group].includes(value) && options[group] !== value) { options[group] = value; changed = true; }
    }
    if (!changed) return;
    this.#doc = { ...this.#doc, product: { ...this.#doc.product, options: this.#coerceOptions(options) }, updatedAt: new Date().toISOString() };
    this.#persist();
    this.#render();
  }

  /**
   * Nach dem Neuladen: Vorschau-URLs aller Fotos des wiederhergestellten Designs aus IndexedDB
   * aufbauen (Fotoliste und belegte Plätze hatten sonst background-image:url('')). Fehlt ein
   * Blob, bleibt das Foto im Dokument und erscheint als Hinweis in der Fotoliste — kein Abbruch.
   */
  async #restorePreviewUrls() {
    const doc = this.#doc;
    if (!doc) return;
    const ids = Object.keys(doc.assets.images).filter((id) => !this.#previewUrls.has(id));
    if (!ids.length) return;
    const missing = [];
    await Promise.all(ids.map(async (id) => {
      try {
        const record = await this.#storage.getImageBlobs(id);
        const blob = record?.preview ?? record?.original ?? null;
        if (blob instanceof Blob) this.#previewUrls.set(id, URL.createObjectURL(blob));
        else missing.push(id);
      } catch (error) {
        console.warn('[dp-designer] Vorschau konnte nicht aus dem Speicher geladen werden.', { assetId: id, error });
        missing.push(id);
      }
    }));
    if (!this.#doc || this.#doc.designId !== doc.designId) return; // inzwischen ein anderes Design
    for (const id of missing) {
      this.#uploadErrors.push({ fileName: doc.assets.images[id]?.sourceFileName ?? id, message: 'Die Vorschau dieses Fotos fehlt in diesem Browser – bitte lade das Foto noch einmal hoch.' });
    }
    if (!this.#gesture) this.#render();
  }

  /** Katalogeintrag ohne Ausnahme; null bei fehlendem oder unbekanntem Schlüssel. @param {string|null|undefined} formatKey */
  #formatFor(formatKey) {
    try {
      return formatKey ? this.#formatsApi.getFormat(formatKey) : null;
    } catch {
      return null;
    }
  }

  /** @param {string} formatKey */
  #isKnownFormat(formatKey) {
    try {
      return Boolean(this.#formatsApi.getFormat(formatKey));
    } catch {
      return false;
    }
  }

  /** Zuletzt gespeichertes Design wiederherstellen; false, wenn keines existiert oder es inkompatibel ist (dann frisch starten). */
  #restoreStoredDesign() {
    const lastId = this.#storage.getLastDesignId();
    if (!lastId) return false;
    const stored = this.#storage.loadDesignDocument(lastId);
    if (!stored) return false;
    if (this.#adoptDocument(stored)) return true;
    this.#doc = null;
    this.#activeFormat = null;
    return false;
  }

  /**
   * Ein gespeichertes Dokument (localStorage oder Entwurfsdatei) zum aktiven machen: migrieren,
   * additive Felder ergänzen, Layout/Design sicherstellen, Prüfung neu rechnen. Bei einem Fehler
   * bleibt der vorherige Zustand unverändert; Rückgabe false.
   * @param {DesignDocument} stored
   */
  #adoptDocument(stored) {
    const previous = { doc: this.#doc, format: this.#activeFormat, pageId: this.#activePageId, year: this.#startYear, tab: this.#activeTab };
    try {
      const doc = this.#model.migrate(stored);
      // Additive Felder (siehe #applyRequestedProductContext()): ältere Dokumente kennen sie nicht.
      this.#doc = { ...doc, product: { dpProductId: null, ...doc.product, options: normalizeProductOptions(doc.product?.options) } };
      this.#activeFormat = this.#formatsApi.getFormat(this.#doc.product.formatKey);
      this.#activePageId = this.#doc.pages[0]?.pageId ?? null;
      this.#startYear = Number.isFinite(this.#doc.meta?.startYear) ? this.#doc.meta.startYear : defaultStartYear();
      // Ältere Dokumente (vor den Seitenlayouts) tragen kein layoutTemplateId — einmal
      // einpassen, damit jede Seite ihre Rahmen nach aktuellem Layout-Katalog hat.
      if (this.#doc.pages.some((p) => !p.layoutTemplateId)) this.#doc = this.#reflow(this.#doc);
      this.#ensureTheme();
      this.#doc = this.#refitOverlappingPages(this.#doc);
      // Ältere Dokumente ohne calendariumYear bzw. mit gewähltem Startmonat: Monat/Jahr je Seite nachziehen.
      this.#syncCalendarPages();
      this.#activeTab = this.#themes.length && Object.keys(this.#doc.assets.images).length === 0 ? 'designs' : 'photos';
      this.#recomputeValidation();
      return true;
    } catch (error) {
      console.warn('[dp-designer] Gespeichertes Design konnte nicht wiederhergestellt werden.', error);
      this.#doc = previous.doc;
      this.#activeFormat = previous.format;
      this.#activePageId = previous.pageId;
      this.#startYear = previous.year;
      this.#activeTab = previous.tab;
      return false;
    }
  }

  /** Shop-Produktkontext (dpProductId aus ?product=, Shopify-IDs) additiv ins Dokument schreiben — nur bei passendem Format. */
  #applyRequestedProductContext() {
    if (!this.#doc) return;
    const next = this.#withProductContext(this.#doc);
    if (next === this.#doc) return;
    this.#doc = next;
    this.#persist();
  }

  /**
   * @param {DesignDocument} doc @returns {DesignDocument} dasselbe Objekt, wenn nichts zu ändern war.
   * dpProductId: ?product= (bekannte ID, dp-designer-link.js), sonst das Produkt der Katalog-Gruppe des Formats —
   * so bleibt der Kontext auch bei ?product=999 oder einem Link ohne ?product= gültig (QA 2026-09-17 D-05).
   */
  #withProductContext(doc) {
    const { requestedProductId, shopifyProductId, shopifyVariantId } = this.#context;
    const product = { ...doc.product };
    let changed = false;
    // Genau ein Shop-Produkt je Katalog-Gruppe: das Format entscheidet; ?product= bzw. der alte Wert nur, wenn
    // das Format keiner Gruppe zuzuordnen ist (dann passt ein ?product=10 zu DIN A4 hoch auch nicht besser).
    const wanted = productIdForFormat(this.#formatFor(product.formatKey)) ?? requestedProductId ?? product.dpProductId ?? null;
    if (product.dpProductId !== wanted) { product.dpProductId = wanted; changed = true; }
    if (!OPTION_VALUES.paper.includes(product.options?.paper) || !OPTION_VALUES.binding.includes(product.options?.binding)) { product.options = normalizeProductOptions(product.options); changed = true; }
    if (shopifyProductId && product.shopifyProductId !== shopifyProductId) { product.shopifyProductId = shopifyProductId; changed = true; }
    if (shopifyVariantId && product.shopifyVariantId !== shopifyVariantId) { product.shopifyVariantId = shopifyVariantId; changed = true; }
    return changed ? { ...doc, product } : doc;
  }

  #bindGlobalListeners() {
    const { signal } = this.#rootController;
    // Escape schließt das oberste Overlay – vorher ging das nur im Prüf-Sheet und im Modus-Popover
    // (Prüfung Voll-Modus 2026-09-22 P2). Ein Handler am Editor, Reihenfolge = Schichtung.
    // Am Dokument, nicht am Editor: der Fokus liegt nach einem Klick oft auf <body>, dann erreicht ein
    // keydown den Editor nie – genau deshalb schloss Escape bisher nichts (Prüfung Voll-Modus 2026-09-22).
    document.addEventListener('keydown', this.#onEscapeKey, { signal });
    // Zweiter Tab: localStorage meldet fremde Schreibvorgänge. Ohne das überschrieb die nächste
    // Änderung hier kommentarlos, was im anderen Tab entstanden war (Prüfung Voll-Modus 2026-09-22).
    window.addEventListener('storage', this.#onForeignSave, { signal });
    this.addEventListener('dragstart', this.#onDragStart, { signal });
    this.addEventListener('dragover', this.#onDragOver, { signal });
    this.addEventListener('dragleave', this.#onDragLeave, { signal });
    this.addEventListener('drop', this.#onDrop, { signal });
    this.#resizeObserver = new ResizeObserver(() => { this.#measureStage(); this.#hydrateSlotTransforms(); });
    this.#resizeObserver.observe(this);
  }

  /* ------------------------------- Format-Auswahl ------------------------------- */

  /** @param {{formatKey:string}} data */
  selectFormat({ formatKey }) {
    let format;
    try {
      format = this.#formatsApi.getFormat(formatKey);
    } catch (error) {
      console.error('[dp-designer] Unbekanntes Format ausgewählt.', error);
      return;
    }

    let doc = this.#model.createDesign('calendar', formatKey);
    // Kalenderjahr im Dokument festhalten (überlebt Neuladen über DesignerStorage), damit die
    // Monatsseiten unten das richtige Jahr rendern — siehe #startYear/changeStartYear().
    doc = { ...doc, meta: { ...doc.meta, startYear: this.#startYear } };
    // Shop-Produktkontext (dpProductId aus ?product=, Shopify-IDs) additiv am Dokument
    // festhalten — nur, wenn das gewählte Format auch das angeforderte ist; wählt der Nutzer in
    // der Formatauswahl etwas anderes, passt die Produkt-ID nicht mehr zwingend dazu.
    const matchesRequest = !this.#context.requestedFormatKey || this.#context.requestedFormatKey === formatKey;
    doc = matchesRequest
      ? this.#withProductContext(doc)
      : { ...doc, product: { ...doc.product, dpProductId: productIdForFormat(format), options: normalizeProductOptions(doc.product.options), shopifyProductId: this.#context.shopifyProductId, shopifyVariantId: this.#context.shopifyVariantId } };
    this.#pendingFormatSwitch = null;
    this.#focusPendingChoice = false;
    this.#unsupportedFormatKey = null;
    if (this.#formatRedirect && this.#formatRedirect.toLabel !== shortFormatLabel(format)) this.#formatRedirect = null;
    this.#activeFormat = format;
    // Jede Seite bekommt sofort leere Fotoplätze (Geometrie aus dp-designer-panels.js bzw. Design).
    this.#doc = this.#reflow(doc);
    this.#ensureTheme();
    this.#syncCalendarPages();
    this.#activePageId = this.#doc.pages[0]?.pageId ?? null;
    this.#activeTab = this.#themes.length ? 'designs' : 'photos';
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  /* ------------------------------------ Designs (Themes) ------------------------------------ */

  /**
   * Aktives Design INKLUSIVE eigener Farben (doc.theme.overrides → resolveTheme() in dp-designer-themes.js):
   * alle Verbraucher (Styles, Kalendarium, Layouts/Dekor, Druck, Flipbook-Abzug) sehen dieselben Farben.
   * Die Kennung des Designs bleibt die des Katalogs (activeThemeId), z. B. für die Kachel-Markierung.
   * Adapter-Funktionen liegen in dp-designer-panels.js (themeIdOf, activeThemeId, resolveTheme, …).
   */
  #activeTheme() {
    const base = this.#panels.resolveTheme(this.#themesApi, this.#themes, this.#panels.activeThemeId(this.#doc));
    const themed = this.#panels.withThemeOverrides(this.#themesApi, base, this.#doc?.theme?.overrides);
    // Kunden-Stil (Raster/Zeile) bestimmt die Bandhöhe des Layouts mit (dp-designer-calendar-panel.js withStyleVariant).
    return typeof this.#panels.withStyleVariant === 'function' ? this.#panels.withStyleVariant(themed, this.#doc) : themed;
  }

  #themeLayoutFor(page, theme = this.#activeTheme()) {
    return this.#panels.themeLayoutFor(this.#themesApi, theme, page, this.#activeFormat, { year: this.#startYear });
  }

  /** Basis-URL des Asset-Ordners (sampleImagesFor liefert Dateinamen wie "dp-hero.jpg"). */
  #assetBase() {
    const explicit = String(this.dataset.assetBase ?? '').trim();
    if (explicit) return explicit;
    const src = String(this.dataset.moduleShell ?? '');
    const m = /^(.*\/)[^/?#]+(?:[?#].*)?$/.exec(src);
    return m ? m[1] : '';
  }

  /** Alle Seiten einpassen: mit Design über applyTheme() (Fotos bleiben), sonst Layout-Katalog aus dp-designer-panels.js. */
  #reflow(doc) {
    const themeId = this.#panels.activeThemeId(doc);
    if (themeId && this.#themesApi?.applyTheme && this.#themes.some((t) => this.#panels.themeIdOf(t) === themeId)) {
      try {
        const themed = this.#themesApi.applyTheme(doc, themeId);
        if (themed && Array.isArray(themed.pages)) return themed;
      } catch (error) {
        console.warn('[dp-designer] applyTheme() ist fehlgeschlagen — nutze Standardlayout.', error);
      }
    }
    return this.#panels.reflowDoc(doc, this.#activeFormat, (page) => this.#themeLayoutFor(page));
  }

  /** Design im Dokument anwenden (ohne Render); liefert true bei Erfolg. */
  #applyThemeToDoc(themeId) {
    if (!this.#themesApi?.applyTheme || !this.#doc) return false;
    let doc;
    try {
      doc = this.#themesApi.applyTheme(this.#doc, themeId);
    } catch (error) {
      console.warn('[dp-designer] applyTheme() ist fehlgeschlagen.', error);
      return false;
    }
    if (!doc || typeof doc !== 'object' || !Array.isArray(doc.pages)) return false;
    if (this.#panels.themeIdOf(doc.theme) !== themeId) doc = { ...doc, meta: { ...doc.meta, themeId } };
    this.#doc = doc;
    return true;
  }

  /** Vorausgewähltes Design: ohne Wahl im Dokument das erste aus dem Katalog. */
  #ensureTheme() {
    if (!this.#themesApi || !this.#themes.length || !this.#doc) return;
    // ?theme=<id> aus dem Link (dp-designer-link.js readDesignerParams) einmalig anwenden, sobald Dokument
    // und Designliste da sind – wie ein Klick im Panel „Designs“; unbekannte IDs werden verworfen.
    const requested = this.#context.requestedThemeId;
    if (requested) {
      this.#context.requestedThemeId = null;
      if (this.#themes.some((t) => this.#panels.themeIdOf(t) === requested) && this.#applyThemeToDoc(requested)) {
        this.#settlePhotoSlots();
        this.#persist();
        return;
      }
    }
    const current = this.#panels.activeThemeId(this.#doc);
    if (current && this.#themes.some((t) => this.#panels.themeIdOf(t) === current)) return;
    if (this.#applyThemeToDoc(this.#panels.themeIdOf(this.#themes[0]))) this.#persist();
  }

  /** @param {{themeId:string}} data */
  selectTheme({ themeId }) {
    if (!themeId || !this.#themes.some((t) => this.#panels.themeIdOf(t) === themeId)) return;
    // Der neue Stil bringt eigene Seitenlayouts mit; eine vorher von Hand gewählte Aufteilung geht dabei
    // verloren. Das stand nirgends (Prüfung 2026-09-22 G-03): die Karten tragen jetzt den Hinweis, und
    // wer eine Seite selbst eingeteilt hatte, hört es zusätzlich als Ansage.
    const before = (this.#doc?.pages ?? []).map((p) => p.layoutTemplateId ?? '');
    if (!this.#applyThemeToDoc(themeId)) return;
    this.#settlePhotoSlots();
    this.#recomputeValidation();
    this.#persist();
    this.#render();
    const changed = (this.#doc?.pages ?? []).filter((p, i) => (p.layoutTemplateId ?? '') !== before[i]).length;
    if (changed > 0) this.#announce(`Stil gewechselt. ${changed} ${changed === 1 ? 'Seite hat' : 'Seiten haben'} das Layout des neuen Stils bekommen, deine Fotos bleiben.`);
  }

  /**
   * Nach dem Designwechsel (selectTheme, randomizeTheme, ?theme= aus dem Link): freie Fotos verteilen und Seiten
   * mit weniger Fotos als Plätzen auf die passende Variante bringen – dieselbe Regel wie „Alle leeren Seiten füllen“
   * (dp-designer-panels.js fillAndShrinkPages). Vorher blieben bei 13 Fotos auf Editorial/Familienjahr/Terrakotta
   * 12–13 Seiten mit leeren Plätzen, die weiß gedruckt worden wären (QA 2026-09-21 F-02, 2026-09-17 D-06).
   */
  #settlePhotoSlots() {
    if (!this.#doc) return;
    const { doc, assigned, shrunk } = this.#fillAndShrink(this.#doc);
    if (assigned || shrunk) this.#doc = doc;
  }

  /**
   * Knopf „Zufälliges Design“ (Tab „Designs“, Inhaber-Wunsch 2026-09-17, Punkt 12c): Design, Kalendarium-Stil
   * und Akzentfarbe zufällig – Fotos, Texte und Termine bleiben. Nie dasselbe Design zweimal hintereinander
   * (auch nicht das gerade aktive); Stil und Farbe wechseln ebenfalls, wenn es Alternativen gibt. Kurze
   * Animation der Bühne (.is-shuffling, CSS in dp-designer.liquid).
   * @param {{ random?: () => number }} [data] Testbarkeit: eigener Zufallsgenerator (0 ≤ x < 1)
   */
  randomizeTheme(data = {}) {
    if (!this.#doc || !this.#themes.length || !this.#themesApi?.applyTheme) return;
    const rnd = typeof data?.random === 'function' ? data.random : Math.random;
    const pick = (list) => list[Math.floor(rnd() * list.length)];
    const currentTheme = this.#panels.activeThemeId(this.#doc);
    const themeIds = this.#themes.map((t) => this.#panels.themeIdOf(t)).filter(Boolean);
    const themePool = themeIds.filter((id) => id !== currentTheme && id !== this.#lastRandomThemeId);
    const themeId = pick(themePool.length ? themePool : themeIds.filter((id) => id !== currentTheme).length ? themeIds.filter((id) => id !== currentTheme) : themeIds);
    if (!themeId) return;
    // Akzentfarbe aus der Farbwahl (COLOR_CHOICES.accent), nie die aktuell wirksame.
    const accents = (this.#themesApi.COLOR_CHOICES?.accent ?? []).map((c) => String(c.hex).toUpperCase());
    const currentAccent = String(this.#doc.theme?.overrides?.accent ?? this.#activeTheme()?.palette?.accent ?? '').toUpperCase();
    const accentPool = accents.filter((hex) => hex !== currentAccent);
    const accent = accentPool.length ? pick(accentPool) : null;
    // Kalendarium-Stil (dp-kalendarium-styles.js über das Kalender-Panel), nie der aktuell wirksame.
    const current = this.#effectiveCalendar();
    const styleIds = (typeof this.#panels.styleChoices === 'function' ? this.#panels.styleChoices(current.style) : []).map((s) => s.id).filter((id) => id !== current.style);
    const style = styleIds.length ? pick(styleIds) : null;

    const before = this.#doc;
    const { font, background } = this.#doc.theme?.overrides ?? {};
    const overrides = { ...(font ? { font } : {}), ...(background ? { background } : {}), ...(accent ? { accent } : {}) };
    const theme = { ...(this.#doc.theme ?? {}) };
    delete theme.overrides;
    this.#doc = { ...this.#doc, theme: Object.keys(overrides).length ? { ...theme, overrides } : theme };
    if (style) {
      const { showHolidays, region, showWeekNumbers, startMonth } = current;
      this.#doc = { ...this.#doc, meta: { ...this.#doc.meta, calendar: { showHolidays, region, showWeekNumbers, startMonth, style } } };
    }
    if (!this.#applyThemeToDoc(themeId)) { this.#doc = before; return; }
    this.#lastRandomThemeId = themeId;
    this.#selectedElementId = null;
    this.#assigningElementId = null;
    this.#settlePhotoSlots();
    this.#doc = this.#refitOverlappingPages(this.#doc);
    this.#syncCalendarPages();
    this.#recomputeValidation();
    this.#persist();
    this.#render();
    this.#playShuffle();
    const themeName = this.#themes.find((t) => this.#panels.themeIdOf(t) === themeId)?.name ?? themeId;
    const parts = [`Design ${themeName}`];
    if (style) parts.push(`Stil ${this.#panels.styleLabel?.(style) ?? style}`);
    if (accent) parts.push(`Akzent ${this.#panels.colorChoiceName(this.#themesApi, 'accent', accent) ?? accent}`);
    this.#announce(`Zufällige Mischung: ${parts.join(', ')}. Deine Fotos bleiben.`);
    this.querySelector('[data-focus-key="theme:random"]')?.focus({ preventScroll: true });
  }

  /** Bühne kurz „mischen“ (Klasse .is-shuffling für 480 ms) – respektiert prefers-reduced-motion per CSS. */
  #playShuffle() {
    const stage = this.querySelector('.dp-stage__scroll');
    if (!(stage instanceof HTMLElement)) return;
    window.clearTimeout(this.#shuffleTimer);
    stage.classList.remove('is-shuffling');
    void stage.offsetWidth; // Animation neu starten
    stage.classList.add('is-shuffling');
    this.#shuffleTimer = window.setTimeout(() => stage.classList.remove('is-shuffling'), 480);
  }

  /**
   * Eigene Farbe (Panel „Designs“ → „Farben anpassen“): Farbfeld `on:click="/setThemeColor?role=accent&value=%23…"`
   * oder Farbwähler `<input type="color" on:change="/setThemeColor?role=background">` (Wert aus dem Feld).
   * Nur `accent`/`background` als #RRGGBB (normalizeThemeOverrides in dp-designer-themes.js prüft);
   * danach das Design neu anwenden, damit Textfarben (Titel auf Band/Fläche, heller Text auf dunklem
   * Grund) im Dokument mitziehen – Bühne, Flipbook und PDF lesen dasselbe Dokument.
   * @param {{role:string, value?:string}} data @param {Event} [event]
   */
  setThemeColor({ role, value }, event) {
    if (!this.#doc || !this.#themesApi?.applyTheme) return;
    const r = role === 'accent' || role === 'background' ? role : null;
    const raw = typeof value === 'string' && value ? value : /** @type {HTMLInputElement|null} */ (event?.target)?.value;
    const hex = this.#panels.normalizeColorHex(this.#themesApi, raw);
    if (!r || !hex) return;
    const overrides = { ...(this.#doc.theme?.overrides ?? {}), [r]: hex };
    if (!this.#applyThemeOverrides(overrides)) return;
    const choice = this.#panels.colorChoiceName(this.#themesApi, r, hex);
    this.#announce(`${r === 'accent' ? 'Akzentfarbe' : 'Seitenhintergrund'}: ${choice ?? hex}.`);
  }

  /** Alle eigenen Farben verwerfen → Farben des gewählten Designs (die Schriftwahl bleibt). */
  resetThemeColors() {
    const overrides = this.#doc?.theme?.overrides;
    if (!overrides) return;
    if (!this.#applyThemeOverrides(overrides.font ? { font: overrides.font } : null)) return;
    this.#announce('Farben zurückgesetzt – das Design zeigt wieder seine eigenen Farben.');
  }

  /**
   * Schriftwahl (Panel „Designs“ → „Schrift“): Kachel `on:click="/setThemeFont?value=serif"`.
   * 'sans' | 'serif' | 'hand' | 'display' | 'rounded' (FONT_CHOICES in dp-designer-themes-data.js; normalizeThemeOverrides prüft).
   * Gilt für Titel und Bildunterschriften; das Kalendarium bleibt Instrument Sans. Danach das Design
   * neu anwenden, damit die Textelemente font.family tragen – Bühne, Flipbook und PDF lesen dasselbe Dokument.
   * @param {{value?:string}} data
   */
  setThemeFont({ value }) {
    if (!this.#doc || !this.#themesApi?.applyTheme) return;
    const choice = this.#panels.fontChoiceFor(this.#themesApi, value);
    if (!choice) return;
    const { font: _previous, ...rest } = this.#doc.theme?.overrides ?? {};
    if (!this.#applyThemeOverrides({ ...rest, font: choice.value })) return;
    this.#announce(`Schrift: ${choice.name}.`);
  }

  /** Overrides ins Dokument schreiben (null = Feld entfernen), Design neu anwenden, sichern, rendern. */
  #applyThemeOverrides(overrides) {
    const themeId = this.#panels.activeThemeId(this.#doc);
    if (!themeId) return false;
    const theme = { ...(this.#doc.theme ?? {}) };
    delete theme.overrides;
    const before = this.#doc;
    this.#doc = { ...this.#doc, theme: overrides ? { ...theme, overrides } : theme };
    if (!this.#applyThemeToDoc(themeId)) { this.#doc = before; return false; }
    this.#recomputeValidation();
    this.#persist();
    this.#render();
    return true;
  }

  /* --------------------------------- Layout / Text / Tabs --------------------------------- */

  /** @param {{layoutId:string}} data */
  selectLayout({ layoutId }) {
    const page = this.#activePage();
    if (!page || this.#panels.layoutIdForPage({ layoutTemplateId: layoutId }) !== layoutId) return;
    if (page.role !== 'month' && layoutId === 'caption') return;
    this.#doc = this.#panels.applyLayout(this.#doc, page.pageId, layoutId, { format: this.#activeFormat, themeLayout: this.#themeLayoutFor(page) });
    this.#selectedElementId = null;
    this.#assigningElementId = null;
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  /**
   * Text (Titel/Bildunterschrift) live übernehmen. Kein Voll-Render — das Eingabefeld muss den
   * Fokus behalten; nur Bogen und Seitenstreifen werden ausgetauscht. `pageId` (Monatsliste im
   * Tab „Text“) erlaubt die Bildunterschrift einer anderen als der aktiven Seite.
   * @param {{slot:string, pageId?:string}} data @param {Event} event
   */
  setPageText({ slot, pageId }, event) {
    const page = pageId ? this.#doc?.pages.find((p) => p.pageId === pageId) ?? null : this.#activePage();
    if (!page || !this.#doc) return;
    const text = /** @type {HTMLInputElement} */ (event.target).value;
    const color = slot === 'title' ? TEXT_COLOR_TITLE : TEXT_COLOR_CAPTION;
    this.#doc = this.#panels.setPageText(this.#doc, page, slot, text, { format: this.#activeFormat, themeLayout: this.#themeLayoutFor(page) }, this.#model, color);
    if (slot === 'caption') this.#refitCaptionPage(page.pageId, event.target);
    this.#recomputeValidation();
    this.#persistDebounced();
    if (page.pageId === this.#activePageId) this.#patchSheet();
    this.#patchValidationPanel();
    // Fertig-Bildschirm: die Zeile „Titel: …“ über dem Feld gehört zum selben Text (Prüfung 2026-09-22 G-02).
    if (slot === 'title') this.#patchSimpleTitleLine(text);
  }

  /** Titelzeile des Fertig-Panels ohne Voll-Render nachziehen (das Eingabefeld behält den Fokus). */
  #patchSimpleTitleLine(text) {
    const el = this.querySelector('.dp-simple__titleline strong');
    if (!el) return;
    const value = String(text ?? '').replace(/\s+/g, ' ').trim();
    const next = value ? `\u201E${value}\u201C` : 'ohne Titel';
    if (el.textContent !== next) el.textContent = next;
  }

  /**
   * Tab „Text“ auf der Titelseite: „Jahreszahl 2027 als Untertitel einsetzen“ – füllt den Slot `caption` der
   * Titelseite (seit 2026-09-17 auch dort, dp-designer-themes.js coverLayout) mit dem Kalenderjahr.
   */
  setCoverSubtitleYear() {
    const page = this.#activePage();
    if (!page || page.role !== 'cover' || !this.#doc) return;
    const year = String(this.#startYear);
    this.#doc = this.#panels.setPageText(this.#doc, page, 'caption', year, { format: this.#activeFormat, themeLayout: this.#themeLayoutFor(page) }, this.#model, TEXT_COLOR_CAPTION);
    this.#refitCaptionPage(page.pageId, null);
    this.#recomputeValidation();
    this.#persist();
    this.#render();
    this.#announce(`Untertitel auf der Titelseite: ${year}.`);
    this.querySelector('#dp-text-caption')?.focus({ preventScroll: true });
  }

  /**
   * Bildunterschrift: Das Design-Layout hängt vom Text ab (Fotobereich weicht der Zeile aus, siehe
   * layoutForPage in dp-designer-themes.js) — Rahmen der Seite nachziehen (Fotos/Ausschnitte bleiben)
   * und das Zwillingsfeld (Feld der aktiven Seite ↔ Monatsliste) ohne Neuaufbau angleichen.
   */
  /**
   * Gespeicherte Seiten gegen das aktuelle Design-Layout prüfen: ragt ein Fotoplatz in das
   * Kalendarium-Band (z. B. nach Stilwechsel Zeile → Raster, nach Band-Änderungen im Theme oder
   * bei älteren Entwürfen), wird die Seite neu eingepasst – Fotos und Ausschnitte bleiben.
   * Inhaber-Befund 2026-09-16: Collage überlappte das Kalendarium nach Stil „Kreise".
   */
  #refitOverlappingPages(doc) {
    if (!doc || typeof this.#panels?.refitPage !== 'function' || typeof this.#panels?.pageDecor !== 'function') return doc;
    let next = doc;
    for (const page of doc.pages) {
      if (page.role !== 'month') continue;
      try {
        const layout = this.#themeLayoutFor(page);
        const band = this.#panels.pageDecor(page, doc.product, this.#activeFormat, layout)?.calendarium;
        if (!band) continue;
        const overlaps = page.elements.some((el) => el.type === 'image' && el.frame && el.frame.yMm + el.frame.heightMm > band.yMm + 0.5);
        if (overlaps) next = this.#panels.refitPage(next, page, { format: this.#activeFormat, themeLayout: layout });
      } catch { /* Seite bleibt */ }
    }
    return next;
  }

  #refitCaptionPage(pageId, source) {
    const page = this.#doc?.pages.find((p) => p.pageId === pageId);
    if (page && typeof this.#panels.refitPage === 'function') {
      this.#doc = this.#panels.refitPage(this.#doc, page, { format: this.#activeFormat, themeLayout: this.#themeLayoutFor(page) });
    }
    const value = this.#panels.pageText(page, 'caption');
    for (const input of this.querySelectorAll(`input[data-caption-page="${CSS.escape(pageId)}"]`)) {
      if (input !== source && input instanceof HTMLInputElement && input.value !== value) input.value = value;
    }
  }

  /** @param {{tab:string}} data */
  selectTab({ tab }) {
    const known = this.#panels.tabsFor({ hasThemes: this.#themes.length > 0 }).some((t) => t.id === tab);
    if (!known) return;
    const same = this.#activeTab === tab;
    this.#activeTab = tab;
    this.#sheetOpen = same ? !this.#sheetOpen : true;
    if (!same) this.#eventNotice = null;
    this.#render();
  }

  /** Pfeiltasten zwischen den Tabs (role=tablist). @param {{tab:string}} data @param {KeyboardEvent} event */
  handleTabKeydown({ tab }, event) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const step = keys[event.key];
    if (!step) return;
    event.preventDefault();
    const tabs = this.#panels.tabsFor({ hasThemes: this.#themes.length > 0 });
    const idx = tabs.findIndex((t) => t.id === tab);
    const next = tabs[(idx + step + tabs.length) % tabs.length];
    this.#activeTab = next.id;
    this.#render();
    this.querySelector(`[data-focus-key="tab:${next.id}"]`)?.focus();
  }

  closeSheet() {
    this.#sheetOpen = false;
    this.#render();
  }

  toggleGuides() {
    this.#showGuides = !this.#showGuides;
    this.#render();
  }

  dismissHint() {
    this.#hintDismissed = true;
    try { localStorage.setItem(HINT_SEEN_KEY, '1'); } catch { /* Speicher gesperrt — nur für diese Sitzung */ }
    this.#render();
  }

  /** Zieh-Tipp unter der Bühne schließen – er kommt nicht wieder (localStorage). */
  dismissTrayTip() {
    this.#trayTipDismissed = true;
    try { localStorage.setItem(TRAY_TIP_SEEN_KEY, '1'); } catch { /* Speicher gesperrt — nur für diese Sitzung */ }
    this.#render();
  }

  changeFormat() {
    const hasWork = this.#doc && Object.keys(this.#doc.assets.images).length > 0;
    if (hasWork && !window.confirm('Wenn du das Format wechselst, gehen deine bisherigen Fotozuordnungen für dieses Design verloren. Trotzdem fortfahren?')) {
      return;
    }
    this.#doc = null;
    this.#activeFormat = null;
    this.#activePageId = null;
    this.#selectedElementId = null;
    this.#assigningElementId = null;
    this.#pendingAssetId = null;
    this.#sheetOpen = false;
    this.#pendingFormatSwitch = null;
    this.#focusPendingChoice = false;
    this.#issues = [];
    this.#render();
  }

  /* ------------------------- Panel „Kalender“: Kalenderart + Format ------------------------- */

  /**
   * Kalenderart (DP-Produkt) wechseln, ohne den Editor zu verlassen. Führt das Produkt das aktive
   * Format nicht, springt der Kalender auf das erste verfügbare Format dieser Art (Fotos bleiben).
   * @param {{dpProductId:string}} data
   */
  selectProduct({ dpProductId }) {
    const id = sanitizeProductIdParam(dpProductId);
    if (!id || !this.#doc) return;
    const choice = this.#panels.productChoices(this.#formatMap, this.#formatsApi.listFormats()).find((p) => p.id === id && p.available);
    if (!choice) return;
    this.#context.requestedProductId = id;
    this.#doc = { ...this.#doc, product: { ...this.#doc.product, dpProductId: id }, updatedAt: new Date().toISOString() };
    const activeKey = this.#activeFormat?.key;
    if (activeKey && !choice.formatKeys.includes(activeKey)) {
      this.switchFormat({ formatKey: choice.formatKeys[0] });
      return;
    }
    this.#doc = { ...this.#doc, product: { ...this.#doc.product, options: this.#coerceOptions(this.#doc.product.options) } };
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  /**
   * Papier oder Bindung wählen (Panel „Kalender“, `on:click="/selectOption?group=paper&value=glanz-170"`).
   * Nur Schlüssel aus OPTION_VALUES; sie wandern über getOptions() mit der Bestellung in den Handoff-Speicher.
   * @param {{group:string, value:string}} data
   */
  /**
   * Express-Angebot des aktiven Formats aus der Format-Map, aufbereitet fürs Panel: Aufschlag,
   * Lieferzeit als Klartext und die Standard-Lieferzeit daneben. null = dieses Format hat kein Express.
   */
  #expressOffer() {
    const product = this.#formatMapProduct();
    const offer = typeof expressOfferFrom === 'function' ? expressOfferFrom(product) : null;
    if (!offer) return null;
    return {
      surcharge: offer.surcharge,
      days: deliveryLabel(offer.deliveryDays) ?? '3 Werktage',
      standardDays: deliveryLabel(product?.deliveryDays) ?? '5 Werktage',
      // Rohwerte („3-3"/„5-5") für das voraussichtliche Lieferdatum (dp-lieferdatum.js).
      deliveryDays: offer.deliveryDays ?? null,
      standardDeliveryDays: typeof product?.deliveryDays === 'string' ? product.deliveryDays : null,
    };
  }

  /**
   * Aufschlag der gewählten Lieferzeit in Euro — 0 bei Standard und bei Formaten ohne Express.
   * Er fällt einmal je Sendung an (Versandart, nicht Stückpreis), deshalb wird er auf den Gesamtpreis
   * einer Staffelstufe addiert und nicht mit der Menge multipliziert.
   */
  #speedSurcharge() {
    if (this.#speed !== 'express') return 0;
    const offer = this.#expressOffer();
    const n = Number(offer?.surcharge);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /** Zeile fürs Prüf-Sheet: „Express · 3 Werktage“. null, wenn das Format gar kein Express anbietet. */
  #speedFact() {
    const offer = this.#expressOffer();
    if (!offer) return null;
    return this.#speed === 'express' ? `Express · ${offer.days}` : `Standard · ${offer.standardDays}`;
  }

  /** Lieferzeit wählen (Panel „Kalender“). Wandert über getSpeed in den Übergabe-Eintrag. */
  selectSpeed({ value }) {
    const next = String(value ?? '').trim().toLowerCase() === 'express' ? 'express' : 'standard';
    if (next === 'express' && !this.#expressOffer()) return;
    if (next === this.#speed) { this.#render(); return; }
    this.#speed = next;
    this.#render();
    this.#announce(next === 'express' ? 'Express gewählt: Produktion und Versand in 3 statt 5 Werktagen.' : 'Standardversand gewählt.');
  }

  selectOption({ group, value }) {
    if (!this.#doc) return;
    const g = String(group ?? '');
    const v = String(value ?? '');
    if (!OPTION_VALUES[g]?.includes(v)) return;
    if (g === 'paper' && !this.#availablePapers().some((c) => c.key === v)) return;
    const options = normalizeProductOptions(this.#doc.product.options);
    if (options[g] === v) { this.#render(); return; }
    this.#doc = { ...this.#doc, product: { ...this.#doc.product, options: { ...options, [g]: v } }, updatedAt: new Date().toISOString() };
    this.#persist();
    this.#render();
  }

  /**
   * Format wechseln und Fotos, Texte, Layouts und Design mitnehmen: neues Dokument im Zielformat,
   * Seiten nach Index übernommen, dann neu eingepasst (Rahmen aus dem Layout-Katalog bzw. Design).
   * @param {{formatKey:string}} data
   */
  switchFormat({ formatKey }) {
    if (!this.#doc || !formatKey) return;
    if (formatKey === this.#activeFormat?.key) { this.#render(); return; }
    let format;
    try {
      format = this.#formatsApi.getFormat(formatKey);
    } catch (error) {
      console.error('[dp-designer] Unbekanntes Format beim Wechsel.', error);
      return;
    }
    const old = this.#doc;
    let next = this.#model.createDesign('calendar', formatKey);
    next = {
      ...next,
      designId: old.designId,
      createdAt: old.createdAt,
      // Produkt: das der Katalog-Gruppe des neuen Formats (genau ein Shop-Produkt je Gruppe), sonst das bisherige.
      product: { ...next.product, dpProductId: productIdForFormat(format) ?? old.product.dpProductId ?? null, options: normalizeProductOptions(old.product.options), shopifyProductId: old.product.shopifyProductId ?? null, shopifyVariantId: old.product.shopifyVariantId ?? null },
      assets: old.assets,
      meta: { ...next.meta, ...old.meta, editorClientVersion: next.meta.editorClientVersion },
      pages: next.pages.map((page) => {
        const prev = old.pages[page.index];
        return prev ? { ...page, layoutTemplateId: prev.layoutTemplateId ?? null, elements: prev.elements.map((el) => ({ ...el, id: el.id.replace(prev.pageId, page.pageId) })) } : page;
      }),
    };
    if (old.theme) next.theme = old.theme;
    const activeIndex = this.#activePage()?.index ?? 0;
    this.#activeFormat = format;
    this.#context.requestedFormatKey = formatKey;
    this.#unsupportedFormatKey = null;
    if (this.#formatRedirect && this.#formatRedirect.toLabel !== shortFormatLabel(format)) this.#formatRedirect = null;
    this.#pendingFormatSwitch = null;
    this.#doc = this.#reflow(next);
    this.#doc = { ...this.#doc, product: { ...this.#doc.product, options: this.#coerceOptions(this.#doc.product.options) } };
    this.#ensureTheme();
    this.#syncCalendarPages();
    this.#activePageId = this.#doc.pages[activeIndex]?.pageId ?? this.#doc.pages[0]?.pageId ?? null;
    this.#selectedElementId = null;
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  /* ------------------ Per URL angefordertes Format vs. gespeichertes Design ------------------ */

  /** "Weitermachen": angefangenes Design behalten, Wahl schließen (siehe #renderFormatSwitchChoice()). */
  keepCurrentDesign() {
    this.#pendingFormatSwitch = null;
    this.#focusPendingChoice = false;
    this.#render();
    this.querySelector('.dp-slot')?.focus();
  }

  /** "Neu starten": neues Design im per URL angeforderten Format — die Wahl selbst ist die Bestätigung. */
  startRequestedFormat() {
    const formatKey = this.#pendingFormatSwitch?.formatKey;
    this.#pendingFormatSwitch = null;
    this.#focusPendingChoice = false;
    if (!formatKey) { this.#render(); return; }
    this.selectFormat({ formatKey });
    this.querySelector('.dp-slot')?.focus();
  }

  /** Hinweis "Dieses Format kommt bald" im Editor schließen; der angeforderte Schlüssel bleibt im Kontext. */
  dismissUnsupportedFormatNotice() {
    this.#unsupportedFormatKey = null;
    this.#render();
    this.querySelector('.dp-slot')?.focus();
  }

  /* --------------------------------- Kalenderjahr --------------------------------- */

  /** Kalenderjahr aller Monatsseiten wechseln; überlebt bewusst einen Formatwechsel. @param {Event} event */
  changeStartYear(event) {
    const year = Number(/** @type {HTMLSelectElement} */ (event.target).value);
    if (!Number.isFinite(year)) return;
    const previousYear = this.#startYear;
    this.#startYear = year;
    if (this.#doc) {
      this.#doc = { ...this.#doc, meta: { ...this.#doc.meta, startYear: year } };
      this.#syncCalendarPages();
      this.#syncDefaultCoverTitle(previousYear, year);
      this.#persist();
    }
    this.#render();
  }

  /* ------------------------ Kalendarium-Einstellungen des Kunden ------------------------ */

  /**
   * Wirksame Kalendarium-Einstellungen: Shop-Vorgaben (#calendarSettings, inkl. variant)
   * überschrieben durch doc.meta.calendar (dp-designer-calendar-panel.js effectiveCalendar).
   * @returns {{variant:string, showHolidays:boolean, region:string|null, showWeekNumbers:boolean, startMonth:number}}
   */
  #effectiveCalendar() {
    // Design-Darstellung („zeile“ bei Editorial) als Vorgabe hineinreichen: ohne Kundenwahl bleibt der
    // Stil „zeile“, sonst „klar“ — die Kundenwahl (doc.meta.calendar.style) gewinnt (dp-kalendarium-styles.js).
    const themeVariant = this.#themeKalendariumOptions()?.darstellung;
    return this.#panels.effectiveCalendar(this.#doc, { ...this.#calendarSettings, variant: themeVariant ?? this.#calendarSettings.variant });
  }

  /** Tatsächliche Bandhöhe (mm) der Monatsseite aus dem Design-Layout — für die Auto-Umschaltung raster → zeile bei kleinen Formaten. */
  #bandHeightMm(page) {
    try {
      const theme = this.#activeTheme();
      const decor = this.#panels.pageDecor(page, this.#doc.product, this.#activeFormat, this.#themeLayoutFor(page, theme));
      const h = Number(decor?.calendarium?.heightMm);
      return Number.isFinite(h) && h > 0 ? h : null;
    } catch { return null; }
  }

  /** Monat und Jahr je Monatsseite nach Startjahr/Startmonat nachziehen (calendariumMonth/calendariumYear). */
  #syncCalendarPages() {
    if (!this.#doc) return;
    this.#doc = this.#panels.applyCalendarToPages(this.#doc, this.#startYear, this.#effectiveCalendar());
  }

  /**
   * Panel „Kalender“ → Block „Kalendarium“ (`on:change="/setCalendarOption?key=showHolidays"`,
   * Kacheln `on:click="/setCalendarOption?key=style&value=kreise"`): Stil, Feiertage, Bundesland,
   * Kalenderwochen, erster Monat. Der Wert kommt aus `value` (Kacheln) oder dem Formularfeld
   * (Schalter: checked, Auswahl: value); geprüft wird an der Grenze (normalizeCalendarSettings),
   * beim ersten Setzen wandern die Shop-Vorgaben als Grundlage mit ins Dokument. Der Stil wird nur
   * gespeichert, wenn der Kunde ihn gewählt hat (sonst folgt er weiter der Design-Vorgabe).
   * @param {{key:string, value?:string}} data @param {Event} [event]
   */
  setCalendarOption({ key, value }, event) {
    if (!this.#doc) return;
    const k = String(key ?? '');
    const fields = ['showHolidays', 'region', 'showWeekNumbers', 'startMonth', 'style'];
    if (!fields.includes(k)) return;
    const target = /** @type {HTMLInputElement|HTMLSelectElement|null} */ (event?.target ?? null);
    const fromField = target && 'checked' in target && target.type === 'checkbox' ? target.checked : target?.value;
    const raw = typeof value === 'string' && value ? value : fromField;
    const current = this.#effectiveCalendar();
    const next = this.#panels.normalizeCalendarSettings({ ...current, [k]: raw }, current);
    const { showHolidays, region, showWeekNumbers, startMonth, style } = next;
    if (fields.every((f) => next[f] === current[f])) { this.#render(); return; }
    const calendar = { showHolidays, region, showWeekNumbers, startMonth };
    if (k === 'style' || typeof this.#doc.meta?.calendar?.style === 'string') calendar.style = style;
    this.#doc = { ...this.#doc, meta: { ...this.#doc.meta, calendar }, updatedAt: new Date().toISOString() };
    // Stilwechsel Raster ↔ Zeile ändert die Bandhöhe → Seiten neu einpassen (Fotos/Ausschnitte bleiben).
    if (k === 'style' && current.style !== style) this.#doc = this.#reflow(this.#doc);
    this.#syncCalendarPages();
    this.#recomputeValidation();
    this.#persist();
    this.#render();
    const texts = {
      showHolidays: showHolidays ? 'Feiertage werden angezeigt.' : 'Feiertage werden nicht angezeigt.',
      region: region ? `Feiertage für ${target?.selectedOptions?.[0]?.textContent?.trim() || region} eingestellt.` : 'Nur bundesweite Feiertage.',
      showWeekNumbers: showWeekNumbers ? 'Kalenderwochen werden angezeigt.' : 'Kalenderwochen werden nicht angezeigt.',
      startMonth: `Dein Kalender beginnt jetzt im ${MONTH_NAMES_DE[startMonth - 1]} – ${this.#panels.periodLabel(this.#startYear, startMonth)}.`,
      style: `Kalendarium-Stil: ${this.#panels.styleLabel?.(style) ?? style}.`,
    };
    this.#announce(texts[k]);
  }

  /* --------------------------------- Eigene Termine --------------------------------- */

  /**
   * Termin aus dem Formular des Panels „Termine“ übernehmen (`on:submit="/addEvent"`). Die
   * Validierung (Tag passt zum Monat, Label nicht leer, max. 60) macht das Modell; die Meldung
   * erscheint im Panel und in der Live-Region. @param {Event} event
   */
  addEvent(event) {
    event?.preventDefault?.();
    if (!this.#doc || typeof this.#model.addEvent !== 'function') return;
    const field = (ref, selector) => this.refs[ref] ?? this.querySelector(selector);
    const day = field('eventDay', '#dp-event-day')?.value;
    const month = field('eventMonth', '#dp-event-month')?.value;
    const label = field('eventLabel', '#dp-event-label')?.value ?? '';
    try {
      const before = new Set(this.#model.listEvents(this.#doc).map((e) => e.id));
      this.#doc = this.#model.addEvent(this.#doc, { day, month, label });
      const added = this.#model.listEvents(this.#doc).find((e) => !before.has(e.id));
      this.#eventNotice = { tone: 'ok', text: `Eingetragen: ${added ? `${added.day}. ${MONTH_NAMES_DE[added.month - 1]} – ${added.label}` : 'Termin'}.` };
      this.#eventDraft = { day: Number(day) || 1, month: Number(month) || 1, label: '' };
      this.#persist();
    } catch (error) {
      this.#eventNotice = { tone: 'error', text: error?.message || 'Der Termin konnte nicht eingetragen werden.' };
      this.#eventDraft = { day: Number(day) || 1, month: Number(month) || 1, label: String(label) };
    }
    this.#render();
    this.#announce(this.#eventNotice.text);
    this.querySelector('#dp-event-label')?.focus();
  }

  /** Termin entfernen (`on:click="/removeEvent?id=…"`). @param {{id:string}} data */
  removeEvent({ id }) {
    if (!this.#doc || typeof this.#model.removeEvent !== 'function') return;
    const key = String(id ?? '');
    const gone = this.#model.listEvents(this.#doc).find((e) => e.id === key);
    this.#doc = this.#model.removeEvent(this.#doc, key);
    this.#eventNotice = gone ? { tone: 'ok', text: `Entfernt: ${gone.label}.` } : null;
    this.#persist();
    this.#render();
    if (gone) this.#announce(this.#eventNotice.text);
  }

  /** Daten fürs Panel „Termine“ (dp-designer-events-panel.js). */
  #eventsView() {
    const list = typeof this.#model?.listEvents === 'function' && this.#doc ? this.#model.listEvents(this.#doc) : [];
    // Ohne eigene Eingabe ist der Monat der aktiven Seite vorausgewählt.
    const draft = this.#eventDraft ?? { day: 1, month: this.#activePage()?.calendariumMonth ?? 1, label: '' };
    return { list, max: 60, labelMax: 24, notice: this.#eventNotice, draft };
  }

  /** Trägt die Titelseite noch den Standardtitel des Designs („Unser Jahr 2027“), zieht das Jahr mit. */
  #syncDefaultCoverTitle(previousYear, year) {
    const theme = this.#activeTheme();
    const fn = this.#themesApi?.defaultCoverTitle;
    const cover = this.#doc?.pages.find((p) => p.role === 'cover');
    if (!theme || typeof fn !== 'function' || !cover) return;
    try {
      const current = this.#panels.pageText(cover, 'title');
      // Der Standardtitel kann von einem früher gewählten Design stammen (applyTheme behält den
      // Text) – deshalb gegen die Standardtitel ALLER Designs mit dem alten Jahr vergleichen.
      const candidates = [theme, ...this.#themes];
      const isDefault = Boolean(current) && candidates.some((t) => { try { return fn(t, previousYear) === current; } catch { return false; } });
      if (isDefault) this.#doc = this.#model.setText(this.#doc, cover.pageId, 'title', fn(theme, year));
    } catch {
      // Titel bleibt, wie er ist.
    }
  }

  /* --------------------------------- Fotos hinzufügen --------------------------------- */

  openFilePicker() {
    // querySelector als Rückfall: Components MutationObserver startet verzögert, refs können fehlen.
    const input = this.refs.fileInput ?? this.querySelector('input[type="file"]');
    input?.click();
  }

  /** @param {Event} event */
  async handleFileInputChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const files = Array.from(input.files ?? []);
    input.value = '';
    await this.#ingestFiles(files);
  }

  /**
   * Vom Rechner abgelegte Dateien: wie über den Dateidialog verarbeiten; fällt genau ein Foto auf
   * einen Fotoplatz, wird es dort direkt eingesetzt.
   * @param {File[]} files @param {string|null} elementId
   */
  async #ingestDroppedFiles(files, elementId) {
    if (files.length === 1 && isDesignFileName(files[0].name)) { await this.#importDesignFile(files[0]); return; }
    if (!files.length || !this.#doc) return;
    const before = new Set(Object.keys(this.#doc.assets.images));
    await this.#ingestFiles(files);
    if (!elementId || files.length !== 1) return;
    const added = Object.keys(this.#doc.assets.images).find((id) => !before.has(id));
    if (!added) return;
    this.#assignAsset(elementId, added);
    this.#pendingAssetId = null;
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  /**
   * Fotos einlesen (Dateidialog, Ablegen, Kamera): zu dritt parallel dekodieren + speichern (#prepareFile), dabei
   * den Fortschritt live zeigen, dann die Ergebnisse in Auswahlreihenfolge ins Dokument übernehmen (#commitAsset).
   * Im Quiz „Jetzt deine Fotos“ endet das Quiz von selbst, sobald jede Seite ein Foto bekommen kann (quizFinish).
   * @param {File[]} files
   */
  async #ingestFiles(files) {
    this.#uploadErrors = [];
    if (!files.length || !this.#doc) return;
    const before = Object.keys(this.#doc.assets.images).length;
    this.#uploadProgress = { done: 0, total: files.length };
    this.#render();
    const results = new Array(files.length);
    await runLimited(files, INGEST_CONCURRENCY, async (file, i) => {
      results[i] = await this.#prepareFile(file);
      this.#uploadProgress = { done: Math.min(files.length, (this.#uploadProgress?.done ?? 0) + 1), total: files.length };
      this.#patchUploadProgress();
    });
    this.#uploadProgress = null;
    for (const [i, result] of results.entries()) {
      if (!result) continue;
      if (result.error) this.#uploadErrors.push({ fileName: files[i].name, message: result.error });
      else this.#commitAsset(result.asset);
    }
    this.#recomputeValidation();
    this.#persist();
    this.#render();
    const added = Object.keys(this.#doc.assets.images).length - before;
    const failed = this.#uploadErrors.length;
    // Einfach-Modus (Quiz „Jetzt deine Fotos“ und Fertig-Bildschirm): neue Fotos landen sofort auf den leeren Seiten (Magic Layout).
    if (added > 0 && this.#mode === 'einfach' && !this.#assigningElementId) await this.runMagicLayout();
    if (added || failed) this.#announce(`${added} ${added === 1 ? 'Foto' : 'Fotos'} hinzugefügt${failed ? `, ${failed} nicht lesbar` : ''}.`);
    // Fotos können auch mitten im Quiz ankommen (auf eine Frage gezogen). Dann gehört der Kunde auf den
    // Fotoschritt – sonst blieb die Frage stehen, quizFinish und das Auffüllen der Seiten entfielen und
    // nach einem Reload fehlten die Seiten (Prüfung geführter Modus 2026-09-22 G-05).
    if (added > 0 && this.#mode === 'einfach' && !this.#quiz.done && this.#quiz.screen !== 'fotos' && this.#doc) {
      this.#quiz = { ...this.#quiz, screen: 'fotos' };
      this.#persistQuiz();
      this.#render();
    }
    // Genug Fotos für alle Seiten → direkt zum Fertig-Bildschirm, ohne weiteren Tipp (Tempo-Ziel unter 90 s).
    if (added > 0 && this.#quizPhotosComplete()) this.quizFinish();
  }

  /**
   * Eine Datei vorbereiten: billige Prüfung ohne Dekodieren (Typ, Größe – dp-upload.js quickCheck), dann EIN
   * Dekodier-Lauf (buildImageAsset: Maße + Vorschau) und Ablage in IndexedDB. Fehlermeldungen liefert erst im
   * Fehlerfall dp-upload.js inspectFile (HEIC, defekt) – so wird kein Foto doppelt dekodiert.
   * @param {File} file @returns {Promise<{ asset?: object, error?: string }>}
   */
  async #prepareFile(file) {
    const api = this.#uploadApi;
    const quickOk = typeof api.quickCheck === 'function' && api.quickCheck(file);
    if (!quickOk) {
      const inspection = await api.inspectFile(file);
      if (!inspection.ok) return { error: inspection.message };
    }
    try {
      // Einzige Aufrufstelle der Foto-Dekodierung — austauschbar über #imagePipeline.
      const asset = await this.#imagePipeline.buildImageAsset(file);
      await this.#storage.putImageBlobs(asset.meta.assetId, { original: asset.originalBlob, preview: asset.previewBlob });
      return { asset };
    } catch (error) {
      console.error('[dp-designer] Foto konnte nicht verarbeitet werden', error);
      const quota = error?.name === 'QuotaExceededError' || /quota|storage/i.test(String(error?.message ?? ''));
      if (quota) return { error: 'Der Speicher deines Browsers ist voll. Lösche nicht benötigte Fotos aus der Liste oder verkleinere die Fotos vor dem Hochladen.' };
      // Website-Daten blockiert (IndexedDB gesperrt): der Grund liegt im Browser, nicht am Foto. Vorher stand
      // hier „Dieses Foto konnte nicht gelesen werden“ – eine Sackgasse ohne Ausweg (Prüfung Voll-Modus 2026-09-22).
      const blocked = error?.name === 'InvalidStateError' || error?.name === 'SecurityError' || error?.name === 'UnknownError'
        || /indexeddb|database|datenbank/i.test(String(error?.message ?? ''));
      if (blocked) {
        // Domainname aus dem Browser statt fest verdrahtet – die Meldung stimmt auch nach einem Domainwechsel.
        let host = 'diese Seite';
        try { host = location.hostname || host; } catch { /* kein location (Tests) */ }
        return { error: 'Dein Browser lässt diese Seite keine Daten speichern – ohne das können wir die Fotos nicht halten. '
          + `Erlaube Website-Daten (Cookies/Website-Daten) für ${host} oder nutze ein normales Fenster statt des privaten Modus.` };
      }
      const inspection = quickOk ? await api.inspectFile(file).catch(() => null) : null;
      return { error: inspection && !inspection.ok ? inspection.message : 'Dieses Foto konnte nicht gelesen werden. Bitte versuche es erneut oder wähle ein anderes Foto.' };
    }
  }

  /** Fortschritt „7 von 13 verarbeitet“ im Quiz live nachführen (Ring, Zahl, Text) – ohne Voll-Render. */
  #patchUploadProgress() {
    const p = this.#uploadProgress;
    if (!p || !p.total) return;
    const ring = this.querySelector('[data-upload-ring]');
    if (ring) {
      const c = Number(ring.getAttribute('data-circumference')) || 150.8;
      ring.setAttribute('stroke-dashoffset', (c * (1 - Math.min(1, p.done / p.total))).toFixed(1));
    }
    const num = this.querySelector('[data-upload-num]');
    if (num) num.textContent = String(p.done);
    const text = this.querySelector('[data-upload-text]');
    if (text) text.textContent = `${p.done} von ${p.total} verarbeitet …`;
  }

  /** Quiz bei „Jetzt deine Fotos“ und mindestens so viele Fotos wie Seiten → das Quiz darf von selbst enden. */
  #quizPhotosComplete() {
    if (this.#mode !== 'einfach' || this.#quiz.done || this.#quiz.screen !== 'fotos' || !this.#doc) return false;
    const total = Number(this.#shell?.computeProgress?.(this.#doc)?.totalPages) || 0;
    return total > 0 && Object.keys(this.#doc.assets.images).length >= total;
  }

  /** Vorbereitetes Foto ins Dokument übernehmen (Vorschau-URL, addPhoto, ggf. direkt in den wartenden Platz). */
  #commitAsset(asset) {
    this.#previewUrls.set(asset.meta.assetId, URL.createObjectURL(asset.previewBlob));
    this.#doc = this.#model.addPhoto(this.#doc, asset.meta);
    if (this.#assigningElementId) {
      this.#assignAsset(this.#assigningElementId, asset.meta.assetId);
      this.#selectedElementId = this.#assigningElementId;
      this.#assigningElementId = null;
      this.#sheetOpen = false;
    }
    // Erste Fotos: der Kunde ist im Fluss — Fotoliste zeigen, Hinweiskarte gilt als gesehen.
    this.#activeTab = 'photos';
    if (!this.#hintDismissed) this.dismissHintQuietly();
  }

  /* ------------------------- Entwurf als Datei sichern / öffnen ------------------------- *
   * Der Entwurf liegt nur in diesem Browser. Als Datei (.dpkalender = ZIP mit design.json und
   * Originalfotos, dp-design-file.js) nimmt ihn der Kunde auf ein anderes Gerät mit oder legt ihn
   * beiseite. Öffnen setzt Fotos und Dokument unter derselben Design-ID wieder ein.
   * ------------------------------------------------------------------------------------ */

  async #designFile() {
    if (!this.#designFileApi) {
      const src = this.dataset.moduleDesignFile;
      if (!src) throw new Error('data-module-design-file fehlt.');
      this.#designFileApi = await import(src);
    }
    return this.#designFileApi;
  }

  async saveDesignFile() {
    if (!this.#doc || this.#designFileBusy) return;
    this.#designFileBusy = 'saving';
    this.#designFileNotice = null;
    this.#render();
    try {
      const api = await this.#designFile();
      const doc = this.#doc;
      const images = [];
      for (const [assetId, meta] of Object.entries(doc.assets.images)) {
        // eslint-disable-next-line no-await-in-loop -- nacheinander, IndexedDB-Last
        const record = await this.#storage.getImageBlobs(assetId).catch(() => null);
        const blob = record?.original ?? null;
        if (!(blob instanceof Blob)) continue;
        // eslint-disable-next-line no-await-in-loop
        images.push({ assetId, bytes: new Uint8Array(await blob.arrayBuffer()), mimeType: blob.type || meta.mimeType, fileName: meta.sourceFileName });
      }
      const bytes = api.packDesignFile({ doc, images });
      const fileName = api.designFileName(doc);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.rel = 'noopener'; a.hidden = true;
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      const skipped = Object.keys(doc.assets.images).length - images.length;
      const text = `Entwurf gespeichert als „${fileName}“ (${formatMegabytes(bytes.length)}). Zum Weitermachen öffnest du die Datei hier über „Entwurf öffnen“.${skipped ? ` ${skipped} ${skipped === 1 ? 'Foto fehlte' : 'Fotos fehlten'} im Browserspeicher und ${skipped === 1 ? 'ist' : 'sind'} nicht enthalten.` : ''}`;
      this.#designFileNotice = { tone: 'ok', text };
      this.#announce(text);
    } catch (error) {
      console.error('[dp-designer] Entwurf konnte nicht als Datei gespeichert werden.', error);
      this.#designFileNotice = { tone: 'error', text: 'Der Entwurf konnte nicht als Datei gespeichert werden. Bitte versuche es noch einmal.' };
    } finally {
      this.#designFileBusy = null;
      this.#render();
    }
  }

  openDesignFilePicker() {
    const input = this.refs.designFileInput ?? this.querySelector('input[data-dp-design-file]');
    input?.click();
  }

  /** @param {Event} event */
  async handleDesignFileInputChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (file) await this.#importDesignFile(file);
  }

  /** @param {File} file */
  async #importDesignFile(file) {
    if (this.#designFileBusy) return;
    this.#designFileBusy = 'opening';
    this.#designFileNotice = null;
    this.#render();
    try {
      const api = await this.#designFile();
      const { doc, images } = api.unpackDesignFile(new Uint8Array(await file.arrayBuffer()));
      if (!this.#isKnownFormat(doc.product?.formatKey)) throw new Error('Das Format dieses Entwurfs gibt es in diesem Designer nicht.');
      // Fotos zuerst in den Speicher (unter ihrer alten ID), dann das Dokument übernehmen –
      // so stehen die Vorschauen sofort beim ersten Render.
      const restored = new Set();
      for (const img of images) {
        try {
          const source = new File([img.bytes], img.fileName || `${img.assetId}.jpg`, { type: img.mimeType });
          // eslint-disable-next-line no-await-in-loop -- nacheinander (Canvas/IndexedDB-Last)
          const asset = await this.#imagePipeline.buildImageAsset(source);
          // eslint-disable-next-line no-await-in-loop
          await this.#storage.putImageBlobs(img.assetId, { original: asset.originalBlob, preview: asset.previewBlob });
          const old = this.#previewUrls.get(img.assetId);
          if (old) URL.revokeObjectURL(old);
          this.#previewUrls.set(img.assetId, URL.createObjectURL(asset.previewBlob));
          restored.add(img.assetId);
        } catch (error) {
          console.warn('[dp-designer] Foto aus der Entwurfsdatei nicht lesbar.', { assetId: img.assetId, error });
        }
      }
      if (!this.#adoptDocument(doc)) throw new Error('Der Entwurf konnte nicht übernommen werden.');
      this.#selectedElementId = null;
      this.#assigningElementId = null;
      this.#pendingAssetId = null;
      this.#sheetOpen = false;
      this.#review = null;
      this.#pendingFormatSwitch = null;
      this.#uploadErrors = [];
      for (const id of Object.keys(this.#doc.assets.images)) {
        if (!restored.has(id)) this.#uploadErrors.push({ fileName: this.#doc.assets.images[id]?.sourceFileName ?? id, message: 'Dieses Foto fehlt in der Entwurfsdatei – bitte lade es noch einmal hoch.' });
      }
      this.#persist();
      const text = `Entwurf geöffnet: ${restored.size} ${restored.size === 1 ? 'Foto' : 'Fotos'}, Format ${shortFormatLabel(this.#activeFormat)}. Du kannst direkt weitermachen.`;
      this.#designFileNotice = { tone: 'ok', text };
      this.#announce(text);
    } catch (error) {
      console.warn('[dp-designer] Entwurfsdatei konnte nicht geöffnet werden.', error);
      const known = this.#designFileApi?.MSG ? Object.values(this.#designFileApi.MSG).includes(error?.message) : false;
      const text = known || /Format dieses Entwurfs/.test(String(error?.message)) ? error.message : 'Die Entwurfsdatei konnte nicht geöffnet werden. Bitte versuche es noch einmal.';
      this.#designFileNotice = { tone: 'error', text };
      this.#announce(text);
    } finally {
      this.#designFileBusy = null;
      this.#render();
    }
  }

  /** Hinweiskarte als gesehen markieren, ohne zu rendern (Aufrufer rendert). */
  dismissHintQuietly() {
    this.#hintDismissed = true;
    try { localStorage.setItem(HINT_SEEN_KEY, '1'); } catch { /* Speicher gesperrt */ }
  }

  /** @param {{assetId:string}} data */
  deleteTrayPhoto({ assetId }) {
    if (this.#pendingAssetId === assetId) this.#pendingAssetId = null;
    this.#doc = this.#model.removePhoto(this.#doc, assetId);
    const url = this.#previewUrls.get(assetId);
    if (url) { URL.revokeObjectURL(url); this.#previewUrls.delete(assetId); }
    this.#storage.deleteImageBlobs(assetId).catch(() => {});
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  /** Foto angetippt: in die wartende Stelle, sonst in den ersten freien Platz der Seite, sonst vormerken. @param {{assetId:string}} data */
  handleTrayPhotoClick({ assetId }) {
    if (!this.#doc?.assets.images[assetId]) return;
    if (this.#assigningElementId) { this.pickPhotoForSlot({ assetId }); return; }
    if (this.#pendingAssetId === assetId) { this.#pendingAssetId = null; this.#render(); return; }
    const page = this.#activePage();
    const free = page?.elements.find((el) => el.type === 'image' && !el.assetId);
    if (free) {
      this.#assignAsset(free.id, assetId);
      this.#pendingAssetId = null;
      this.#selectedElementId = free.id;
      this.#sheetOpen = false;
      this.#recomputeValidation();
      this.#persist();
    } else {
      this.#pendingAssetId = assetId;
      this.#selectedElementId = null;
      this.#sheetOpen = false;
    }
    this.#render();
  }

  /**
   * Escape im Editor: schließt der Reihe nach, was oben liegt – Rückfrage „Von vorne anfangen“,
   * Prüf-Sheet, Foto-Auswahl, Werkzeug-Sheet (mobil), Modus-Popover, Fotoauswahl auf der Bühne.
   * Der Prüf-Sheet-Handler bleibt zusätzlich bestehen (Fokusfalle), doppeltes Schließen schadet nicht.
   * @param {KeyboardEvent} event
   */
  #onEscapeKey = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    if (this.#startOverPending) { this.cancelStartOver(); event.preventDefault(); return; }
    if (this.#review) { this.closeReviewSheet(); event.preventDefault(); return; }
    if (this.#assigningElementId) { this.cancelAssign(); event.preventDefault(); return; }
    if (this.#sheetOpen) { this.closeSheet(); event.preventDefault(); return; }
    if (this.#modeHelpOpen) { this.dismissModeHelp(); event.preventDefault(); return; }
    if (this.#selectedElementId) { this.deselectSlot(); event.preventDefault(); }
  };

  /**
   * Derselbe Entwurf wurde in einem anderen Tab gespeichert: diese Ansicht zieht nach, statt beim
   * nächsten Klick den fremden Stand zu überschreiben. Fotos liegen ohnehin gemeinsam in IndexedDB.
   * @param {StorageEvent} event
   */
  #onForeignSave = (event) => {
    const id = this.#doc?.designId;
    if (!id || event?.key !== `dp-designer:design:${id}` || !event.newValue) return;
    let incoming = null;
    try { incoming = JSON.parse(event.newValue); } catch { return; }
    if (!incoming || incoming.designId !== id) return;
    // Nur einen NEUEREN Stand übernehmen (updatedAt ist ein ISO-Zeitstempel des Modells).
    if (String(incoming.updatedAt ?? '') <= String(this.#doc.updatedAt ?? '')) return;
    this.#doc = incoming;
    this.#selectedElementId = null;
    this.#assigningElementId = null;
    this.#recomputeValidation();
    this.#render();
    this.#announce('Dein Kalender wurde in einem anderen Tab geändert – diese Ansicht zeigt jetzt den neuen Stand.');
  };

  #onDragStart = (event) => {
    const item = /** @type {HTMLElement} */ (event.target)?.closest?.('.dp-tray__item');
    if (!item || !event.dataTransfer) return;
    event.dataTransfer.setData('text/plain', item.dataset.assetId ?? '');
    event.dataTransfer.effectAllowed = 'copy';
  };

  #onDragOver = (event) => {
    // Dateien vom Rechner: überall im Editor annehmen (sonst öffnet der Browser die Datei und
    // verlässt den Designer – Technik-Prüfung 2026-09-16, P1-3).
    if (hasFileDrag(event)) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      this.classList.add('is-file-drag');
      return;
    }
    if (!/** @type {HTMLElement} */ (event.target)?.closest?.('.dp-slot--image, .dp-mini')) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  };

  #onDragLeave = (event) => {
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    this.classList.remove('is-file-drag');
  };

  /** Ablegen auf einem Fotoplatz der Bühne oder auf einer Miniatur im Seitenstreifen. */
  #onDrop = (event) => {
    this.classList.remove('is-file-drag');
    if (hasFileDrag(event)) {
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files ?? []);
      const slot = /** @type {HTMLElement} */ (event.target)?.closest?.('.dp-slot--image');
      const elementId = slot?.dataset.elementId ?? null;
      this.#ingestDroppedFiles(files, elementId);
      return;
    }
    const target = /** @type {HTMLElement} */ (event.target)?.closest?.('.dp-slot--image, .dp-mini');
    if (!target) return;
    event.preventDefault();
    const assetId = event.dataTransfer?.getData('text/plain');
    if (!assetId || !this.#doc?.assets.images[assetId]) return;
    let elementId = target.dataset.elementId;
    if (!elementId && target.dataset.pageId) {
      const page = this.#doc.pages.find((p) => p.pageId === target.dataset.pageId);
      const images = page?.elements.filter((el) => el.type === 'image') ?? [];
      elementId = (images.find((el) => !el.assetId) ?? images[0])?.id;
      if (page) this.#activePageId = page.pageId;
    }
    if (!elementId) return;
    this.#assignAsset(elementId, assetId);
    this.#pendingAssetId = null;
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  };

  /* ----------------------------------- Seiten ----------------------------------- */

  /** @param {{pageId:string}} data */
  selectPage({ pageId }) {
    if (!this.#doc?.pages.some((p) => p.pageId === pageId)) return;
    this.#activePageId = pageId;
    this.#selectedElementId = null;
    this.#assigningElementId = null;
    this.#sheetOpen = false;
    this.#recomputeValidation();
    this.#render();
  }

  /** @param {{direction:'prev'|'next'}} data */
  goToAdjacentPage({ direction }) {
    if (!this.#doc) return;
    const idx = this.#doc.pages.findIndex((p) => p.pageId === this.#activePageId);
    const target = this.#doc.pages[direction === 'prev' ? idx - 1 : idx + 1];
    if (target) this.selectPage({ pageId: target.pageId });
  }

  /**
   * „Alle leeren Seiten füllen“: freie Fotos in Upload-Reihenfolge auf leere Plätze (Rückseite zuletzt), kein
   * Bin-Matching; Collage-Seiten mit weniger Fotos als Plätzen werden auf die passende Variante verkleinert –
   * auch ohne freie Fotos (QA 2026-09-21 F-02: „Automatisch verteilen“ änderte sonst nichts). Regel in
   * dp-designer-panels.js fillAndShrinkPages, dieselbe wie beim Designwechsel.
   */
  async runMagicLayout() {
    if (!this.#doc) return;
    const { doc, assigned, shrunk } = this.#fillAndShrink(this.#doc);
    if (!assigned && !shrunk) return;
    this.#doc = { ...doc, meta: { ...doc.meta, magicLayoutUsed: true } };
    this.#pendingAssetId = null;
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  /** @returns {{ doc:object, assigned:number, shrunk:number }} siehe dp-designer-panels.js fillAndShrinkPages */
  #fillAndShrink(doc) {
    if (typeof this.#panels?.fillAndShrinkPages !== 'function') return { doc, assigned: 0, shrunk: 0 };
    return this.#panels.fillAndShrinkPages(doc, {
      format: this.#activeFormat,
      themeLayoutFor: (page) => this.#themeLayoutFor(page),
      assignPhotoToSlot: (d, pageId, slotId, assetId, options) => this.#model.assignPhotoToSlot(d, pageId, slotId, assetId, options),
    });
  }

  /* --------------------------------- Slot-Zuweisung --------------------------------- */

  /** Seite, auf der ein Element liegt (Element-IDs sind über alle Seiten eindeutig). */
  #pageOfElement(elementId) {
    return this.#doc?.pages.find((p) => p.elements.some((el) => el.id === elementId)) ?? null;
  }

  #assignAsset(elementId, assetId) {
    const element = this.#findElement(elementId);
    const page = this.#pageOfElement(elementId);
    if (!element || !page) return;
    this.#doc = this.#model.assignPhotoToSlot(this.#doc, page.pageId, element.slotId, assetId, { frame: element.frame });
  }

  /**
   * @param {{elementId:string}} data
   * @param {Event} [event]
   */
  handleSlotClick({ elementId }, event) {
    const element = this.#findElement(elementId);
    if (!element || element.type !== 'image') return;

    // Klick auf den Foto-Layer eines bereits gewählten Platzes = Ende einer Zieh-Geste, keine Abwahl.
    const targetIsPhotoLayer = event?.target instanceof HTMLElement && Boolean(event.target.closest('.dp-slot__photo'));
    if (targetIsPhotoLayer && this.#selectedElementId === elementId) return;

    // Foto zuerst gewählt (Fotoliste), jetzt die Stelle: sofort einsetzen.
    if (this.#pendingAssetId && this.#doc.assets.images[this.#pendingAssetId]) {
      this.#assignAsset(elementId, this.#pendingAssetId);
      this.#pendingAssetId = null;
      this.#assigningElementId = null;
      this.#selectedElementId = elementId;
      this.#recomputeValidation();
      this.#persist();
      this.#render();
      return;
    }

    if (!element.assetId) {
      // Leere Stelle: Fotoliste öffnen, dort wählt der Kunde das Foto.
      this.#assigningElementId = elementId;
      this.#selectedElementId = null;
      this.#activeTab = 'photos';
      this.#sheetOpen = true;
    } else if (this.#selectedElementId === elementId) {
      this.#selectedElementId = null;
    } else {
      this.#selectedElementId = elementId;
      this.#assigningElementId = null;
      this.#sheetOpen = false;
    }
    this.#render();
  }

  /** Enter/Leertaste wie Antippen; Pfeiltasten verschieben ein ausgewähltes Foto (KI-29). @param {{elementId:string}} data @param {KeyboardEvent} event */
  handleSlotKeydown({ elementId }, event) {
    const arrows = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
    if (arrows[event.key] && this.#selectedElementId === elementId) {
      event.preventDefault();
      this.nudgePhoto({ dir: arrows[event.key] });
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.handleSlotClick({ elementId });
  }

  /** @param {{assetId:string}} data */
  pickPhotoForSlot({ assetId }) {
    if (!this.#assigningElementId) return;
    const elementId = this.#assigningElementId;
    this.#assignAsset(elementId, assetId);
    this.#assigningElementId = null;
    this.#pendingAssetId = null;
    this.#selectedElementId = elementId;
    this.#sheetOpen = false;
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  cancelAssign() {
    this.#assigningElementId = null;
    this.#pendingAssetId = null;
    this.#render();
  }

  /** @param {{elementId:string}} data */
  replaceSlotPhoto({ elementId }) {
    this.#assigningElementId = elementId;
    this.#selectedElementId = null;
    this.#activeTab = 'photos';
    this.#sheetOpen = true;
    this.#render();
  }

  /** @param {{elementId:string}} data */
  removeSlotPhoto({ elementId }) {
    this.#assignAsset(elementId, null);
    this.#selectedElementId = null;
    this.#recomputeValidation();
    this.#persist();
    this.#render();
  }

  /* ------------------------- Look (Bildverbesserung / Filter) ------------------------- */

  /**
   * Gruppe „Look“ im Kontextpanel (dp-designer-shell.js renderLookGroup): `key` 'enhance' mit
   * value true|false (component.js parseValue liefert Booleans) oder 'filter' mit value
   * none|bw|sepia|warm|cool. Wirkt auf das gewählte Foto.
   * @param {{key:'enhance'|'filter', value:string|boolean}} data
   */
  setSlotLook({ key, value }) {
    const el = this.#findElement(this.#selectedElementId);
    const pageId = el ? this.#doc.pages.find((p) => p.elements.includes(el))?.pageId : null;
    if (!el || !pageId) return;
    const patch = key === 'enhance' ? { enhance: value === true || value === 'true' } : key === 'filter' ? { filter: String(value) } : null;
    if (!patch) return;
    this.#doc = this.#model.setPhotoLook(this.#doc, pageId, el.id, patch);
    this.#persist();
    this.#render();
    const look = this.#findElement(el.id)?.look ?? {};
    this.#announce(key === 'enhance'
      ? `Automatisch verbessern ${look.enhance ? 'an' : 'aus'}.`
      : `Look: ${LOOK_NAMES[look.filter] ?? 'Original'}.`);
  }

  /** „Auf alle Fotos anwenden“: Look des gewählten Fotos auf alle Fotoplätze des Designs übertragen. */
  applyLookToAllPhotos() {
    const el = this.#findElement(this.#selectedElementId);
    if (!el) return;
    this.#doc = this.#model.applyLookToAll(this.#doc, el.look ?? {});
    this.#persist();
    this.#render();
    const look = el.look ?? {};
    this.#announce(`Look „${LOOK_NAMES[look.filter] ?? 'Original'}“${look.enhance ? ' mit automatischer Verbesserung' : ''} auf alle Fotos angewendet.`);
  }

  /** Foto im Rahmen um NUDGE_STEP_PX verschieben (Buttons im Kontextpanel, Pfeiltasten). @param {{dir:'left'|'right'|'up'|'down'}} data */
  nudgePhoto({ dir }) {
    const el = this.#findElement(this.#selectedElementId);
    const asset = el?.assetId ? this.#doc.assets.images[el.assetId] : null;
    const photoEl = el ? this.querySelector(`.dp-slot__photo[data-element-id="${el.id}"]`) : null;
    if (!el || !asset || !photoEl) return;
    const rect = photoEl.parentElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const img = orientedAssetPx(asset, el);
    const pan = normalizedOffsetToPanPx(el.crop.offsetX, el.crop.offsetY, el.crop.zoom, rect.width, rect.height, img.widthPx, img.heightPx);
    const delta = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] }[dir] ?? [0, 0];
    const next = clampPanPx({ x: pan.x + delta[0] * NUDGE_STEP_PX, y: pan.y + delta[1] * NUDGE_STEP_PX }, el.crop.zoom, rect.width, rect.height);
    const { offsetX, offsetY } = panPxToNormalizedOffset(next, el.crop.zoom, rect.width, rect.height, img.widthPx, img.heightPx);
    this.#commitCrop(el.id, { ...el.crop, offsetX, offsetY }, { skipFullRender: true });
  }

  /** Foto um 90° drehen (Kontextpanel „Links drehen“/„Rechts drehen“). Ausschnitt wird zentriert, Zoom bleibt. @param {{dir:'left'|'right'}} data */
  rotateSlotPhoto({ dir }) {
    const el = this.#findElement(this.#selectedElementId);
    const page = el ? this.#pageOfElement(el.id) : null;
    if (!el || !page || !el.assetId || typeof this.#model.rotatePhoto !== 'function') return;
    this.#doc = this.#model.rotatePhoto(this.#doc, page.pageId, el.id, dir === 'left' ? -90 : 90);
    this.#afterOrientationChange(`Foto nach ${dir === 'left' ? 'links' : 'rechts'} gedreht.`);
  }

  /** Foto horizontal spiegeln (erneut = zurück). @returns {void} */
  flipSlotPhoto() {
    const el = this.#findElement(this.#selectedElementId);
    const page = el ? this.#pageOfElement(el.id) : null;
    if (!el || !page || !el.assetId || typeof this.#model.flipPhoto !== 'function') return;
    this.#doc = this.#model.flipPhoto(this.#doc, page.pageId, el.id);
    const flipped = this.#findElement(el.id)?.orientation?.flipH === true;
    this.#afterOrientationChange(flipped ? 'Foto gespiegelt.' : 'Spiegelung aufgehoben.');
  }

  #afterOrientationChange(message) {
    this.#persist();
    this.#recomputeValidation();
    this.#render();
    this.#announce(message);
  }

  deselectSlot() {
    this.#selectedElementId = null;
    this.#render();
  }

  /* ------------------------------ Zoom-Werkzeugleiste ------------------------------ */

  /** @param {Event} event */
  setZoomFromSlider(event) {
    const value = Number(/** @type {HTMLInputElement} */ (event.target).value);
    this.#applyZoom(value);
  }

  /** @param {{direction:'in'|'out'}} data */
  stepZoom({ direction }) {
    const el = this.#findElement(this.#selectedElementId);
    if (!el) return;
    const delta = direction === 'in' ? 0.1 : -0.1;
    this.#applyZoom(clamp(el.crop.zoom + delta, ZOOM_MIN, ZOOM_MAX));
  }

  centerPhoto() {
    const el = this.#findElement(this.#selectedElementId);
    if (!el) return;
    this.#commitCrop(el.id, { ...el.crop, offsetX: 0, offsetY: 0 });
  }

  #applyZoom(newZoom) {
    const el = this.#findElement(this.#selectedElementId);
    if (!el) return;
    this.#commitCrop(el.id, { ...el.crop, zoom: newZoom }, { skipFullRender: true });
  }

  #commitCrop(elementId, crop, { skipFullRender = false } = {}) {
    const element = this.#findElement(elementId);
    if (!element || !this.#activePageId) return;
    this.#doc = this.#model.setSlotTransform(this.#doc, this.#activePageId, element.slotId, crop);
    this.#recomputeValidation();
    this.#persistDebounced();
    if (skipFullRender) {
      this.#hydrateSlotTransforms();
      this.#patchValidationPanel();
      this.#patchContext({ light: true });
    } else {
      this.#render();
    }
  }

  /* ------------------------------- Pointer-Gesten ------------------------------- */

  /** @param {{elementId:string}} data @param {PointerEvent} event */
  handlePhotoPointerDown({ elementId }, event) {
    if (elementId !== this.#selectedElementId) return;
    // event.target statt event.currentTarget: der deklarative Listener sitzt auf
    // `document`, currentTarget wäre dort immer document, nicht das Foto-Element.
    const photoEl = /** @type {HTMLElement} */ (event.target);
    try { photoEl.setPointerCapture(event.pointerId); } catch { /* bereits vergeben o. ä. */ }

    const isFirstPointer = this.#activePointers.size === 0;
    this.#activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (isFirstPointer) {
      this.#startGesture(elementId, photoEl);
      this.#maybeHandleDoubleTap();
    } else if (this.#activePointers.size === 2) {
      this.#startPinch();
    }
  }

  #startGesture(elementId, photoEl) {
    const el = this.#findElement(elementId);
    const asset = el ? this.#doc.assets.images[el.assetId] : null;
    if (!el || !asset) return;
    const frameRect = photoEl.parentElement.getBoundingClientRect();
    const img = orientedAssetPx(asset, el);
    const panPx = normalizedOffsetToPanPx(el.crop.offsetX, el.crop.offsetY, el.crop.zoom, frameRect.width, frameRect.height, img.widthPx, img.heightPx);
    const [firstPoint] = this.#activePointers.values();
    this.#gesture = {
      elementId, photoEl,
      frameWidthPx: frameRect.width, frameHeightPx: frameRect.height,
      frameCenter: { x: frameRect.left + frameRect.width / 2, y: frameRect.top + frameRect.height / 2 },
      zoom: el.crop.zoom, panPx,
      dragOrigin: firstPoint, dragOriginPanPx: panPx,
      orientationCss: orientationTransform(el),
    };
    this.#bindGestureMoveListeners();
  }

  #startPinch() {
    if (!this.#gesture) return;
    const [p1, p2] = this.#activePointers.values();
    this.#gesture.pinchStartDistance = distanceBetween(p1, p2);
    this.#gesture.pinchStartZoom = this.#gesture.zoom;
  }

  #bindGestureMoveListeners() {
    this.#gestureController?.abort();
    this.#gestureController = new AbortController();
    const { signal } = this.#gestureController;
    window.addEventListener('pointermove', this.#onGesturePointerMove, { signal });
    window.addEventListener('pointerup', this.#onGesturePointerUp, { signal });
    window.addEventListener('pointercancel', this.#onGesturePointerUp, { signal });
  }

  #onGesturePointerMove = (event) => {
    if (!this.#gesture || !this.#activePointers.has(event.pointerId)) return;
    this.#activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...this.#activePointers.values()];
    if (points.length >= 2) {
      this.#processPinch(points[0], points[1]);
    } else {
      this.#processDrag(points[0]);
    }
  };

  #processDrag(point) {
    const g = this.#gesture;
    const raw = { x: g.dragOriginPanPx.x + (point.x - g.dragOrigin.x), y: g.dragOriginPanPx.y + (point.y - g.dragOrigin.y) };
    g.panPx = clampPanPx(raw, g.zoom, g.frameWidthPx, g.frameHeightPx);
    this.#applyLiveTransform();
  }

  #processPinch(p1, p2) {
    const g = this.#gesture;
    const distance = distanceBetween(p1, p2);
    const newZoom = clamp((distance / g.pinchStartDistance) * g.pinchStartZoom, ZOOM_MIN, ZOOM_MAX);
    const midX = (p1.x + p2.x) / 2 - g.frameCenter.x;
    const midY = (p1.y + p2.y) / 2 - g.frameCenter.y;
    const k = newZoom / g.zoom;
    const rawPan = { x: g.panPx.x * k + midX * (1 - k), y: g.panPx.y * k + midY * (1 - k) };
    g.zoom = newZoom;
    g.panPx = clampPanPx(rawPan, newZoom, g.frameWidthPx, g.frameHeightPx);
    this.#applyLiveTransform();
  }

  #applyLiveTransform() {
    const g = this.#gesture;
    if (!g?.photoEl) return;
    g.photoEl.style.transform = photoTransform(g.panPx, g.zoom, g.orientationCss);
    const slider = this.querySelector('.dp-toolbar__slider');
    if (slider instanceof HTMLInputElement) slider.value = String(g.zoom);
    const label = this.querySelector('[data-zoom-label]');
    if (label) label.textContent = `${Math.round(g.zoom * 100)} %`;
  }

  #onGesturePointerUp = (event) => {
    this.#activePointers.delete(event.pointerId);
    if (this.#activePointers.size === 1) {
      const [remaining] = this.#activePointers.values();
      this.#gesture.dragOrigin = remaining;
      this.#gesture.dragOriginPanPx = { ...this.#gesture.panPx };
      return;
    }
    if (this.#activePointers.size === 0 && this.#gesture) {
      this.#finishGesture();
    }
  };

  #finishGesture() {
    const g = this.#gesture;
    this.#gestureController?.abort();
    this.#gestureController = null;
    this.#gesture = null;
    const el = this.#findElement(g.elementId);
    const asset = this.#doc.assets.images[el?.assetId];
    if (!el || !asset) return;
    const img = orientedAssetPx(asset, el);
    const { offsetX, offsetY } = panPxToNormalizedOffset(g.panPx, g.zoom, g.frameWidthPx, g.frameHeightPx, img.widthPx, img.heightPx);
    // Tipp ohne Bewegung (oder ein verwaister Pointer): nichts geändert → kein Voll-Render. Der würde
    // zwischen pointerup und click den ganzen Editor austauschen, sodass der Klick (z. B. auf einen
    // Panel-Knopf) ins Leere geht — der Browser liefert click nur an noch vorhandene Knoten.
    const same = (a, b) => Math.abs(a - b) < 1e-4;
    if (same(el.crop.zoom, g.zoom) && same(el.crop.offsetX, offsetX) && same(el.crop.offsetY, offsetY)) return;
    this.#commitCrop(g.elementId, { zoom: g.zoom, offsetX, offsetY });
  }

  /** Vereinfachtes Doppeltipp-Verhalten (im Vergleich zu drag-zoom-wrapper.js ohne
   *  Ankerpunkt-Erhalt): wechselt zwischen 1× und 1,5×, immer zentriert. */
  #maybeHandleDoubleTap() {
    const now = performance.now();
    const last = this.#lastTapAt;
    this.#lastTapAt = now;
    if (now - last > DOUBLE_TAP_DELAY_MS) return;
    this.#lastTapAt = 0;
    const el = this.#findElement(this.#selectedElementId);
    if (!el) return;
    const targetZoom = el.crop.zoom > ZOOM_MIN + 0.05 ? ZOOM_MIN : DOUBLE_TAP_ZOOM;
    this.#commitCrop(el.id, { zoom: targetZoom, offsetX: 0, offsetY: 0 });
    // #startGesture() hat #gesture schon mit dem alten Zoom/Pan befüllt — abgleichen, sonst
    // überschreibt das Loslassen (#finishGesture) die Doppeltipp-Position sofort wieder.
    if (this.#gesture) {
      this.#gesture.zoom = targetZoom;
      this.#gesture.panPx = { x: 0, y: 0 };
      this.#gesture.dragOriginPanPx = { x: 0, y: 0 };
    }
  }

  /* ------------------------------- Validierung/Fortschritt ------------------------------- */

  #recomputeValidation() {
    this.#issues = this.#doc ? [...this.#model.validateDesign(this.#doc, this.#activeFormat ?? undefined), ...this.#unprintableIssues(this.#doc)] : [];
    if (this.#selectedElementId) {
      const el = this.#findElement(this.#selectedElementId);
      if (!el || el.type !== 'image' || !el.assetId) this.#selectedElementId = null;
    }
  }

  /**
   * Zeichen ohne Glyphe in der Druckschrift (Emoji, Symbole) in Titel/Untertitel/Bildunterschriften und Terminen:
   * die PDF entfernt sie (dp-print-page.js stripUnsupportedChars), die Bühne zeigt sie nicht – als Warnung je Seite,
   * damit Seitenhinweis, Seitenstreifen und Prüf-Sheet sie nennen (QA 2026-09-21 F-03). Gleiche Abdeckung wie
   * das Text-Meter (dp-designer-panels.js unprintableChars).
   * @returns {Array<{level:'warning', code:'TEXT_NICHT_DRUCKBAR', seite:string, nachricht:string, elementId?:string}>}
   */
  #unprintableIssues(doc) {
    if (typeof this.#panels?.unprintableChars !== 'function' || !doc?.pages) return [];
    const issues = [];
    // Termine setzt die PDF im Kalendarium mit Instrument Sans (dp-print-page.js buildPage, ctx.fonts.regular).
    const eventFont = 'Instrument Sans';
    for (const page of doc.pages) {
      const chars = [];
      for (const el of page.elements) {
        if (el.type !== 'text') continue;
        // Versalien-Stile (Editorial, Klassik) setzen die PDF aus dem GROSSGESCHRIEBENEN Text – ß→SS, µ→Μ
        // und andere Sonderzeichen entstehen erst dabei und fehlen dann in der Schrift. Deshalb denselben
        // Text prüfen, den dp-print-page.js setzt (Druckprüfung 2026-09-22 D-3).
        const text = el.transform === 'uppercase' ? String(el.text ?? '').toLocaleUpperCase('de-DE') : el.text;
        for (const ch of this.#panels.unprintableChars(text, el.font?.family)) if (!chars.includes(ch)) chars.push(ch);
      }
      if (page.role === 'month' && Array.isArray(doc.meta?.events)) {
        for (const e of doc.meta.events) {
          if (Number(e?.month) !== Number(page.calendariumMonth)) continue;
          for (const ch of this.#panels.unprintableChars(e.label, eventFont)) if (!chars.includes(ch)) chars.push(ch);
        }
      }
      if (chars.length) issues.push({ level: 'warning', code: 'TEXT_NICHT_DRUCKBAR', seite: modelPageLabel(page), nachricht: this.#panels.unprintableLabel(chars) });
    }
    return issues;
  }

  /** Warnungen als Klartext „Seite: Nachricht“ (ohne Doppelte) – Prüf-Sheet und Handoff (dp-designer-handoff.js begin). */
  #warningTexts() {
    return [...new Set(this.#issues.filter((i) => i.level === 'warning').map((i) => `${i.seite}: ${i.nachricht}`))];
  }

  /** @param {{issueId:string}} data */
  dismissIssue({ issueId }) {
    this.#dismissedIssueIds.add(issueId);
    this.#patchValidationPanel();
  }

  /**
   * Bildqualität je Foto für Fotoliste und Bühne (Inhaber-Wunsch 2026-09-17, Punkt 10): Pixelmaße gegen
   * Rahmen in mm × Zielauflösung des Formats (dp-designer-model.js assessDocumentPhotos, gleiche Schwellen wie
   * validateDesign). Klartext mit Format und – wenn es eines gibt – dem größten kleineren Format, das reicht.
   * @param {object} doc @param {object|null} format @param {string} formatLabel
   * @returns {Record<string, { level: string, hint: string, dpi: number }>}
   */
  #photoQualities(doc, format, formatLabel) {
    if (typeof this.#model?.assessDocumentPhotos !== 'function' || typeof this.#panels?.photoQualityHint !== 'function') return {};
    let formats = [];
    try { formats = this.#formatsApi?.listFormats?.() ?? []; } catch { formats = []; }
    let raw;
    try { raw = this.#model.assessDocumentPhotos(doc, format, formats); } catch { return {}; }
    const out = {};
    for (const [assetId, q] of Object.entries(raw)) {
      if (q.level === 'ok') continue;
      let okLabel = null;
      if (q.okFormatKey) {
        try { okLabel = shortFormatLabel(this.#formatsApi.getFormat(q.okFormatKey)); } catch { okLabel = null; }
      }
      out[assetId] = { level: q.level, dpi: q.dpi, hint: this.#panels.photoQualityHint(q.level, formatLabel || 'dieses Format', okLabel) };
    }
    return out;
  }

  /** „Zum ersten Foto“ in der Zusammenfassung „N Fotos könnten unscharf wirken“: Seite wechseln, Foto auswählen (Ausschnitt-Werkzeuge). */
  goToBlurryPhoto() {
    if (!this.#doc) return;
    const facts = this.#shell.deriveFacts(this.#doc, this.#issues, this.#dismissedIssueIds, modelPageLabel, issueKey);
    const first = facts.blurry?.first;
    if (!first) return;
    this.#activePageId = first.pageId;
    this.#selectedElementId = first.elementId;
    this.#assigningElementId = null;
    this.#pendingAssetId = null;
    this.#sheetOpen = false;
    this.#recomputeValidation();
    this.#render();
    this.querySelector(`[data-focus-key="slot:${CSS.escape(first.elementId)}"]`)?.focus({ preventScroll: false });
    this.#announce(`${modelPageLabel(this.#activePage())}: Dieses Foto könnte im Druck unscharf wirken. Wähle ein größeres Foto oder zoome weniger hinein.`);
  }

  /**
   * „Weiter“: bei Fehlern zur ersten betroffenen Seite; sonst Prüf-Sheet öffnen und
   * dp:design-ready auslösen. Mit Handoff-Modul (data-module-handoff) übernimmt dessen
   * Controller ab hier (begin → Stufe 'saved' mit „Druckdatei erzeugen“, dann exporting/done/
   * error über detail.openReviewSheet()); ohne Modul endet der Weg ehrlich im Sheet 'saved'.
   * Warnungen (z. B. geringe Auflösung) blockieren nicht, nur level 'error'.
   */
  continueToNextStep() {
    if (!this.#doc || !this.#eligibility().eligible) return;
    const blocking = this.#issues.filter((i) => i.level === 'error');
    if (blocking.length > 0) {
      const targetPage = this.#doc.pages.find((p) => modelPageLabel(p) === blocking[0].seite);
      if (targetPage) this.selectPage({ pageId: targetPage.pageId });
      // Sichtbare Rückmeldung statt stummem Klick (Technik-Prüfung 2026-09-16, P2-8).
      const missing = this.#doc.pages.filter((p) => p.elements.some((el) => el.type === 'image') && !p.elements.some((el) => el.type === 'image' && el.assetId)).length;
      this.#nudge = missing > 0
        ? `Noch ${missing} ${missing === 1 ? 'Seite' : 'Seiten'} ohne Foto – fülle sie, dann geht es weiter.`
        : `Bitte zuerst den Hinweis beheben: ${blocking[0].nachricht}`;
      window.clearTimeout(this.#nudgeTimer);
      this.#nudgeTimer = window.setTimeout(() => { this.#nudge = null; this.#patchValidationPanel(); }, 8000);
      this.#patchValidationPanel();
      this.#announce(this.#nudge);
      this.querySelector('[data-focus-key="validation:nudge"]')?.focus({ preventScroll: false });
      return;
    }
    this.#persist();
    const detail = {
      designId: this.#doc.designId,
      getDesignDocument: () => this.getDesignDocument(),
      getOriginalImageBlob: (assetId) => this.getOriginalImageBlob(assetId),
      openReviewSheet: (state) => this.openReviewSheet(state),
      // Flipbook im Nachkauf-Popup (dp-upload-flipbook.js): Abzug + Vorschau-Blobs, fehlertolerant.
      getPreviewSnapshot: () => this.#previewSnapshot(),
      // Papier/Bindung (Schlüssel) für den Handoff-Eintrag (dp-designer-handoff.js → options).
      getOptions: () => this.#coerceOptions(this.#doc?.product?.options),
      // Wunschmenge der Staffel-Karte (1/2/3, live beim „Zur Bestellung“ gelesen) → Handoff-Eintrag wantedQuantity.
      getWantedQuantity: () => this.#effectiveWantedQuantity(),
      // Nur melden, was das aktive Format auch anbietet: nach einem Formatwechsel (oder einem ?speed=express
      // auf ein Format ohne Express) bliebe sonst eine Versandart stehen, die der Konfigurator nicht kennt.
      getSpeed: () => (this.#speed === 'express' && this.#expressOffer() ? 'express' : 'standard'),
    };
    if (this.#handoff) {
      try {
        this.#reviewHintsOpen = null;
        this.#handoff.begin({
          detail, format: this.#activeFormat, dpProductId: this.#effectiveProductId(),
          calendar: this.#effectiveCalendar(), theme: this.#printThemeOptions(), year: this.#startYear, referrer: document.referrer, origin: location.origin,
          // Warnungen aus der Entwurfsprüfung inkl. nicht druckbarer Zeichen (#recomputeValidation) – gleiche Liste wie im Sheet.
          warnings: this.#warningTexts(),
        });
      } catch (error) {
        console.error('[dp-designer] Handoff-Modul ist fehlgeschlagen.', error);
        this.openReviewSheet({ stage: 'saved', message: '', progress: 0 });
      }
    } else {
      this.openReviewSheet({ stage: 'saved', message: '', progress: 0 });
    }
    this.dispatchEvent(new DesignReadyForHandoffEvent(detail));
  }

  /**
   * Prüf-Sheet öffnen bzw. aktualisieren (Erweiterungsstelle für das Handoff-Modul). Bei gleicher
   * Stufe und focus:false (Fortschritts-Ticks) wird nur der Fortschritt im DOM nachgeführt, sonst
   * das Sheet neu gerendert (ohne den ganzen Editor neu aufzubauen, wenn es schon steht).
   * @param {{stage?:string, message?:string, progress?:number, percent?:number}} state @param {{focus?:boolean}} [options]
   */
  openReviewSheet(state = {}, { focus = true } = {}) {
    const previous = this.#review;
    const stage = ['saved', 'exporting', 'done', 'error'].includes(state.stage) ? state.stage : (previous?.stage ?? 'saved');
    this.#review = { ...(previous ?? {}), ...state, stage };
    const sheet = this.querySelector('.dp-review');
    if (sheet && previous?.stage === stage && !focus && this.#patchReviewProgress(sheet)) return;
    if (sheet) sheet.outerHTML = this.#shell.renderReviewSheet(this.#buildView());
    else this.#render();
    if (focus) this.querySelector('[data-focus-key="review:primary"]')?.focus();
    if (previous?.stage !== stage || focus) {
      const title = this.querySelector('.dp-review__title')?.textContent?.trim() ?? '';
      const message = this.#review.message ?? '';
      this.#announce([title, message].filter(Boolean).join('. '));
    }
  }

  /** Fortschrittsbalken, Prozent und Meldung an Ort und Stelle aktualisieren (kein Neuaufbau je Tick). */
  #patchReviewProgress(sheet) {
    const bar = sheet.querySelector('[data-review-progress]');
    if (!bar) return false;
    const pct = Math.round(Number(this.#review.percent ?? (Number(this.#review.progress) || 0) * 100) || 0);
    bar.setAttribute('aria-valuenow', String(pct));
    const fill = bar.querySelector('i');
    if (fill) fill.style.width = `${pct}%`;
    const pctEl = sheet.querySelector('[data-review-percent]');
    if (pctEl) pctEl.textContent = `${pct} %`;
    const msgEl = sheet.querySelector('[data-review-message]');
    const message = this.#review.message ?? '';
    if (msgEl && msgEl.textContent !== message) msgEl.textContent = message;
    return true;
  }

  /* ---- Knöpfe des Prüf-Sheets → Handoff-Controller (siehe shell.renderReviewSheet) ---- */
  startHandoffExport() { this.#handoff?.startExport(); }
  cancelHandoffExport() { this.#handoff?.cancel(); }
  retryHandoff() { this.#handoff?.retry(); }
  downloadHandoffPdf() { this.#handoff?.download(); }
  proceedToOrder() { this.#handoff?.proceedToOrder(); }

  /**
   * `<details>` der Hinweisliste im Prüf-Sheet auf-/zugeklappt (on:toggle): Zustand merken und „Trotzdem weiter“
   * nur bei offener Liste zeigen – leere Fotoplätze (weiße Kästen) sollen gesehen werden (QA 2026-09-21 F-02).
   */
  toggleReviewHints(event) {
    const details = event?.target;
    if (!(details instanceof HTMLElement) || !('open' in details)) return;
    const open = Boolean(details.open);
    this.#reviewHintsOpen = open;
    const button = this.querySelector('[data-review-continue]');
    if (button instanceof HTMLElement) button.hidden = !open;
    // Erklärzeile und Knopf-Optik müssen mitziehen: zugeklappt erklärt der Text, wie „Trotzdem weiter“
    // erscheint, aufgeklappt beschreibt er den Knopf, der jetzt da steht (Inhaber-Befund 2026-09-23).
    details.classList.toggle('dp-review__more--gate', !open);
    const hint = this.querySelector('.dp-review__hint');
    if (hint instanceof HTMLElement && this.#shell?.REVIEW_HINT_TEXTS) {
      const texts = this.#shell.REVIEW_HINT_TEXTS;
      hint.textContent = open ? texts.confirm : texts.confirmGated;
    }
  }

  closeReviewSheet() {
    if (!this.#review || this.#review.stage === 'exporting') return;
    this.#handoff?.reset();
    this.#review = null;
    this.#reviewHintsOpen = null;
    this.#render();
    // Fokus zurück auf „Weiter“ – im Einfach-Modus mobil ist das der Knopf der Fußzeile (Kopfzeilen-Knopf ausgeblendet).
    const ctas = [...this.querySelectorAll('.dp-designer__cta')].filter((el) => el.getClientRects().length > 0);
    (ctas[0] ?? this.querySelector('.dp-designer__cta'))?.focus();
  }

  /** @param {KeyboardEvent} event */
  handleReviewKeydown(event) {
    if (event.key === 'Escape') { this.closeReviewSheet(); return; }
    if (event.key !== 'Tab') return;
    // Fokusfalle: das Sheet ist modal (aria-modal), Tab bleibt im Dialog (Technik-Prüfung 2026-09-16, P2-14).
    const dialog = this.querySelector('.dp-review__dialog');
    if (!dialog) return;
    const focusable = Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((el) => el.getClientRects().length > 0 && el.getAttribute('aria-disabled') !== 'true');
    if (!focusable.length) { event.preventDefault(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !dialog.contains(active))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
  }

  /** Ziel „Zur Bestellung“: Produktseite (Handle aus der Format-Map, sonst Referrer, sonst Rückfall) mit ?fmt= (und ?design=, wenn eine Übergabe folgt). */
  #orderHref({ withDesign = true } = {}) {
    const key = this.#activeFormat?.key ?? '';
    const handle = this.#formatMapProduct()?.productHandle;
    const base = typeof handle === 'string' && /^[a-z0-9-]+$/i.test(handle) ? `/products/${handle}` : this.#backHref().split('?')[0];
    const params = new URLSearchParams();
    if (key) params.set('fmt', key);
    if (withDesign && this.#doc?.designId) params.set('design', this.#doc.designId);
    return `${base}?${params}`;
  }

  /** Eintrag der Format-Map für aktives Format + Produkt (null ohne Karte/Produkt). */
  #formatMapProduct() {
    const key = this.#activeFormat?.key;
    const dpProductId = this.#effectiveProductId();
    return (key && dpProductId ? this.#formatMap?.formats?.[key]?.products?.[dpProductId] : null) ?? null;
  }

  /**
   * Vorschau-Abzug des Dokuments für das Flipbook während der Druckdatei-Prüfung
   * (dp-design-snapshot.js + Vorschau-Blobs aus dem Designer-Speicher). Nie werfend: null bei Fehler.
   */
  /**
   * Darstellung des gewählten Designs für die PDF (dp-designer-handoff.js buildExportOptions → dp-print-page.js):
   * Kalendarium-Variante und -Farben, Seitenhintergrund, Farbflächen je Seite. Genau das, was Bühne und
   * Flipbook zeigen (Befund 2026-09-16: PDF druckte Raster statt Zeile, ohne Titelband/Farbfläche, ohne Versalien).
   */
  #printThemeOptions() {
    if (!this.#doc) return null;
    const theme = this.#activeTheme();
    const themeOpts = this.#themeKalendariumOptions();
    let pageBackground = null;
    try {
      const styles = theme && typeof this.#themesApi?.themeStyles === 'function' ? this.#themesApi.themeStyles(theme) : null;
      if (styles && typeof styles['--dp-theme-bg'] === 'string') pageBackground = styles['--dp-theme-bg'];
    } catch { pageBackground = null; }
    const pages = {};
    for (const p of this.#doc.pages) {
      try {
        const decor = this.#panels.pageDecor(p, this.#doc.product, this.#activeFormat, this.#themeLayoutFor(p, theme));
        pages[p.pageId] = {
          background: typeof decor?.background === 'string' ? decor.background : null,
          decorations: (decor?.decorations ?? []).filter((d) => d?.frame).map((d) => ({ frame: { ...d.frame }, color: d.color ?? null })),
        };
      } catch { /* Seite ohne Dekor */ }
    }
    return {
      variant: this.#preferredVariant(themeOpts?.darstellung ?? this.#calendarSettings.variant),
      farben: themeOpts?.farben ? { ...themeOpts.farben } : null,
      pageBackground,
      pages,
    };
  }

  async #previewSnapshot() {
    try {
      if (!this.#doc || typeof this.#panels.buildPreviewSnapshot !== 'function') return null;
      const theme = this.#activeTheme();
      const snapshot = this.#panels.buildPreviewSnapshot({
        doc: this.#doc,
        format: this.#activeFormat,
        year: this.#startYear,
        calendar: this.#effectiveCalendar(),
        themeKal: this.#themeKalendariumOptions(),
        themeStyle: this.#panels.themeStyleFor(this.#themesApi, theme, this.#shell.normalizeStyles),
        pageDecor: (p) => this.#panels.pageDecor(p, this.#doc.product, this.#activeFormat, this.#themeLayoutFor(p, theme)),
        labelFor: modelPageLabel,
        textIsOverlay: (p, el) => this.#panels.textIsOverlay(p, el),
      });
      await Promise.all(snapshot.assetIds.map(async (id) => {
        try {
          const record = await this.#storage.getImageBlobs(id);
          const blob = record?.preview ?? record?.original ?? null;
          if (blob instanceof Blob) snapshot.images[id] = blob;
        } catch { /* Foto fehlt in der Vorschau – Flipbook zeigt leeren Platz */ }
      }));
      return snapshot;
    } catch (error) {
      console.warn('[dp-designer] Vorschau-Abzug fürs Flipbook fehlgeschlagen.', error);
      return null;
    }
  }

  /** Daten fürs Panel „Kalender“ (dp-designer-calendar-panel.js): Kalenderarten, Formatkarten mit Preis/Eignung, Papier/Bindung. */
  #calendarView(photoCount) {
    const catalog = this.#formatsApi.listFormats();
    const dpProductId = this.#effectiveProductId();
    const options = normalizeProductOptions(this.#doc?.product?.options);
    return {
      products: this.#panels.productChoices(this.#formatMap, catalog),
      formats: this.#panels.formatChoices(this.#formatMap, catalog, dpProductId, this.#activeFormat?.key ?? null),
      activeProductId: dpProductId,
      activeFormatKey: this.#activeFormat?.key ?? null,
      photoCount,
      options,
      // Block „Kalendarium“: wirksame Einstellungen des Kunden + Startjahr für den Laufzeit-Hinweis.
      settings: this.#effectiveCalendar(),
      startYear: this.#startYear,
      // Flache Formate (DIN lang, A5 lang, A6 quer) erzwingen die Darstellung „zeile“ – das Panel zeigt
      // dann einen Hinweis unter den Stil-Kacheln, statt eine Raster-Kachel stumm aktiv zu lassen.
      forcedZeile: this.#preferredVariant('raster') === 'zeile',
      // Papiere, die es für Kalenderart + Format ab 1 Stück gibt (dp-format-map.json `papers`, QA 2026-09-21
      // I-07); das Panel markiert daraus die Auswahl. Bindungen: alle drei (Standard schwarz).
      papers: this.#availablePapers(),
      bindings: OPTION_VALUES.binding.map((key) => ({ key, active: key === options.binding })),
      // Map-Angebot des aktiven Formats (priceFrom/tiers/options) für den Aufpreis je Kachel („+0,42 €“,
      // dp-designer-link.js optionTileExtra) und den Preishinweis unter den Kacheln; null ohne Karte.
      product: this.#formatMapProduct(),
      optionOffer: this.#optionOffer(),
      // Lieferzeit: Angebot des Lieferanten für dieses Format (Aufschlag pro Sendung, Lieferzeiten)
      // und die aktuelle Wahl. Ohne Express-Block bleibt die Gruppe im Panel weg.
      express: this.#expressOffer(),
      speed: this.#speed,
      // Menge aus der Staffel-Karte: die Lieferzeit-Preise zeigen den Gesamtpreis dieser Menge.
      quantity: this.#effectiveWantedQuantity(),
    };
  }

  /** Verfügbare Papiere der aktiven Kalenderart im aktiven Format (paperChoicesFor, sonst alle drei). */
  #availablePapers() {
    const fn = this.#panels?.paperChoicesFor;
    if (typeof fn !== 'function') return OPTION_VALUES.paper.map((key) => ({ key }));
    return fn(this.#effectiveProductId(), this.#formatMap, this.#activeFormat?.key ?? null);
  }

  /**
   * Papier auf ein verfügbares zurückführen: bietet das Format das gewählte Papier nicht (z. B.
   * Streifenkalender ohne 170 g glänzend), gilt das erste verfügbare – sonst fände die Brücke keine
   * Variante (Material ist ein hartes Attribut, dp-connector-handoff.js).
   */
  #coerceOptions(options) {
    const o = normalizeProductOptions(options);
    const papers = this.#availablePapers();
    if (!papers.length || papers.some((c) => c.key === o.paper)) return o;
    return { ...o, paper: papers[0].key };
  }

  /**
   * Eignung fürs Gestalten laut Format-Map (designerEligible, printClass); fehlende Felder = geeignet.
   * Druckklasse c sperrt nur Formate außerhalb des Designer-Katalogs: die großen Wandformate (A2, 50 × 70,
   * Panorama …) tragen ein eigenes dpiTarget (dp-designer-formats.js) und werden damit erzeugt – wie
   * assessEligibility() in dp-designer-handoff.js.
   */
  #eligibility() {
    const entry = this.#activeFormat ? this.#formatMap?.formats?.[this.#activeFormat.key] : null;
    const product = this.#formatMapProduct();
    // Erst ab 100 Stück (DIN A2 quer, 70 × 50): den echten Grund nennen statt auf einen Upload zu verweisen,
    // den die Produktseite für dieses Format nicht anbietet (QA 2026-09-21 I-11 / T-15).
    if (this.#activeFormat && !this.#isOrderableFromOne(this.#activeFormat)) {
      const minQty = Number(this.#activeFormat.minQuantity) || 100;
      const productId = productIdForFormat(this.#activeFormat);
      const alternatives = (this.#formats ?? [])
        .filter((f) => f.key !== this.#activeFormat.key && this.#isOrderableFromOne(f) && (!productId || productIdForFormat(f) === productId))
        .map((f) => shortFormatLabel(f));
      const list = alternatives.length > 1 ? `${alternatives.slice(0, -1).join(', ')} und ${alternatives.at(-1)}` : alternatives[0] ?? '';
      return { eligible: false, reason: `Dieses Format drucken wir erst ab ${minQty} Stück – Preis auf Anfrage unter support@dp-digital.de.${list ? ` Ab 1 Stück gibt es ${list}.` : ''}` };
    }
    if (product?.designerEligible === false) {
      return { eligible: false, reason: String(product.designerIneligibleReason || 'Für dieses Format gestaltest du mit einer eigenen Druckdatei – lade sie auf der Produktseite hoch, wir prüfen sie dort.') };
    }
    const inCatalog = Number.isFinite(this.#activeFormat?.dpiTarget) && this.#activeFormat.dpiTarget > 0;
    if (entry?.printClass === 'c' && !inCatalog) return { eligible: false, reason: 'Dieses Format braucht besondere Druckdaten. Lade deine fertige Druckdatei auf der Produktseite hoch, wir prüfen sie dort.' };
    return { eligible: true, reason: '' };
  }

  /* --------------------------------------- Helpers --------------------------------------- */

  #findElement(elementId) {
    if (!elementId || !this.#doc) return null;
    for (const page of this.#doc.pages) {
      const el = page.elements.find((e) => e.id === elementId);
      if (el) return el;
    }
    return null;
  }

  #activePage() {
    return this.#doc?.pages.find((p) => p.pageId === this.#activePageId) ?? null;
  }

  #persist() {
    if (!this.#doc) return;
    const ok = this.#storage.saveDesignDocument(this.#doc);
    // Speicher voll (QuotaExceeded o. ä.): einmal sichtbar melden statt still weiterzuarbeiten.
    if (!ok && !this.#uploadErrors.some((e) => e.fileName === STORAGE_WARNING.fileName)) {
      this.#uploadErrors.push({ ...STORAGE_WARNING });
    }
  }

  /* ----------------------------------------- Render ----------------------------------------- */

  /** Voll-Render per innerHTML; Fokus (data-focus-key) und Scrollpositionen bleiben erhalten. */
  #render() {
    if (!this.#doc) {
      // Einfach-Modus ohne Dokument: das Quiz führt zum Format (Frage 2 legt das Dokument an) – statt der Formatauswahl.
      const quiz = this.#mode === 'einfach' && this.#shell && typeof this.#panels?.renderQuizScreen === 'function';
      this.innerHTML = quiz ? this.#shell.renderShell(this.#buildQuizView()) : this.#renderFormatPickerScreen();
      if (quiz) this.querySelector('[data-focus-key="quiz:question"]')?.focus({ preventScroll: true });
      return;
    }
    const active = document.activeElement;
    const focusKey = active instanceof HTMLElement && this.contains(active) ? active.getAttribute('data-focus-key') : null;
    const panelScroll = this.querySelector('.dp-shell__panel-body')?.scrollTop ?? 0;
    const stripScroll = this.querySelector('.dp-pagestrip__list')?.scrollLeft ?? null;

    this.innerHTML = this.#shell.renderShell(this.#buildView());

    // Bühnenhöhe neu messen: Hinweiskarte/Notizen über der Bühne kommen und gehen mit dem Render, ohne dass
    // sich das Element selbst (ResizeObserver) ändert — die Seite soll trotzdem immer ganz hineinpassen.
    this.#measureStage();
    const panelBody = this.querySelector('.dp-shell__panel-body');
    if (panelBody) panelBody.scrollTop = panelScroll;
    this.#scrollActiveThumbIntoView(stripScroll);
    if (focusKey) {
      const target = this.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`);
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }
    requestAnimationFrame(() => this.#hydrateSlotTransforms());
    if (this.#focusPendingChoice) {
      // Standardfokus auf "Weitermachen" (die nicht-destruktive Option), einmalig nach dem
      // ersten Render der Wahl — danach soll ein erneutes Rendern den Fokus nicht mehr stehlen.
      this.#focusPendingChoice = false;
      this.querySelector('[data-dp-focus="keep-design"]')?.focus();
    }
  }

  /** Aktive Miniatur im Seitenstreifen sichtbar halten (nur horizontal, kein Seiten-Scroll). */
  #scrollActiveThumbIntoView(previousScroll) {
    const list = this.querySelector('.dp-pagestrip__list');
    const item = list?.querySelector('.dp-pagestrip__item.is-active');
    if (!(list instanceof HTMLElement) || !(item instanceof HTMLElement)) return;
    if (previousScroll !== null) list.scrollLeft = previousScroll;
    const left = item.offsetLeft - list.offsetLeft;
    const right = left + item.offsetWidth;
    if (left < list.scrollLeft) list.scrollLeft = Math.max(0, left - 8);
    else if (right > list.scrollLeft + list.clientWidth) list.scrollLeft = right - list.clientWidth + 8;
  }

  /**
   * Verfügbare Höhe der Bühne als CSS-Variable (--dp-stage-h) auf dem Element: die Seite wird
   * damit so groß wie möglich, ohne dass zwischen Topbar und Monatsstreifen gescrollt werden muss
   * (Inhaber-Wunsch 2026-09-16). Die Höhe der Bühnenleiste/Hinweise wird abgezogen.
   */
  #measureStage() {
    const scroll = this.querySelector('.dp-stage__scroll');
    if (!scroll) return;
    // Nur messen, wenn die Bühne eine feste Höhe hat (Section „volle Bildschirmhöhe“ ab 1024 px, siehe CSS
    // .dp-designer-section--full-height). Sonst wächst der Scrollbereich mit der Seite, und Messen → schmalere
    // Seite → kleinerer Scrollbereich → … liefe ohne die frühere 260-px-Untergrenze (D-01) bis auf null zusammen;
    // dann gilt der CSS-Rückfall calc(100svh - 300px).
    const fixedHeight = Boolean(this.closest('.dp-designer-section--full-height')) && (typeof matchMedia !== 'function' || matchMedia('(min-width: 1024px)').matches);
    if (!fixedHeight) {
      if (this.style.getPropertyValue('--dp-stage-h')) this.style.removeProperty('--dp-stage-h');
      return;
    }
    const h = Math.round(scroll.clientHeight);
    if (h > 0 && this.style.getPropertyValue('--dp-stage-h') !== `${h}px`) this.style.setProperty('--dp-stage-h', `${h}px`);
  }

  /**
   * Titel/Bildunterschrift auf der Bühne wie in der PDF einpassen (dp-print-page.js fitSingleLine):
   * eine Zeile; bei Überlänge Schrift bis 60 % verkleinern, erst danach greift die CSS-Ellipsis.
   * Vorher zeigte die Bühne „…“, die PDF den vollen Titel kleiner (QA 2026-09-17, PR-1 Rest).
   */
  #fitStageTexts() {
    const fit = {};
    for (const box of this.querySelectorAll('.dp-text')) {
      const span = box.querySelector('span');
      if (!(span instanceof HTMLElement)) continue;
      const slot = box.getAttribute('data-slot') || '';
      span.style.fontSize = '';
      const available = box.clientWidth;
      const needed = span.scrollWidth;
      if (!available || needed <= available) { if (slot) fit[slot] = { factor: 1, truncated: false }; continue; }
      const ratio = available / needed;
      const factor = Math.max(TEXT_SHRINK_MIN, Math.floor(ratio * 1000) / 1000);
      span.style.fontSize = `${(factor * 100).toFixed(1)}%`;
      // Reicht die Mindestgröße nicht (Zeile bräuchte < 60 %), greift die CSS-Ellipsis – dieselbe Regel wie
      // fitSingleLine (fitted < minSize → „…“); das Panel sagt es vorher (Punkt 11, 2026-09-17). Die PDF darf
      // bis 58 % verkleinern (PDF_TEXT_SHRINK_MIN): „Wird gekürzt“ nur, wenn auch sie kürzt; dazwischen
      // „Sehr klein“ (QA 2026-09-21 F-03: Meter sagte „gekürzt“, die PDF war vollständig).
      if (slot) fit[slot] = { factor, truncated: ratio < PDF_TEXT_SHRINK_MIN, tight: ratio < TEXT_SHRINK_MIN };
    }
    this.#textFit = fit;
    this.#syncTextMeters();
  }

  /**
   * Live-Hinweis unter den Textfeldern (Tab „Text“, dp-designer-panels.js renderTextMeter): Zeichenzähler und
   * „Passt in eine Zeile“ / „Wird verkleinert“ / „Wird gekürzt – bitte kürzer fassen“ aus dem Einpass-Ergebnis
   * der Bühne – per DOM nachgeführt, damit das Eingabefeld den Fokus behält und nichts springt.
   */
  #syncTextMeters() {
    if (typeof this.#panels?.textMeterState !== 'function') return;
    for (const meter of this.querySelectorAll('[data-text-meter]')) {
      const slot = meter.getAttribute('data-text-meter') || '';
      const input = this.querySelector(`#dp-text-${CSS.escape(slot)}`);
      const max = Number(meter.getAttribute('data-text-max')) || Number(input?.getAttribute('maxlength')) || 60;
      const text = input instanceof HTMLInputElement ? input.value : '';
      // Wie im Seitenhinweis: bei Versalien-Stilen zählt der großgeschriebene Text (D-3).
      const printed = this.#slotIsUppercase(slot) ? text.toLocaleUpperCase('de-DE') : text;
      const unprintable = typeof this.#panels.unprintableChars === 'function' ? this.#panels.unprintableChars(printed, this.#textFontFamily(slot)) : [];
      const m = this.#panels.textMeterState(text, max, this.#textFit[slot], unprintable);
      const count = meter.querySelector('[data-text-count]');
      const state = meter.querySelector('[data-text-state]');
      if (count && count.textContent !== m.count) count.textContent = m.count;
      if (state instanceof HTMLElement) {
        if (state.textContent !== m.label) state.textContent = m.label;
        state.className = `dp-field__meter-state is-${m.state}`;
      }
    }
  }

  /** Setzt das Design diesen Text-Slot in Versalien? (Element `transform`, wie dp-print-page.js) */
  #slotIsUppercase(slot) {
    const page = this.#activePage();
    return page?.elements.some((e) => e.type === 'text' && e.slotId === slot && e.transform === 'uppercase') === true;
  }

  /** Druckschrift des Text-Slots der aktiven Seite (Element `font.family`, sonst Schriftwahl des Designs) – für unprintableChars. */
  #textFontFamily(slot) {
    const page = this.#activePage();
    const el = page?.elements.find((e) => e.type === 'text' && e.slotId === slot);
    if (el?.font?.family) return el.font.family;
    const choice = typeof this.#panels.fontChoiceFor === 'function' ? this.#panels.fontChoiceFor(this.#themesApi, this.#doc?.theme?.overrides?.font) : null;
    return choice?.pdfFamily ?? this.#activeTheme()?.pdfFamily ?? 'Instrument Sans';
  }

  #hydrateSlotTransforms() {
    this.#fitStageTexts();
    const page = this.#activePage();
    if (!page) return;
    for (const el of page.elements) {
      if (el.type !== 'image' || !el.assetId) continue;
      const photoEl = this.querySelector(`.dp-slot__photo[data-element-id="${el.id}"]`);
      if (!photoEl) continue;
      const frameRect = photoEl.parentElement.getBoundingClientRect();
      if (frameRect.width === 0 || frameRect.height === 0) continue;
      const asset = this.#doc.assets.images[el.assetId];
      if (!asset) continue;
      const img = orientedAssetPx(asset, el);
      const panPx = normalizedOffsetToPanPx(el.crop.offsetX, el.crop.offsetY, el.crop.zoom, frameRect.width, frameRect.height, img.widthPx, img.heightPx);
      photoEl.style.transform = photoTransform(panPx, el.crop.zoom, orientationTransform(el));
    }
  }

  #patchValidationPanel() {
    const el = this.querySelector('.dp-validation');
    if (el) el.outerHTML = this.#shell.renderValidation(this.#buildView());
  }

  /** Kontextpanel: `light` aktualisiert nur Zoom-Regler/-Wert (während einer Geste kein Austausch). */
  #patchContext({ light = false } = {}) {
    const existing = this.querySelector('.dp-shell__context');
    if (!existing) return;
    if (light) {
      const el = this.#findElement(this.#selectedElementId);
      if (!el) return;
      const slider = existing.querySelector('.dp-toolbar__slider');
      if (slider instanceof HTMLInputElement) slider.value = String(el.crop.zoom);
      const label = existing.querySelector('[data-zoom-label]');
      if (label) label.textContent = `${Math.round(el.crop.zoom * 100)} %`;
      return;
    }
    existing.outerHTML = this.#shell.renderContext(this.#buildView());
  }

  /** Nur den Bogen auf der Bühne austauschen (Texteingabe behält den Fokus im Panel). */
  #patchSheet() {
    const scroll = this.querySelector('.dp-stage__scroll');
    const page = this.#activePage();
    if (!scroll || !page) return;
    scroll.innerHTML = this.#shell.renderSheet(this.#buildView(), page);
    this.#hydrateSlotTransforms();
  }

  /** Alles, was die Hülle zum Rendern braucht — reine Daten und kleine Nachschlagefunktionen. */
  #buildView() {
    const doc = this.#doc;
    const format = this.#activeFormat;
    const page = this.#activePage();
    const facts = this.#shell.deriveFacts(doc, this.#issues, this.#dismissedIssueIds, modelPageLabel, issueKey);
    const images = doc.assets.images;
    const ids = facts.photoIds;
    const theme = this.#activeTheme();
    const assetBase = this.#assetBase();
    const samples = this.#panels.samplesFor(this.#themesApi, theme, assetBase);
    const layoutCache = new Map();
    const pageLayout = (p) => {
      if (!layoutCache.has(p.pageId)) layoutCache.set(p.pageId, this.#themeLayoutFor(p, theme));
      return layoutCache.get(p.pageId);
    };
    const selected = this.#findElement(this.#selectedElementId);
    const productName = this.#formatMapProduct()?.productName ?? null;
    const formatShort = shortFormatLabel(format);
    // Papier/Bindung nur als Abweichung vom Standard (170 g matt, Schwarz) im Chip.
    const optionParts = optionChipParts(doc.product?.options);
    const chip = format ? {
      visible: [productName ? `${formatShort} · ${shortProductName(productName)}` : formatShort, ...optionParts].join(' · '),
      // Auch die Langform (title, Screenreader, Prüf-Sheet) ohne Familienpräfix: "DIN A4 hoch · Fotokalender Lykke"
      // statt "Wandkalender DIN A4 Hochformat · Fotokalender Lykke" (QA 2026-09-16, Befund 13).
      full: [productName ? `${formatShort} · ${productName}` : formatShort, ...optionParts].join(' · '),
    } : null;
    const eligibility = this.#eligibility();
    const orderHref = this.#orderHref();
    let formatSwitch = null;
    if (this.#pendingFormatSwitch) {
      try {
        const requested = this.#formatsApi.getFormat(this.#pendingFormatSwitch.formatKey);
        formatSwitch = { currentLabel: formatShort, requestedLabel: shortFormatLabel(requested), hasPhotos: ids.length > 0 };
      } catch { formatSwitch = null; }
    }
    const unsupportedLabel = this.#unsupportedFormatKey ? this.#formatMap?.formats?.[this.#unsupportedFormatKey]?.label : null;
    const qualities = this.#photoQualities(doc, format, formatShort);
    const themeTiles = this.#panels.themeTiles(this.#themesApi, this.#themes, this.#panels.activeThemeId(doc), this.#shell.normalizeStyles, assetBase);
    const view = {
      panels: this.#panels,
      // Einfach-Modus (Quiz): Bildschirm/Antworten, Stil-Karten, Titelvorschlag, Werkzeug im Fertig-Panel.
      mode: this.#mode,
      modeHelpOpen: this.#modeHelpOpen,
      modeHelpNew: !this.#modeHelpSeen,
      // Einmaliger, dezenter Hinweis "Mehr Einfluss? Alle Optionen" auf dem Fertig-Bildschirm statt des
      // Popovers im Quiz (Conversion-Audit 2026-09-22 #7, Begruendung an #maybeOpenModeHelp).
      modeHintVisible: !this.#modeHelpSeen && this.#mode === 'einfach' && this.#quiz.done === true,
      modeToast: this.#modeToast,
      quiz: { ...this.#quiz, answers: { ...this.#quiz.answers }, entering: this.#quizEntering, formatPicked: this.#quizOpts().formatPicked },
      formatLabel: formatShort,
      quizStyles: typeof this.#panels.quizStyleTiles === 'function' ? this.#panels.quizStyleTiles(themeTiles, assetBase) : [],
      quizTitleSuggestion: this.#quizTitleSuggestion(),
      startOverPending: this.#startOverPending,
      assetBase,
      simpleTool: this.#simpleTool,
      coverPageId: doc.pages.find((p) => p.role === 'cover')?.pageId ?? null,
      labels: this.#labels,
      backHref: this.#backHref(),
      chip,
      priceLabel: this.#priceLabel(),
      // Zahl hinter dem ab-Preis: entscheidet „inkl./zzgl. Versand“ (shippingLabelFor).
      priceValue: this.#priceValue(),
      // „Express · 3 Werktage“ im Prüf-Sheet – der Kunde sieht vor der Übergabe, welche Versandart mitgeht.
      speedFact: this.#speedFact(),
      // Lieferzeit-Karte im geführten Modus (renderSpeedChoice): Angebot, Wahl und Preis der Kombination.
      express: this.#expressOffer(),
      speed: this.#speed,
      optionOffer: this.#optionOffer(),
      wantedQuantityEffective: this.#effectiveWantedQuantity(),
      // Staffel-Karte „Gleich zwei bestellen?“ + „2 Stück: 20,56 €“ unter dem ab-Preis (Zahlen aus der Format-Map).
      quantityOffer: this.#quantityOffer(),
      wantedQuantity: this.#effectiveWantedQuantity(),
      quantityCardOpen: this.#quantityCardOpen,
      progress: facts.progress,
      startYear: this.#startYear,
      // Jahr der aktiven Monatsseite (Startmonat ≠ Januar: nach dem Jahreswechsel +1) und Laufzeit „September 2027 – August 2028“.
      activePageYear: Number.isInteger(page?.calendariumYear) ? page.calendariumYear : this.#startYear,
      periodLabel: this.#panels.periodLabel(this.#startYear, this.#effectiveCalendar().startMonth),
      yearOptionsHtml: yearSelectOptions(this.#startYear),
      canProceed: eligibility.eligible && this.#issues.every((i) => i.level !== 'error'),
      // Eigene Druckdatei: Produktseite OHNE ?design= (sonst sucht dp-connector-handoff.js einen Eintrag, der nicht existiert).
      ineligible: eligibility.eligible ? null : { reason: eligibility.reason, href: this.#orderHref({ withDesign: false }) },
      review: this.#review ? { ...this.#review, href: orderHref, productHref: this.#orderHref({ withDesign: false }), chipFull: chip?.full ?? '', warnings: facts.warnings, pagesDone: facts.progress.completedPages, pagesTotal: facts.progress.totalPages, emptySlots: facts.emptySlotCount > 0, hintsOpen: this.#reviewHintsOpen } : null,
      formatSwitch,
      unsupportedLead: this.#unsupportedFormatKey
        ? (unsupportedLabel ? `Das Format „${this.#panels.escapeHtml(unsupportedLabel)}“ kommt bald in den Designer – wähle so lange ein anderes.` : 'Dieses Format kommt bald in den Designer – wähle so lange ein anderes.')
        : null,
      formatRedirect: this.#formatRedirect ? { ...this.#formatRedirect } : null,
      hasThemes: this.#themes.length > 0,
      designFile: { busy: this.#designFileBusy, notice: this.#designFileNotice },
      activeTab: this.#activeTab === 'designs' && !this.#themes.length ? 'photos' : this.#activeTab,
      calendar: this.#calendarView(ids.length),
      events: this.#eventsView(),
      sheetOpen: this.#sheetOpen,
      themes: themeTiles,
      themeStyle: this.#panels.themeStyleFor(this.#themesApi, theme, this.#shell.normalizeStyles),
      themeColors: this.#panels.themeColorView(this.#themesApi, theme, doc.theme?.overrides),
      themeFonts: this.#panels.themeFontView(this.#themesApi, doc.theme?.overrides),
      pageDecor: (p) => this.#panels.pageDecor(p, doc.product, format, pageLayout(p)),
      doc,
      product: doc.product,
      format,
      binding: format?.binding ?? null,
      activePage: page,
      activePageId: this.#activePageId,
      activePageLabel: page ? modelPageLabel(page) : '',
      activePageComplete: page ? Boolean(facts.completeById.get(page.pageId)) : false,
      activeLayoutId: page ? this.#panels.layoutIdForPage(page) : 'one',
      selectedElementId: this.#selectedElementId,
      selectedElement: selected && selected.type === 'image' && selected.assetId ? selected : null,
      assigningElementId: this.#assigningElementId,
      pendingAssetId: this.#pendingAssetId,
      zoomMin: ZOOM_MIN,
      zoomMax: ZOOM_MAX,
      photos: ids.map((id) => ({ assetId: id, url: this.#previewUrls.get(id) ?? '', name: images[id].sourceFileName, used: facts.usedIds.has(id), quality: qualities[id] ?? null })),
      unusedCount: facts.unusedCount,
      emptySlotCount: facts.emptySlotCount,
      uploadErrors: this.#uploadErrors,
      uploadProgress: this.#uploadProgress,
      nudge: this.#nudge,
      previewUrl: (assetId) => this.#previewUrls.get(assetId) ?? '',
      sampleMode: ids.length === 0 && samples.length > 0,
      sampleUrl: (p, slotIndex) => this.#panels.sampleUrlFor(samples, p, slotIndex),
      calendariumHtml: (p) => this.#buildCalendariumInnerHtml(p.calendariumMonth, Number.isInteger(p.calendariumYear) ? p.calendariumYear : this.#startYear, p),
      findingForPage: facts.findingForPage,
      findingForSlot: facts.findingForSlot,
      blurry: facts.blurry,
      pageIssues: facts.pageIssuesFor(page),
      pageStatus: facts.pageStatus,
      pageLabel: modelPageLabel,
      shortPageLabel,
      showGuides: this.#showGuides,
      // Hinweiskarte „So geht’s“ nur im Voll-Modus – im Einfach-Modus führt die Schrittleiste.
      hintVisible: parseBooleanAttr(this.dataset.showHint, true) && !this.#hintDismissed && ids.length === 0 && this.#mode !== 'einfach',
      // Zieh-Tipp in der Zeile unter der Bühne: erst wenn es Fotos zum Ziehen gibt, und nur einmal.
      trayTipVisible: !this.#trayTipDismissed && ids.length > 0 && this.#mode !== 'einfach',
      texts: {
        title: this.#panels.pageText(page, 'title'),
        caption: this.#panels.pageText(page, 'caption'),
      },
      // Der geführte Modus zeigt und schreibt IMMER den Titel der Titelseite (setPageText mit
      // coverPageId). Gelesen wurde bisher der Titel der AKTIVEN Seite – stand der Kunde auf einer
      // Monatsseite, meldete der Fertig-Kopf „ohne Titel" und das erste getippte Zeichen ersetzte den
      // echten Titel (Prüfung Voll-Modus 2026-09-22 P1-02).
      coverTitle: this.#panels.pageText(doc.pages.find((p) => p.role === 'cover') ?? null, 'title'),
      // Tab „Text“: Einpass-Ergebnis der Bühne je Slot der aktiven Seite ({ factor, truncated, tight }, #fitStageTexts) für den Live-Hinweis.
      textFit: this.#textFit,
      // Druckschrift je Text-Slot (Glyphenabdeckung für „Emoji/Sonderzeichen werden nicht gedruckt“, panels.unprintableChars).
      textFonts: { title: this.#textFontFamily('title'), caption: this.#textFontFamily('caption') },
      // Tab „Text“: Bildunterschrift je Monat (Liste) und wo sie auf der aktiven Seite steht (Zeile/Overlay).
      captions: doc.pages.filter((p) => p.role === 'month').map((p) => ({ pageId: p.pageId, label: modelPageLabel(p), value: this.#panels.pageText(p, 'caption'), active: p.pageId === this.#activePageId })),
      captionPlacement: page?.role === 'month' && typeof this.#panels.captionPlacement === 'function' ? this.#panels.captionPlacement(page, pageLayout(page)) : 'line',
      notices: '',
    };
    view.notices = this.#shell.renderNotices(view);
    return view;
  }

  #renderFormatPickerScreen() {
    const formats = this.#formats ?? [];
    const key = this.#unsupportedFormatKey;
    const label = key ? this.#formatMap?.formats?.[key]?.label : null;
    const notice = !key ? '' : `<p class="dp-designer__notice dp-designer__notice--info" role="status">${label
      ? `Das Format „${this.#panels.escapeHtml(label)}“ kommt bald in den Designer – wähle so lange ein anderes.`
      : 'Dieses Format kommt bald in den Designer – wähle so lange ein anderes.'}</p>`;
    return `
      <div class="dp-designer__intro">
        <h1 class="dp-designer__heading">${escapeHtml(this.#labels.heading)}</h1>
        ${this.#labels.subline ? `<p class="dp-designer__subline">${escapeHtml(this.#labels.subline)}</p>` : ''}
        ${typeof this.#shell?.renderModeSwitch === 'function' ? `<div class="dp-designer__intro-mode">${this.#shell.renderModeSwitch({ mode: this.#mode, panels: this.#panels })}</div>` : ''}
      </div>
      <div class="dp-designer__formats">
        ${notice}
        <h2 class="dp-designer__step-title">Format wählen</h2>
        ${formats.length === 0
          ? '<p class="dp-designer__empty">Aktuell ist kein Format verfügbar. Bitte versuche es später erneut.</p>'
          : `<ul class="dp-format-grid" role="list">${formats.map((f) => this.#renderFormatCard(f)).join('')}</ul>`}
        ${this.#panels.renderDesignFileResume ? this.#panels.renderDesignFileResume({ panels: this.#panels, designFile: { busy: this.#designFileBusy, notice: this.#designFileNotice } }) : ''}
      </div>
    `;
  }

  /**
   * Günstigstes designer-taugliches Angebot eines Formats laut Format-Map (Produktname + ab-Preis)
   * für die Startkarten; null ohne Karte oder ohne Angebot.
   * @param {string} formatKey @returns {{productName:string, price:string}|null}
   */
  #cheapestOffer(formatKey) {
    const offers = this.#formatMap?.formats?.[formatKey]?.products;
    if (!offers || typeof offers !== 'object') return null;
    let best = null;
    for (const offer of Object.values(offers)) {
      if (!offer || offer.designerEligible === false || !offer.productName) continue;
      const p = Number(offer.priceFrom);
      if (!best || (Number.isFinite(p) && p > 0 && (best.priceFrom == null || p < best.priceFrom))) best = { productName: String(offer.productName), priceFrom: Number.isFinite(p) && p > 0 ? p : null };
    }
    return best ? { productName: best.productName, price: priceFromLabel(best.priceFrom) } : null;
  }

  /**
   * Startkarte: Kurzformat („DIN A4 hoch“), Maße/Blatt, darunter Produkt und ab-Preis aus der Format-Map.
   * Formate, die es erst ab 100 Stück gibt (Katalog `orderableFromOne:false`: DIN A2 quer, 70 × 50), bleiben
   * als ausgegraute, nicht klickbare Karte mit „erst ab 100 Stück – nicht im Designer“ stehen (QA 2026-09-21
   * I-11): vorher waren sie anklickbar und endeten in einem Hinweis, der auf einen Upload verwies, den die
   * Produktseite für dieses Format gar nicht anbietet.
   * @param {CalendarFormat} format
   */
  #renderFormatCard(format) {
    const orderable = this.#isOrderableFromOne(format);
    const minQty = Number(format.minQuantity) || 100;
    const offer = orderable ? this.#cheapestOffer(format.key) : null;
    const productLine = offer
      ? `<span class="dp-format-card__product">${escapeHtml(shortProductName(offer.productName))}${offer.price ? ` · ${escapeHtml(offer.price)}` : ''}</span>`
      : orderable ? '' : '<span class="dp-format-card__product">Preis auf Anfrage – support@dp-digital.de</span>';
    const meta = `${format.endFormatMm.widthMm} × ${format.endFormatMm.heightMm} mm · ${format.pageCount} Blatt${format.qualityNote ? ` · ${escapeHtml(format.qualityNote)}` : ''}${orderable ? '' : ` · erst ab ${minQty} Stück – nicht im Designer`}`;
    // Name ohne Maße – die stehen in der Metazeile (QA 2026-09-21 I-25, wie die Kalender-Tab-Karten).
    const cardName = typeof this.#panels?.shortFormatName === 'function' ? this.#panels.shortFormatName(format) : shortFormatLabel(format);
    if (!orderable) {
      return `
      <li>
        <div class="dp-format-card dp-format-card--locked" aria-disabled="true">
          <span class="dp-format-card__name">${escapeHtml(cardName)}</span>
          <span class="dp-format-card__meta">${meta}</span>
          ${productLine}
        </div>
      </li>
    `;
    }
    return `
      <li>
        <button type="button" class="dp-format-card" on:click="/selectFormat?formatKey=${encodeURIComponent(format.key)}">
          <span class="dp-format-card__name">${escapeHtml(cardName)}</span>
          <span class="dp-format-card__meta">${meta}</span>
          ${productLine}
        </button>
      </li>
    `;
  }

  /**
   * Nutzt bevorzugt das dynamisch geladene @theme/dp-kalendarium-render (#kalendariumRenderFn),
   * fällt bei fehlendem Modul, fehlendem Export oder einem Laufzeitfehler auf
   * shell.renderFallbackCalendarium() zurück — siehe #loadKalendariumModule().
   * @param {number} month @param {number} year @param {object} [page] Monatsseite (Bandhöhe für die Auto-Umschaltung) @returns {string}
   */
  #buildCalendariumInnerHtml(month, year, page = null) {
    if (this.#kalendariumRenderFn) {
      try {
        // formatKey ist Pflicht für die maßstäbliche Typografie des Renderers (ohne ihn rechnet er
        // fest mit A4 hoch); variant/region sind bereits normalisiert, siehe #calendarSettings.
        // Kalenderwochen/Feiertage/Bundesland: Shop-Vorgaben, vom Kunden im Panel „Kalender“
        // überschreibbar (doc.meta.calendar); Farben kommen vom Design (kalendariumOptions). Der Stil
        // (settings.style, dp-kalendarium-styles.js) bringt seine Variante mit und gilt vor Design und Shop.
        const settings = this.#effectiveCalendar();
        const themeOpts = this.#themeKalendariumOptions();
        const html = this.#kalendariumRenderFn({
          month,
          year,
          formatKey: this.#activeFormat?.key ?? this.#doc?.product?.formatKey,
          variant: this.#preferredVariant(themeOpts?.darstellung ?? settings.variant),
          style: settings.style,
          bandHeightMm: page ? this.#bandHeightMm(page) : null,
          farben: themeOpts?.farben,
          showWeekNumbers: settings.showWeekNumbers,
          showHolidays: settings.showHolidays,
          region: settings.region ?? undefined,
          // Eigene Termine dieses Monats (Panel „Termine“, doc.meta.events)
          events: typeof this.#model?.eventsForMonth === 'function' && this.#doc ? this.#model.eventsForMonth(this.#doc, month) : [],
          weekStart: 'monday',
          locale: 'de-DE',
        });
        if (typeof html === 'string' && html.trim()) return html;
        throw new Error('Kein nutzbarer HTML-String zurückgegeben.');
      } catch (error) {
        console.warn('[dp-designer] @theme/dp-kalendarium-render ist bei der Ausführung fehlgeschlagen — nutze eingebauten Fallback für diese Seite.', error);
      }
    }
    return this.#shell.renderFallbackCalendarium(month, year, MONTH_NAMES_DE[month - 1]);
  }

  /**
   * Kalendarium-Vorgaben des aktiven Designs (optionaler Export kalendariumOptions), sonst null.
   * `darstellung` ist bereits durch #preferredVariant() gelaufen, damit auch der Flipbook-Abzug
   * (dp-design-snapshot.js liest themeKal.darstellung) dieselbe Darstellung zeigt wie Bühne und PDF.
   */
  #themeKalendariumOptions() {
    const theme = this.#activeTheme();
    const fn = this.#themesApi?.kalendariumOptions;
    if (!theme || typeof fn !== 'function') return null;
    try {
      const opts = fn(theme);
      if (!opts || typeof opts !== 'object') return null;
      return { ...opts, darstellung: this.#preferredVariant(opts.darstellung) };
    } catch {
      return null;
    }
  }

  /**
   * Darstellung des Kalendariums für das aktive Format: die gewünschte („raster"/„zeile"), aber
   * „zeile", wenn das Raster bei kleinen Seiten (Tischkalender DIN lang, DIN A5 lang, DIN A6 quer)
   * nicht mehr lesbar ins Band passt — reine Regel preferredVariant() in dp-designer-formats.js,
   * gleich für Bühne (#buildCalendariumInnerHtml), Flipbook und PDF (#printThemeOptions →
   * dp-designer-handoff.js buildExportOptions). Ohne Regel/Format bleibt der Wunsch unverändert.
   * @param {string|null|undefined} variant @returns {string|null|undefined}
   */
  #preferredVariant(variant) {
    const fn = this.#formatsApi?.preferredVariant;
    const format = this.#activeFormat ?? this.#doc?.product ?? null;
    if (typeof fn !== 'function' || !format) return variant;
    try {
      return fn(format, variant);
    } catch {
      return variant;
    }
  }
}

if (!customElements.get('dp-designer')) {
  customElements.define('dp-designer', DpDesigner);
}
